import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData, loadAllBlueprints, SHIPS_LIST_COLS } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import { optimizeImageFile } from "./imageOptimize";
import UexCurrencyWidget from "./UexCurrencyWidget";
import ItemPicker from "./ItemPicker";
import SuggestedPricing from "./SuggestedPricing";
import Nav from "./Nav";

const ACCENT = "#fcd657";
const ALL_DIVISIONS = ["Industry", "Security"];
const ORG_ROLES = ["Member", "Crew", "Specialist", "Officer", "Lt. Commander", "Commander", "Division Lead", "Executive Officer", "President", "CEO"];
const EVENT_TYPES = ["Combat", "Industry", "Salvage", "Training", "Piracy", "Exploration", "Other"];
const TEAM_COLORS = ["#ce93d8", "#ef5350", "#fcd657", "#4fc3f7", "#81c784", "#ff7043", "#a78bfa", "#34d399"];
const PRESET_COLORS = ["#ef5350","#fcd657","#facc15","#4fc3f7","#81c784","#34d399","#a78bfa","#ce93d8","#f87171","#00fffb","#ff7043","#ffffff"];

const ROLE_PRIORITY = {
  "CEO": 0, "President": 1, "Executive Officer": 2, "Director of Strategic Development": 3,
  "Operations Liaison": 4, "Division Lead": 5, "Commander": 6, "Lt. Commander": 7,
  "Officer": 8, "Specialist": 9, "Crew": 10, "Member": 11,
};

function getPerms(profile) {
  return {
    isSuperAdmin: profile?.perm_super_admin === true || profile?.admin_tier === "super_admin",
    isDivisionLead: profile?.perm_division_lead === true || profile?.admin_tier === "division_lead",
    isTeamLead: profile?.perm_team_lead === true || profile?.admin_tier === "team_lead",
  };
}

function sortMembers(members) {
  return [...members].sort((a, b) => {
    const pa = a.org_role_sort ?? 99;
    const pb = b.org_role_sort ?? 99;
    if (pa !== pb) return pa - pb;
    return (a.discord_username || "").localeCompare(b.discord_username || "");
  });
}

// Normalize team/division fields — support both old single string and new array
function toArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return [val];
}

const inputStyle = {
  width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)",
  border: `1px solid ${ACCENT}33`, color: "#e8eaf6",
  fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none",
};

const selectStyle = {
  width: "100%", padding: "10px 14px", background: "#0a0610",
  border: `1px solid ${ACCENT}33`, color: "#e8eaf6",
  fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none",
};

function SectionLabel({ children, color = ACCENT }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <div style={{ width: 32, height: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

function StatCard({ label, value, color = ACCENT }) {
  return (
    <div style={{ padding: "24px 28px", border: `1px solid ${color}33`, background: `${color}08`, flex: 1, minWidth: 140 }}>
      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 40, color, lineHeight: 1, marginBottom: 8 }}>{value}</div>
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", background: "#0a0610", border: `1px solid ${ACCENT}44`, padding: "36px 32px", position: "relative", clipPath: "polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)" }}>
        <div style={{ position: "absolute", top: 0, left: 0, width: 24, height: 24, borderTop: `2px solid ${ACCENT}`, borderLeft: `2px solid ${ACCENT}` }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 17, color: "#e8eaf6" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 20, cursor: "pointer" }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", display: "block", marginBottom: 8 }}>{label}</label>
      {children}
    </div>
  );
}

function PermToggle({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 8 }}>
      <div onClick={onChange} style={{ width: 40, height: 22, borderRadius: 11, background: checked ? ACCENT : "rgba(255,255,255,0.1)", position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: 3, left: checked ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
      </div>
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: checked ? ACCENT : "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>{label}</span>
    </label>
  );
}

// Multi-select chip picker (for teams and divisions)
function ChipPicker({ label, options, selected, onChange, colorMap = {} }) {
  return (
    <Field label={label}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map(opt => {
          const active = selected.includes(opt);
          const c = colorMap[opt] || ACCENT;
          return (
            <div key={opt} onClick={() => onChange(active ? selected.filter(s => s !== opt) : [...selected, opt])}
              style={{ padding: "4px 12px", border: `1px solid ${active ? c : "rgba(255,255,255,0.15)"}`, background: active ? `${c}22` : "transparent", color: active ? c : "rgba(232,234,246,0.4)", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", borderRadius: 2, transition: "all 0.15s" }}>
              {opt}
            </div>
          );
        })}
      </div>
    </Field>
  );
}

function ActionBtn({ onClick, children, color = ACCENT, small = false, to = null, target = null }) {
  const [hov, setHov] = useState(false);
  const style = { padding: small ? "6px 14px" : "10px 22px", background: hov ? color : "transparent", border: `2px solid ${color}`, color: hov ? "#05070f" : color, fontFamily: "'Orbitron', sans-serif", fontSize: small ? 9 : 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s", clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)", textDecoration: "none", display: "inline-block" };
  // Render as a Link when a route target is supplied, so View Profile etc.
  // get real navigation (back button works, middle-click opens new tab).
  if (to) {
    return (
      <Link to={to} target={target || undefined} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={style}>
        {children}
      </Link>
    );
  }
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={style}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drill-down panel — shown under a Timezone / Home Base donut slice when the
// admin clicks it. Mirrors the chip + Close pattern used on the team page so
// the UX is consistent across the app.
// ─────────────────────────────────────────────────────────────────────────────
function DistributionMembersPanel({ label, members, color = ACCENT, onClose }) {
  if (!members || members.length === 0) return null;
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${color}25` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color, textTransform: "uppercase" }}>
          {label} · {members.length} member{members.length === 1 ? "" : "s"}
        </div>
        <button type="button" onClick={onClose}
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.55)", textTransform: "uppercase", cursor: "pointer" }}>
          Close
        </button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {members.map(m => (
          <Link key={m.id} to={`/profile/${m.id}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 10px", border: `1px solid ${color}33`, background: `${color}08`, textDecoration: "none", color: "#e8eaf6" }}>
            {m.discord_avatar
              ? <img src={m.discord_avatar} alt="" style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} />
              : <span style={{ width: 18, height: 18, borderRadius: "50%", background: `${color}22`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 9, color }}>{(m.discord_username || "?").charAt(0).toUpperCase()}</span>}
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }}>{m.discord_username || "—"}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MEMBER MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
function MemberManagement({ profile: adminProfile }) {
  const { isSuperAdmin, isDivisionLead } = getPerms(adminProfile);
  const canSeeAll = isSuperAdmin || isDivisionLead || adminProfile?.is_admin === true;

  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [orgRolesList, setOrgRolesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [newMember, setNewMember] = useState({ discord_username: "", org_role: "Member", teams: [], divisions: [], team_role: "", bio: "", timezone: "America/New_York" });

  const [memberTab, setMemberTab] = useState("members"); // "members" | "guests" | "pending"
  const [showDiscordImport, setShowDiscordImport] = useState(false);
  const [discordFetching, setDiscordFetching] = useState(false);
  const [discordPreview, setDiscordPreview] = useState(null); // { toAdd: [], toUpdate: [], error: null }
  const [discordImporting, setDiscordImporting] = useState(false);
  const [discordResult, setDiscordResult] = useState(null);
  const [guestEditing, setGuestEditing] = useState(null);

  useEffect(() => { loadAll(); }, []);

  async function fetchDiscordMembers() {
    setDiscordFetching(true);
    setDiscordPreview(null);
    try {
      const res = await fetch("/api/discord-members");
      const data = await res.json();
      if (!res.ok) { setDiscordPreview({ error: data.error || "Failed to fetch Discord members" }); setDiscordFetching(false); return; }

      // Match ONLY on discord_id — never username (prevents false positives)
      const existingByDiscordId = {};
      members.forEach(m => {
        if (m.discord_id) existingByDiscordId[m.discord_id] = m;
      });

      // Find duplicates (same discord_id appearing more than once in DB)
      const discordIdCount = {};
      members.forEach(m => {
        if (m.discord_id) discordIdCount[m.discord_id] = (discordIdCount[m.discord_id] || 0) + 1;
      });
      const duplicates = members.filter(m => m.discord_id && discordIdCount[m.discord_id] > 1);

      const toAdd = [];
      const toUpdate = [];

      data.members.forEach(dm => {
        const existing = existingByDiscordId[dm.discord_id];

        if (existing) {
          // Always sync — roles/teams need updating even if avatar/username unchanged
          toUpdate.push({ ...dm, existing_id: existing.id });
        } else {
          // Not in DB by discord_id — truly new
          toAdd.push(dm);
        }
      });

      // Detect members who left the Discord server
      const serverSet = new Set(data.allDiscordIds || []);
      const toLeaveMark = members.filter(m => {
        if (!m.discord_id) return false;
        if (m.is_admin) return false;
        if (m.member_status === "left") return false;
        return serverSet.size > 0 && !serverSet.has(m.discord_id);
      });

      setDiscordPreview({ toAdd, toUpdate, duplicates, toLeaveMark, total: data.total, allDiscordIds: data.allDiscordIds || [], error: null });
    } catch (err) {
      setDiscordPreview({ error: err.message || "Network error" });
    }
    setDiscordFetching(false);
  }

  async function runDiscordImport() {
    if (!discordPreview) return;
    setDiscordImporting(true);
    const { toAdd, toUpdate, duplicates = [], allDiscordIds = [] } = discordPreview;
    let added = 0, updated = 0, failed = 0, flushed = 0;
    let firstError = null;

    // Flush duplicates first — keep the one with is_admin=true or oldest joined_at
    for (const dup of duplicates) {
      const { data: dupeRows } = await supabase
        .from("profiles").select("id, is_admin, joined_at")
        .eq("discord_id", dup.discord_id);
      if (dupeRows && dupeRows.length > 1) {
        const sorted = [...dupeRows].sort((a, b) => {
          if (a.is_admin && !b.is_admin) return -1;
          if (!a.is_admin && b.is_admin) return 1;
          return new Date(a.joined_at) - new Date(b.joined_at);
        });
        for (let i = 1; i < sorted.length; i++) {
          const { error } = await supabase.from("profiles").delete().eq("id", sorted[i].id);
          if (!error) flushed++;
        }
      }
    }

    // Auto-assign teams based on discord_role_id
    const { data: teamsWithRoles } = await supabase
      .from("teams")
      .select("id, name, discord_role_id, sub_groups")
      .not("discord_role_id", "is", null);
    const roleTeamMap = {}; // discord_role_id -> team name (supports comma-separated multi-role)
    const teamIdMap = {}; // team name -> team id
    (teamsWithRoles || []).forEach(t => {
      teamIdMap[t.name] = t.id;
      if (t.discord_role_id) {
        t.discord_role_id.split(",").map(s => s.trim()).filter(Boolean).forEach(rid => { roleTeamMap[rid] = t.name; });
      }
    });
    // Also load ALL teams (not just those with roles) for vacancy creation
    const { data: allTeams } = await supabase.from("teams").select("id, name");
    (allTeams || []).forEach(t => { if (!teamIdMap[t.name]) teamIdMap[t.name] = t.id; });

    // Upsert new members in chunks of 50
    const chunkSize = 50;
    for (let i = 0; i < toAdd.length; i += chunkSize) {
      const chunk = toAdd.slice(i, i + chunkSize).map(dm => ({
        id: crypto.randomUUID(),
        discord_id: dm.discord_id,
        discord_username: dm.discord_username,
        discord_avatar: dm.discord_avatar || null,
        discord_roles: dm.roles || [],
        org_role: "Member",
        member_status: "member",
        teams: (dm.roles || []).map(rid => roleTeamMap[rid]).filter(Boolean),
        is_admin: false,
        admin_tier: "none",
        perm_super_admin: false,
        perm_division_lead: false,
        perm_team_lead: false,
        joined_at: dm.joined_at || new Date().toISOString(),
      }));
      const { error } = await supabase.from("profiles").insert(chunk);
      if (!error) {
        added += chunk.length;
      } else {
        if (!firstError) firstError = error.message;
        // Try one by one
        for (const row of chunk) {
          const { error: e2 } = await supabase.from("profiles").insert([row]);
          if (!e2) added++; else { failed++; if (!firstError) firstError = e2.message; }
        }
      }
    }

    // Update existing members — avatar, username, and auto-promote guests
    let promoted = 0;
    for (const dm of toUpdate) {
      // Compute teams from discord roles
      const autoTeams = (dm.roles || []).map(rid => roleTeamMap[rid]).filter(Boolean);
      // Merge with existing teams (don't remove manually assigned ones)
      const existingProfile = members.find(m => m.id === dm.existing_id);
      const existingTeams = existingProfile ? (existingProfile.teams || []) : [];
      const mergedTeams = [...new Set([...existingTeams, ...autoTeams])];
      // Build update — always sync roles/teams, only update avatar/username if changed
      const updatePayload = {
        discord_roles: dm.roles || [],
      };
      if (existingProfile?.discord_avatar !== dm.discord_avatar) updatePayload.discord_avatar = dm.discord_avatar || null;
      if (existingProfile?.discord_username !== dm.discord_username) updatePayload.discord_username = dm.discord_username;
      if (mergedTeams.length > 0) updatePayload.teams = mergedTeams;
      // Auto-promote guests/pending to member when found in Discord server
      if (existingProfile?.member_status === 'guest' || existingProfile?.member_status === 'pending') {
        updatePayload.member_status = 'member';
        promoted++;
      }
      // Re-activate a previously-left member who rejoined the server.
      // If we captured a snapshot of their pre-leave state, restore it now so
      // they don't have to be reassigned to their teams/sub-groups manually.
      if (existingProfile?.member_status === 'left') {
        updatePayload.member_status = 'member';
        promoted++;
        const snap = existingProfile.pre_left_snapshot;
        if (snap && typeof snap === 'object') {
          // Only restore fields that are currently blank — never overwrite
          // anything the member set up between leaving and rejoining.
          if ((!existingProfile.teams || existingProfile.teams.length === 0) && Array.isArray(snap.teams) && snap.teams.length > 0) updatePayload.teams = snap.teams;
          if (!existingProfile.team && snap.team) updatePayload.team = snap.team;
          if (!existingProfile.team_role && snap.team_role) updatePayload.team_role = snap.team_role;
          if ((!existingProfile.team_roles || Object.keys(existingProfile.team_roles).length === 0) && snap.team_roles) updatePayload.team_roles = snap.team_roles;
          if (!existingProfile.sub_group && snap.sub_group) updatePayload.sub_group = snap.sub_group;
          if ((!existingProfile.sub_groups || Object.keys(existingProfile.sub_groups).length === 0) && snap.sub_groups) updatePayload.sub_groups = snap.sub_groups;
          if (existingProfile.sort_order == null && snap.sort_order != null) updatePayload.sort_order = snap.sort_order;
          if ((!existingProfile.sort_orders || Object.keys(existingProfile.sort_orders).length === 0) && snap.sort_orders) updatePayload.sort_orders = snap.sort_orders;
          // Clear the snapshot once consumed so future leave/rejoin captures fresh state.
          updatePayload.pre_left_snapshot = null;
        }
      }
      const { error } = await supabase.from("profiles").update(updatePayload).eq("id", dm.existing_id);
      if (!error) updated++;
      else { failed++; if (!firstError) firstError = error.message; }
    }

    // ── Detect members who LEFT the Discord server ──────────────────────────
    let leavers = [];
    const vacanciesCreated = [];
    let removedNonMembers = 0;
    let leaverGuardCancelled = false;
    if (allDiscordIds.length > 0) {
      const serverSet = new Set(allDiscordIds);
      // Pull pre_left_snapshot + sort_order/sort_orders too so we can preserve them
      const { data: allProfiles } = await supabase
        .from("profiles")
        .select("id, discord_id, discord_username, is_admin, member_status, teams, team, team_role, team_roles, sub_group, sub_groups, sort_order, sort_orders, discord_roles, org_role, pre_left_snapshot");
      if (allProfiles) {
        const allGone = allProfiles.filter(p => {
          if (!p.discord_id) return false;
          if (p.is_admin) return false; // never touch admins
          if (p.member_status === "left") return false; // already marked
          return !serverSet.has(p.discord_id);
        });

        // Sanity guard: if a Discord API hiccup returns a truncated member list,
        // we could end up flagging a large slice of the org as "left" in one
        // pass. Require an explicit confirmation when > 10% of currently-active
        // members (or > 5 absolute) would be marked.
        const activeMemberCount = allProfiles.filter(p => p.discord_id && !p.is_admin && p.member_status !== 'left').length;
        const massLeaverThreshold = Math.max(5, Math.ceil(activeMemberCount * 0.1));
        if (allGone.length > massLeaverThreshold) {
          const proceed = window.confirm(
            `Heads up — the Discord import would mark ${allGone.length} member(s) as left in this pass ` +
            `(threshold for safety prompt: ${massLeaverThreshold}). ` +
            `This usually indicates a Discord API hiccup returning a partial member list. ` +
            `Continue and mark them as left?`
          );
          if (!proceed) {
            leaverGuardCancelled = true;
          }
        }

        if (!leaverGuardCancelled) {
          // Split into members (soft-delete → departed) vs guests/pending (now also soft-delete)
          const memberLeavers = allGone.filter(p => !p.member_status || p.member_status === "member");
          const nonMemberLeavers = allGone.filter(p => p.member_status === "guest" || p.member_status === "pending");

          // Helper: build the snapshot of fields about to be wiped so re-activation
          // can restore them. Skipped if profile already has a snapshot (don't
          // overwrite an older one — that protects against the leave/rejoin/leave
          // sequence wiping the original snapshot).
          const buildSnapshot = (p) => ({
            teams: p.teams || [],
            team: p.team || null,
            team_role: p.team_role || null,
            team_roles: p.team_roles || {},
            sub_group: p.sub_group || null,
            sub_groups: p.sub_groups || {},
            sort_order: p.sort_order ?? null,
            sort_orders: p.sort_orders || {},
            discord_roles: p.discord_roles || [],
            snapshot_at: new Date().toISOString(),
          });

          // Soft-delete members: mark as "left", create vacancies, preserve profile
          for (const leaver of memberLeavers) {
            const leaverTeams = Array.isArray(leaver.teams) ? leaver.teams : (leaver.team ? [leaver.team] : []);
            for (const teamName of leaverTeams) {
              const tid = teamIdMap[teamName];
              if (!tid) continue;
              const role = (leaver.team_roles && leaver.team_roles[teamName]) || leaver.team_role || leaver.org_role || "Member";
              const sg = (leaver.sub_groups && leaver.sub_groups[teamName]) || leaver.sub_group || "";
              if (role) {
                const { error: vErr } = await supabase.from("vacancies").insert([{
                  team_id: tid,
                  sub_group: sg,
                  position: role,
                  quantity: 1,
                }]);
                if (!vErr) vacanciesCreated.push({ team: teamName, position: role, sub_group: sg, left_by: leaver.discord_username });
              }
            }

            const snapshot = leaver.pre_left_snapshot || buildSnapshot(leaver);
            await supabase.from("profiles").update({
              member_status: "left",
              teams: [],
              team: null,
              team_role: null,
              team_roles: {},
              sub_group: null,
              sub_groups: {},
              sort_order: null,
              sort_orders: {},
              discord_roles: [],
              pre_left_snapshot: snapshot,
            }).eq("id", leaver.id);
          }

          // Soft-delete guests/pending too — preserves voice_activity_events,
          // any merits, and lets the same restore path work if they come back.
          for (const p of nonMemberLeavers) {
            const snapshot = p.pre_left_snapshot || buildSnapshot(p);
            const { error } = await supabase.from("profiles").update({
              member_status: "left",
              teams: [], team: null, team_role: null, team_roles: {},
              sub_group: null, sub_groups: {}, sort_order: null, sort_orders: {},
              discord_roles: [],
              pre_left_snapshot: snapshot,
            }).eq("id", p.id);
            if (!error) removedNonMembers++;
          }

          leavers = memberLeavers;
        }
      }
    }

    setDiscordImporting(false);
    setDiscordResult({ added, updated, failed, flushed, promoted, firstError, leavers, vacanciesCreated, removedNonMembers, leaverGuardCancelled });
    loadAll();
  }

  async function purgeNonMembers() {
    if (!discordPreview?.allDiscordIds?.length) return;
    const { allDiscordIds } = discordPreview;
    // Find profiles in DB whose discord_id is NOT in the server at all
    // Keep: admins, profiles without discord_id, profiles whose discord_id is in server
    const { data: allProfiles } = await supabase.from("profiles").select("id, discord_id, is_admin, discord_username");
    if (!allProfiles) return;

    const toDelete = allProfiles.filter(p => {
      if (!p.discord_id) return false; // keep profiles with no discord_id
      if (p.is_admin) return false; // never delete admins
      return !allDiscordIds.includes(p.discord_id); // delete if not in server
    });

    if (toDelete.length === 0) { alert("No profiles to remove — all members are in the server."); return; }

    const confirmed = window.confirm(`Remove ${toDelete.length} profile(s) whose Discord IDs are not found in the server?\n\nThis cannot be undone.`);
    if (!confirmed) return;

    let removed = 0;
    for (const p of toDelete) {
      const { error } = await supabase.from("profiles").delete().eq("id", p.id);
      if (!error) removed++;
    }
    alert(`Removed ${removed} profiles.`);
    loadAll();
  }

  async function loadAll() {
    setLoading(true);
    try {
      const profileQuery = canSeeAll
        ? supabase.from("profiles").select("*").order("discord_username").limit(2000)
        : supabase.from("profiles").select("*").eq("team", adminProfile.managed_team).order("discord_username").limit(2000);

      const [{ data: m, error: mErr }, { data: t }, { data: or }] = await Promise.all([
        profileQuery,
        supabase.from("teams").select("id, name, color, divisions").order("name"),
        supabase.from("org_roles").select("name, sort_value").order("sort_value", { ascending: true, nullsFirst: false }),
      ]);
      if (mErr) console.error("loadAll error:", mErr.message, mErr.details);
      setMembers(sortMembers(m || []));
      setTeams(t || []);
      setOrgRolesList((or || []).map(r => r.name));
    } catch (err) {
      console.error("loadAll exception:", err.message);
    }
    setLoading(false);
  }

  async function saveMember() {
    setSaving(true);
    const memberTeams = toArray(editing.teams);
    const memberDivisions = toArray(editing.divisions);
    const updates = {
      org_role: editing.org_role,
      teams: memberTeams,
      divisions: memberDivisions,
      // Keep legacy single team field in sync with first team
      team: memberTeams[0] || null,
      team_role: editing.team_role,
      bio: editing.bio,
      timezone: editing.timezone,
    };
    // Sync org_role_sort from org_roles table so hierarchy page auto-populates
    if (editing.org_role) {
      const { data: roleData } = await supabase.from("org_roles").select("sort_value").eq("name", editing.org_role).single();
      updates.org_role_sort = roleData?.sort_value ?? null;
    } else {
      updates.org_role_sort = null;
    }
    if (isSuperAdmin) {
      updates.perm_super_admin = editing.perm_super_admin || false;
      updates.perm_division_lead = editing.perm_division_lead || false;
      updates.perm_team_lead = editing.perm_team_lead || false;
      updates.managed_team = editing.managed_team || null;
      if (editing.perm_super_admin) updates.admin_tier = "super_admin";
      else if (editing.perm_division_lead) updates.admin_tier = "division_lead";
      else if (editing.perm_team_lead) updates.admin_tier = "team_lead";
      else updates.admin_tier = "none";
      updates.is_admin = !!(editing.perm_super_admin || editing.perm_division_lead || editing.perm_team_lead);
    }
    await supabase.from("profiles").update(updates).eq("id", editing.id);
    setSaving(false); setEditing(null); loadAll();
  }

  async function addMember() {
    if (!newMember.discord_username.trim()) return;
    setSaving(true);
    const memberTeams = toArray(newMember.teams);
    const { error } = await supabase.from("profiles").insert([{
      id: crypto.randomUUID(),
      discord_username: newMember.discord_username.trim(),
      org_role: newMember.org_role,
      teams: memberTeams,
      divisions: toArray(newMember.divisions),
      team: memberTeams[0] || null,
      team_role: newMember.team_role || null,
      bio: newMember.bio || "",
      timezone: newMember.timezone || "America/New_York",
      availability: {}, ships: [], is_admin: false, admin_tier: "none",
      member_status: "member",
      perm_super_admin: false, perm_division_lead: false, perm_team_lead: false,
      joined_at: new Date().toISOString(),
    }]);
    setSaving(false);
    if (!error) { setShowAddForm(false); setNewMember({ discord_username: "", org_role: "Member", teams: [], divisions: [], team_role: "", bio: "", timezone: "America/New_York" }); loadAll(); }
    else alert("Error adding member: " + error.message);
  }

  async function bulkImport() {
    if (!bulkText.trim()) return;
    setBulkImporting(true); setBulkResult(null);
    const names = bulkText.split("\n").map(n => n.trim()).filter(n => n.length > 0);
    let added = 0, skipped = 0;
    for (const name of names) {
      const { data: existing } = await supabase.from("profiles").select("id").eq("discord_username", name).single();
      if (existing) { skipped++; continue; }
      const { error } = await supabase.from("profiles").insert([{
        id: crypto.randomUUID(), discord_username: name, org_role: "Member",
        member_status: "member",
        teams: [], divisions: [], availability: {}, ships: [],
        is_admin: false, admin_tier: "none",
        perm_super_admin: false, perm_division_lead: false, perm_team_lead: false,
        joined_at: new Date().toISOString(),
      }]);
      if (!error) added++; else skipped++;
    }
    setBulkImporting(false); setBulkResult({ added, skipped, total: names.length }); loadAll();
  }

  async function deleteMember(id) {
    if (!window.confirm("Remove this member from the roster?")) return;
    const { error } = await supabase.from("profiles").delete().eq("id", id);
    if (error) {
      alert("Failed to delete member: " + error.message);
      return;
    }
    loadAll();
  }

  // Split members by status
  const allMembers = members.filter(m => !m.member_status || m.member_status === 'member');
  const allGuests = members.filter(m => m.member_status === 'guest');
  const allPending = members.filter(m => m.member_status === 'pending');
  const allDeparted = members.filter(m => m.member_status === 'left');

  const statusFiltered = memberTab === "guests" ? allGuests : memberTab === "pending" ? allPending : memberTab === "departed" ? allDeparted : allMembers;
  const filtered = sortMembers(statusFiltered.filter(m => m.discord_username?.toLowerCase().includes(search.toLowerCase())));
  const leaders = filtered.filter(m => m.org_role_sort != null && m.org_role_sort <= 3.9);
  const crew = filtered.filter(m => m.org_role_sort == null || m.org_role_sort > 3.9);

  // Promote a pending/guest user to a different status
  async function setMemberStatus(profileId, status) {
    const label = status === 'member' ? 'member' : status === 'guest' ? 'guest' : 'pending';
    await supabase.from("profiles").update({ member_status: status }).eq("id", profileId);
    loadAll();
  }

  // Promote a guest to full member
  async function promoteToMember(profileId) {
    if (!window.confirm("Promote this guest to a full member?")) return;
    await setMemberStatus(profileId, 'member');
  }

  // Move a pending user to guest with privileges
  async function moveToGuest(profileId) {
    await setMemberStatus(profileId, 'guest');
  }

  // Deny a pending request
  async function denyPending(profileId) {
    if (!window.confirm("Remove this pending request? The profile will be deleted.")) return;
    await supabase.from("profiles").delete().eq("id", profileId);
    loadAll();
  }

  const roleColor = (sort) => {
    if (sort == null) return "rgba(232,234,246,0.4)";
    if (sort >= 1 && sort < 2) return "#fcd657";
    if (sort >= 2 && sort < 3) return "#4fc3f7";
    if (sort >= 3 && sort < 4) return "#81c784";
    return "rgba(232,234,246,0.4)";
  };

  const teamColorMap = Object.fromEntries(teams.map(t => [t.name, t.color || ACCENT]));
  const divColorMap = { "Industry": "#fcd657", "Security": "#ef5350" };

  function permBadges(m) {
    const b = [];
    if (m.member_status === 'guest') b.push({ label: "GUEST", color: "#ff9800" });
    if (m.member_status === 'pending') b.push({ label: "PENDING", color: "#78909c" });
    if (m.perm_super_admin || m.admin_tier === "super_admin") b.push({ label: "SUPER ADMIN", color: "#fcd657" });
    if (m.perm_division_lead) b.push({ label: "DIV LEAD", color: "#4fc3f7" });
    if (m.perm_team_lead) b.push({ label: "TEAM LEAD", color: "#81c784" });
    return b;
  }

  function MemberRow({ m }) {
    const badges = permBadges(m);
    const memberTeams = toArray(m.teams);
    const memberDivs = toArray(m.divisions);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", border: `1px solid ${(m.org_role_sort != null && m.org_role_sort <= 3.9) ? `${roleColor(m.org_role_sort)}33` : "rgba(255,255,255,0.07)"}`, background: (m.org_role_sort != null && m.org_role_sort <= 3.9) ? `${roleColor(m.org_role_sort)}06` : "rgba(255,255,255,0.02)", flexWrap: "wrap" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", border: `2px solid ${roleColor(m.org_role_sort)}55`, flexShrink: 0 }}>
          {m.discord_avatar ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 13, color: roleColor(m.org_role_sort) }}>{m.discord_username?.charAt(0)?.toUpperCase()}</div>}
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {m.discord_username}
            {!m.discord_avatar && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.1)", padding: "1px 5px" }}>MANUAL</span>}
            {badges.map(b => <span key={b.label} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: b.color, border: `1px solid ${b.color}55`, padding: "1px 5px", background: `${b.color}11` }}>{b.label}</span>)}
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: roleColor(m.org_role_sort), marginBottom: memberTeams.length > 0 ? 4 : 0 }}>
            {m.org_role || "Member"}{m.team_role ? ` · ${m.team_role}` : ""}
          </div>
          {(memberTeams.length > 0 || memberDivs.length > 0) && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {memberTeams.map(t => <span key={t} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, padding: "1px 6px", border: `1px solid ${teamColorMap[t] || ACCENT}55`, background: `${teamColorMap[t] || ACCENT}11`, color: teamColorMap[t] || ACCENT }}>{t}</span>)}
              {memberDivs.map(d => <span key={d} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, padding: "1px 6px", border: `1px solid ${divColorMap[d] || "#ce93d8"}44`, background: `${divColorMap[d] || "#ce93d8"}11`, color: divColorMap[d] || "#ce93d8" }}>{d}</span>)}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {m.member_status === 'pending' ? (
            <>
              <ActionBtn small to={`/profile/${m.id}`} color="#4fc3f7">View</ActionBtn>
              <ActionBtn small onClick={() => moveToGuest(m.id)} color="#ff9800">Add as Guest</ActionBtn>
              <ActionBtn small onClick={() => promoteToMember(m.id)} color="#81c784">Approve</ActionBtn>
              <ActionBtn small onClick={() => denyPending(m.id)} color="#ef5350">Deny</ActionBtn>
            </>
          ) : m.member_status === 'guest' ? (
            <>
              <ActionBtn small to={`/profile/${m.id}`} color="#4fc3f7">View</ActionBtn>
              <ActionBtn small onClick={() => setEditing({ ...m, teams: toArray(m.teams), divisions: toArray(m.divisions) })} color={ACCENT}>Edit</ActionBtn>
              <ActionBtn small onClick={() => promoteToMember(m.id)} color="#81c784">Promote</ActionBtn>
              {isSuperAdmin && <ActionBtn small onClick={() => deleteMember(m.id)} color="#ef5350">X</ActionBtn>}
            </>
          ) : (
            <>
              <ActionBtn small to={`/profile/${m.id}`} color="#4fc3f7">View</ActionBtn>
              <ActionBtn small onClick={() => setEditing({ ...m, teams: toArray(m.teams), divisions: toArray(m.divisions) })} color={ACCENT}>Edit</ActionBtn>
              {isSuperAdmin && <ActionBtn small onClick={() => deleteMember(m.id)} color="#ef5350">X</ActionBtn>}
            </>
          )}
        </div>
      </div>
    );
  }

  const STATUS_TABS = [
    { id: "members", label: "Members", count: allMembers.length, color: ACCENT },
    { id: "guests", label: "Guests", count: allGuests.length, color: "#ff9800" },
    { id: "pending", label: "Pending", count: allPending.length, color: "#78909c" },
    { id: "departed", label: "Departed", count: allDeparted.length, color: "#ef5350" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <SectionLabel>Admin</SectionLabel>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6" }}>Member Management <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "rgba(232,234,246,0.3)", fontWeight: 400 }}>({filtered.length})</span></h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 200 }} />
          <ActionBtn onClick={() => { setShowDiscordImport(true); setDiscordPreview(null); setDiscordResult(null); fetchDiscordMembers(); }} color="#5865f2">Discord Import</ActionBtn>
        </div>
      </div>

      {/* Status tabs: Members / Guests / Pending */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap" }}>
        {STATUS_TABS.map(tab => {
          const active = memberTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setMemberTab(tab.id)}
              style={{
                padding: "8px 18px", background: active ? `${tab.color}18` : "transparent",
                border: `1px solid ${active ? `${tab.color}66` : "rgba(255,255,255,0.08)"}`,
                color: active ? tab.color : "rgba(232,234,246,0.4)",
                fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2,
                textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s",
              }}>
              {tab.label} {tab.count > 0 && <span style={{ marginLeft: 6, opacity: 0.7 }}>({tab.count})</span>}
              {tab.id === "pending" && tab.count > 0 && (
                <span style={{ marginLeft: 8, width: 8, height: 8, borderRadius: "50%", background: "#ff9800", display: "inline-block", animation: "pulse 2s infinite" }} />
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING MEMBERS...</div>
      ) : memberTab === "pending" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {allPending.length === 0 ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>
              NO PENDING REQUESTS
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.12)", marginTop: 8, letterSpacing: 1 }}>People who sign up but aren't in the Discord server will appear here</div>
            </div>
          ) : (
            <>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#78909c", textTransform: "uppercase", marginBottom: 4, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 1, background: "#78909c" }} /> Awaiting Review ({filtered.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{filtered.map(m => <MemberRow key={m.id} m={m} />)}</div>
            </>
          )}
        </div>
      ) : memberTab === "guests" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ padding: "12px 16px", border: "1px solid rgba(255,152,0,0.2)", background: "rgba(255,152,0,0.04)", marginBottom: 4 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "#ff9800", lineHeight: 1.6 }}>
              Guests can be given the same roles and privileges as members, but they'll display with a GUEST tag across the site.
              When the Discord import detects a guest has joined the server, they'll automatically be promoted to a full member.
            </div>
          </div>
          {allGuests.length === 0 ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>
              NO GUESTS
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.12)", marginTop: 8, letterSpacing: 1 }}>Add guests from the Pending tab or by creating them manually</div>
            </div>
          ) : (
            <>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#ff9800", textTransform: "uppercase", marginBottom: 4, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 1, background: "#ff9800" }} /> Guests ({filtered.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{filtered.map(m => <MemberRow key={m.id} m={m} />)}</div>
            </>
          )}
        </div>
      ) : memberTab === "departed" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ padding: "12px 16px", border: "1px solid rgba(239,83,80,0.2)", background: "rgba(239,83,80,0.04)", marginBottom: 4 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "#ef5350", lineHeight: 1.6 }}>
              Members whose Discord accounts are no longer in the server. Their team positions have been converted to vacancies.
              If someone rejoins, the next Discord Import will automatically re-activate their profile.
            </div>
          </div>
          {allDeparted.length === 0 ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>
              NO DEPARTURES RECORDED
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.12)", marginTop: 8, letterSpacing: 1 }}>Members who leave the Discord server will appear here after the next import</div>
            </div>
          ) : (
            <>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#ef5350", textTransform: "uppercase", marginBottom: 4, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 1, background: "#ef5350" }} /> Departed ({filtered.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {filtered.map(m => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", border: "1px solid rgba(239,83,80,0.12)", background: "rgba(239,83,80,0.03)", transition: "all 0.2s" }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: "1px solid rgba(239,83,80,0.25)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.5 }}>
                      {m.discord_avatar ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(100%)" }} />
                        : <span style={{ fontFamily: "'Orbitron'", fontSize: 14, color: "rgba(239,83,80,0.5)" }}>{(m.discord_username || "?").charAt(0).toUpperCase()}</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "rgba(232,234,246,0.45)", textDecoration: "line-through", textDecorationColor: "rgba(239,83,80,0.3)" }}>{m.discord_username}</div>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.25)", marginTop: 2 }}>
                        {m.org_role || "Member"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={async () => {
                        if (!window.confirm(`Permanently delete ${m.discord_username}'s profile?`)) return;
                        await supabase.from("profiles").delete().eq("id", m.id);
                        loadAll();
                      }} style={{
                        fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, padding: "4px 10px",
                        background: "transparent", border: "1px solid rgba(239,83,80,0.3)", color: "rgba(239,83,80,0.6)",
                        cursor: "pointer", textTransform: "uppercase", transition: "all 0.2s",
                      }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {leaders.length > 0 && (
            <div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#fcd657", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 1, background: "#fcd657" }} /> Leadership ({leaders.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{leaders.map(m => <MemberRow key={m.id} m={m} />)}</div>
            </div>
          )}
          {crew.length > 0 && (
            <div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 1, background: "rgba(232,234,246,0.2)" }} /> Crew ({crew.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{crew.map(m => <MemberRow key={m.id} m={m} />)}</div>
            </div>
          )}
          {filtered.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 40 }}>NO MEMBERS FOUND</div>}
        </div>
      )}

      {editing && (
        <Modal title={`Edit - ${editing.discord_username}`} onClose={() => setEditing(null)}>
          <Field label="Org Role"><select value={editing.org_role || "Member"} onChange={e => setEditing(p => ({ ...p, org_role: e.target.value }))} style={selectStyle}>{orgRolesList.map(r => <option key={r} value={r}>{r}</option>)}</select></Field>
          <ChipPicker label="Teams" options={teams.map(t => t.name)} selected={editing.teams} onChange={v => setEditing(p => ({ ...p, teams: v }))} colorMap={teamColorMap} />
          <ChipPicker label="Divisions" options={ALL_DIVISIONS} selected={editing.divisions} onChange={v => setEditing(p => ({ ...p, divisions: v }))} colorMap={divColorMap} />
          <Field label="Team Role"><input value={editing.team_role || ""} onChange={e => setEditing(p => ({ ...p, team_role: e.target.value }))} style={inputStyle} placeholder="e.g. Lt. Commander" /></Field>
          <Field label="Bio"><textarea rows={2} value={editing.bio || ""} onChange={e => setEditing(p => ({ ...p, bio: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} placeholder="Short bio..." /></Field>
          {toArray(editing.discord_roles).length > 0 && (
            <Field label="Discord Roles (from server)">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 0" }}>
                {toArray(editing.discord_roles).map(rid => (
                  <span key={rid} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, padding: "2px 8px", border: "1px solid rgba(88,101,242,0.4)", background: "rgba(88,101,242,0.08)", color: "#5865f2", letterSpacing: 1 }}>{rid}</span>
                ))}
              </div>
            </Field>
          )}
          <Field label="Timezone"><input value={editing.timezone || ""} onChange={e => setEditing(p => ({ ...p, timezone: e.target.value }))} style={inputStyle} placeholder="e.g. America/New_York" /></Field>
          {isSuperAdmin && (
            <>
              <Field label="Permissions (stackable)">
                <div style={{ padding: "12px 16px", border: "1px solid rgba(252,214,87,0.2)", background: "rgba(252,214,87,0.03)" }}>
                  <PermToggle label="Super Admin" checked={editing.perm_super_admin || false} onChange={() => setEditing(p => ({ ...p, perm_super_admin: !p.perm_super_admin }))} />
                  <PermToggle label="Division Lead" checked={editing.perm_division_lead || false} onChange={() => setEditing(p => ({ ...p, perm_division_lead: !p.perm_division_lead }))} />
                  <PermToggle label="Team Lead" checked={editing.perm_team_lead || false} onChange={() => setEditing(p => ({ ...p, perm_team_lead: !p.perm_team_lead }))} />
                </div>
              </Field>
              {(editing.perm_division_lead || editing.perm_team_lead) && (
                <Field label="Managed Team / Division">
                  <select value={editing.managed_team || ""} onChange={e => setEditing(p => ({ ...p, managed_team: e.target.value }))} style={selectStyle}>
                    <option value="">-- None --</option>
                    {teams.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                </Field>
              )}
            </>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <ActionBtn onClick={saveMember} color={ACCENT}>{saving ? "Saving..." : "Save Changes"}</ActionBtn>
            <ActionBtn onClick={() => setEditing(null)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}

      {showAddForm && (
        <Modal title="Add Member Manually" onClose={() => setShowAddForm(false)}>
          <Field label="Discord Username"><input value={newMember.discord_username} onChange={e => setNewMember(p => ({ ...p, discord_username: e.target.value }))} style={inputStyle} placeholder="e.g. SpartanBoy45" /></Field>
          <Field label="Org Role"><select value={newMember.org_role} onChange={e => setNewMember(p => ({ ...p, org_role: e.target.value }))} style={selectStyle}>{orgRolesList.map(r => <option key={r} value={r}>{r}</option>)}</select></Field>
          <ChipPicker label="Teams" options={teams.map(t => t.name)} selected={newMember.teams} onChange={v => setNewMember(p => ({ ...p, teams: v }))} colorMap={teamColorMap} />
          <ChipPicker label="Divisions" options={ALL_DIVISIONS} selected={newMember.divisions} onChange={v => setNewMember(p => ({ ...p, divisions: v }))} colorMap={divColorMap} />
          <Field label="Team Role"><input value={newMember.team_role} onChange={e => setNewMember(p => ({ ...p, team_role: e.target.value }))} style={inputStyle} placeholder="e.g. Crew" /></Field>
          <Field label="Timezone"><input value={newMember.timezone} onChange={e => setNewMember(p => ({ ...p, timezone: e.target.value }))} style={inputStyle} placeholder="e.g. America/Chicago" /></Field>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <ActionBtn onClick={addMember} color={ACCENT}>{saving ? "Adding..." : "Add Member"}</ActionBtn>
            <ActionBtn onClick={() => setShowAddForm(false)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}

      {showBulkForm && (
        <Modal title="Bulk Import Members" onClose={() => { setShowBulkForm(false); setBulkResult(null); setBulkText(""); }}>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.5)", marginBottom: 20, lineHeight: 1.7 }}>Paste Discord usernames one per line. All added as <span style={{ color: ACCENT }}>Member</span> rank with no team assigned.</p>
          <Field label="Discord Usernames (one per line)">
            <textarea rows={10} value={bulkText} onChange={e => setBulkText(e.target.value)} style={{ ...inputStyle, resize: "vertical", fontFamily: "'Space Mono', monospace", fontSize: 12 }} placeholder={"SpartanBoy45\nEminent Oreo\nHypx Eagle\n..."} />
          </Field>
          {bulkResult && <div style={{ padding: "12px 16px", border: "1px solid #34d39944", background: "rgba(52,211,153,0.06)", marginBottom: 16 }}><div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#34d399" }}>{bulkResult.added} added &middot; {bulkResult.skipped} skipped</div></div>}
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn onClick={bulkImport} color="#4fc3f7">{bulkImporting ? "Importing..." : `Import ${bulkText.split("\n").filter(n => n.trim()).length} Members`}</ActionBtn>
            <ActionBtn onClick={() => { setShowBulkForm(false); setBulkResult(null); setBulkText(""); }} color="rgba(255,255,255,0.3)">Close</ActionBtn>
          </div>
        </Modal>
      )}

      {showDiscordImport && (
        <Modal title="Discord Server Import" onClose={() => { setShowDiscordImport(false); setDiscordPreview(null); setDiscordResult(null); }}>
          {discordFetching && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 3, color: "#5865f2", marginBottom: 12 }}>FETCHING FROM DISCORD...</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.4)" }}>Pulling all server members via bot</div>
            </div>
          )}
          {!discordFetching && discordPreview?.error && (
            <div>
              <div style={{ padding: "14px 16px", border: "1px solid #ef535044", background: "rgba(239,83,80,0.06)", marginBottom: 20 }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ef5350", marginBottom: 4 }}>ERROR</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.6)" }}>{discordPreview.error}</div>
              </div>
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.4)", marginBottom: 16 }}>
                Make sure <code style={{ color: "#5865f2" }}>DISCORD_BOT_TOKEN</code> and <code style={{ color: "#5865f2" }}>DISCORD_GUILD_ID</code> are set in your Vercel environment variables.
              </p>
              <ActionBtn onClick={fetchDiscordMembers} color="#5865f2">Retry</ActionBtn>
            </div>
          )}
          {!discordFetching && discordResult && (
            <div>
              <div style={{ padding: "16px 20px", border: "1px solid #34d39944", background: "rgba(52,211,153,0.06)", marginBottom: 20 }}>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: "#34d399", marginBottom: 8 }}>Import Complete</div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#34d399" }}>{discordResult.added} new members added</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#4fc3f7" }}>{discordResult.updated} profiles updated</div>
                  {discordResult.promoted > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ff9800" }}>{discordResult.promoted} promoted to member</div>}
                  {discordResult.flushed > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#fcd657" }}>{discordResult.flushed} duplicates removed</div>}
                  {discordResult.leavers?.length > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ef5350" }}>{discordResult.leavers.length} marked as left server</div>}
                  {discordResult.vacanciesCreated?.length > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ce93d8" }}>{discordResult.vacanciesCreated.length} vacancies auto-created</div>}
                  {discordResult.failed > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ef5350" }}>{discordResult.failed} failed</div>}
                </div>
                {discordResult.firstError && (
                  <div style={{ marginTop: 10, fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#ef5350", wordBreak: "break-all" }}>Error: {discordResult.firstError}</div>
                )}
                {discordResult.leavers?.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#ef5350", textTransform: "uppercase", marginBottom: 8 }}>Members who left</div>
                    <div style={{ maxHeight: 140, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                      {discordResult.leavers.map(l => (
                        <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", background: "rgba(239,83,80,0.04)", border: "1px solid rgba(239,83,80,0.12)" }}>
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.6)" }}>{l.discord_username}</span>
                          {(l.teams || []).length > 0 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(239,83,80,0.5)" }}>({(l.teams || []).join(", ")})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {discordResult.vacanciesCreated?.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#ce93d8", textTransform: "uppercase", marginBottom: 8 }}>Vacancies Created</div>
                    <div style={{ maxHeight: 140, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                      {discordResult.vacanciesCreated.map((v, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", background: "rgba(206,147,216,0.04)", border: "1px solid rgba(206,147,216,0.12)" }}>
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.6)" }}>{v.team}</span>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "#ce93d8" }}>{v.position}{v.sub_group ? ` · ${v.sub_group}` : ""}</span>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.3)", marginLeft: "auto" }}>was {v.left_by}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <ActionBtn onClick={() => { setShowDiscordImport(false); setDiscordPreview(null); setDiscordResult(null); }} color={ACCENT}>Done</ActionBtn>
            </div>
          )}
          {!discordFetching && !discordResult && discordPreview && !discordPreview.error && (
            <div>
              <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
                <div style={{ padding: "12px 16px", border: "1px solid #34d39944", background: "rgba(52,211,153,0.06)", flex: 1, minWidth: 120 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "#34d399", lineHeight: 1 }}>{discordPreview.toAdd.length}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(52,211,153,0.6)", marginTop: 4 }}>NEW TO ADD</div>
                </div>
                <div style={{ padding: "12px 16px", border: "1px solid #4fc3f744", background: "rgba(79,195,247,0.06)", flex: 1, minWidth: 120 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "#4fc3f7", lineHeight: 1 }}>{discordPreview.toUpdate.length}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(79,195,247,0.6)", marginTop: 4 }}>TO UPDATE</div>
                </div>
                {discordPreview.toLeaveMark?.length > 0 && (
                  <div style={{ padding: "12px 16px", border: "1px solid #ef535044", background: "rgba(239,83,80,0.06)", flex: 1, minWidth: 120 }}>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "#ef5350", lineHeight: 1 }}>{discordPreview.toLeaveMark.length}</div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(239,83,80,0.6)", marginTop: 4 }}>LEFT SERVER</div>
                  </div>
                )}
                <div style={{ padding: "12px 16px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)", flex: 1, minWidth: 120 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "rgba(232,234,246,0.4)", lineHeight: 1 }}>{discordPreview.total}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.3)", marginTop: 4 }}>TOTAL IN SERVER</div>
                </div>
              </div>
              {discordPreview.toAdd.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#34d399", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 16, height: 1, background: "#34d399" }} /> New Members ({discordPreview.toAdd.length})
                  </div>
                  <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                    {discordPreview.toAdd.map(dm => (
                      <div key={dm.discord_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "rgba(52,211,153,0.04)", border: "1px solid rgba(52,211,153,0.15)" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: "1px solid rgba(52,211,153,0.3)" }}>
                          {dm.discord_avatar ? <img src={dm.discord_avatar} alt={dm.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 10, color: "#34d399" }}>{dm.discord_username?.charAt(0)?.toUpperCase()}</div>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{dm.discord_username}</div>
                          {dm.nickname && dm.nickname !== dm.discord_username && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.35)" }}>aka {dm.nickname}</div>}
                        </div>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#34d399", border: "1px solid #34d39933", padding: "1px 6px" }}>NEW</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {discordPreview.toUpdate.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#4fc3f7", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 16, height: 1, background: "#4fc3f7" }} /> Role & Team Sync ({discordPreview.toUpdate.length})
                  </div>
                  <div style={{ maxHeight: 160, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                    {discordPreview.toUpdate.map(dm => (
                      <div key={dm.discord_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "rgba(79,195,247,0.04)", border: "1px solid rgba(79,195,247,0.15)" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: "1px solid rgba(79,195,247,0.3)" }}>
                          {dm.discord_avatar ? <img src={dm.discord_avatar} alt={dm.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 10, color: "#4fc3f7" }}>{dm.discord_username?.charAt(0)?.toUpperCase()}</div>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{dm.discord_username}</div>
                        </div>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#4fc3f7", border: "1px solid #4fc3f733", padding: "1px 6px" }}>UPDATE</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {discordPreview.toLeaveMark?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#ef5350", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 16, height: 1, background: "#ef5350" }} /> Left Server ({discordPreview.toLeaveMark.length})
                  </div>
                  <div style={{ maxHeight: 160, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                    {discordPreview.toLeaveMark.map(m => (
                      <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "rgba(239,83,80,0.04)", border: "1px solid rgba(239,83,80,0.15)" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: "1px solid rgba(239,83,80,0.3)" }}>
                          {m.discord_avatar ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 10, color: "#ef5350" }}>{m.discord_username?.charAt(0)?.toUpperCase()}</div>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.discord_username}</div>
                          {(m.teams || []).length > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(239,83,80,0.6)" }}>{(m.teams || []).join(", ")}</div>}
                        </div>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#ef5350", border: "1px solid #ef535033", padding: "1px 6px" }}>LEFT</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.35)", marginTop: 8 }}>
                    These members will be marked inactive and vacancies will be created for their team positions.
                  </div>
                </div>
              )}
              {discordPreview.toAdd.length === 0 && discordPreview.toUpdate.length === 0 && (!discordPreview.toLeaveMark || discordPreview.toLeaveMark.length === 0) && (
                <div style={{ padding: "24px", textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 2, marginBottom: 20 }}>ALL DISCORD MEMBERS ARE UP TO DATE</div>
              )}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {(discordPreview.toAdd.length > 0 || discordPreview.toUpdate.length > 0 || (discordPreview.toLeaveMark?.length || 0) > 0) && (
                  <ActionBtn onClick={runDiscordImport} color="#5865f2">{discordImporting ? "Syncing..." : `Sync ${discordPreview.toAdd.length + discordPreview.toUpdate.length} Members${discordPreview.toLeaveMark?.length ? ` · ${discordPreview.toLeaveMark.length} Left` : ""}`}</ActionBtn>
                )}
                <ActionBtn onClick={() => { setShowDiscordImport(false); setDiscordPreview(null); }} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEAM BUILDER
// ─────────────────────────────────────────────────────────────────────────────
function TeamBuilder({ adminProfile }) {
  const { isSuperAdmin, isDivisionLead, isTeamLead } = getPerms(adminProfile);
  const canCreateDelete = isSuperAdmin || isDivisionLead;

  const emptyForm = { name: "", divisions: [], description: "", about: "", tagline: "", operations: [], icon: "shield", color: "#ce93d8", patch_url: "", lead_id: "", co_lead_id: "", sub_groups: [], discord_role_id: "", discord_channel_id: "", discord_test_mode: false, discord_test_user_id: "", nav_label: "", digest_config: { enabled: false, days: [], time: "09:00" } };

  const [teams, setTeams] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [managingTeam, setManagingTeam] = useState(null);
  const [activeRosterTab, setActiveRosterTab] = useState("roster");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [newVacancy, setNewVacancy] = useState({ sub_group: "", position: "", quantity: 1 });
  const [newSubGroup, setNewSubGroup] = useState({ name: "", color: "#ef5350" });
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const fileInputRef = React.useRef(null);
  const [localEdits, setLocalEdits] = useState({});
  const debounceTimers = React.useRef({});
  const [discordChannels, setDiscordChannels] = useState([]);
  const [discordRoles, setDiscordRoles] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsFetched, setChannelsFetched] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [sendingTeamDigest, setSendingTeamDigest] = useState(false);

  async function sendTeamDigestPreview() {
    if (!form.name) { alert("Save the team first."); return; }
    setSendingTeamDigest(true);
    try {
      const now = new Date();
      const weekOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const todayStr = now.toISOString().slice(0, 10);
      const weekStr = weekOut.toISOString().slice(0, 10);
      // Fetch team events + org-wide events
      const { data: teamEvents } = await supabase.from("events").select("*").eq("archived", false).eq("team", form.name).gte("date", todayStr).lte("date", weekStr).order("date");
      const { data: orgEvents } = await supabase.from("events").select("*").eq("archived", false).eq("is_org_wide", true).gte("date", todayStr).lte("date", weekStr).order("date");
      // Merge and deduplicate
      const merged = [...(teamEvents || [])];
      if (orgEvents) { for (const oe of orgEvents) { if (!merged.find(e => e.id === oe.id)) merged.push(oe); } }
      merged.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

      const tz = adminProfile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const config = {
        test_mode: form.discord_test_mode,
        test_user_id: form.discord_test_user_id,
        channel_id: form.discord_channel_id,
        timezone: tz,
      };
      const r = await fetch("/api/discord-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "send_digest", config, events: merged, title: `📋 ${form.name} — Upcoming Events (Next 7 Days)` }),
      });
      const data = await r.json();
      if (r.ok) { alert(`Team digest sent! ${data.event_count || 0} events listed.`); }
      else { alert("Failed: " + (data.error || "Unknown error")); }
    } catch (err) { alert("Error: " + err.message); }
    setSendingTeamDigest(false);
  }

  async function fetchDiscordChannels() {
    if (channelsFetched) return;
    setChannelsLoading(true);
    try {
      const res = await fetch("/api/discord-channels?include=roles");
      const data = await res.json();
      if (res.ok && data.channels) {
        setDiscordChannels(data.channels);
        if (data.roles) setDiscordRoles(data.roles);
        setChannelsFetched(true);
      }
    } catch (e) { /* non-critical */ }
    setChannelsLoading(false);
  }

  // Close role dropdown on outside click
  useEffect(() => {
    if (!roleDropdownOpen) return;
    function handleClick(e) {
      if (!e.target.closest(".role-dropdown-container")) setRoleDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [roleDropdownOpen]);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: t }, { data: m }, { data: v }, { data: apps }] = await Promise.all([
      supabase.from("teams").select("*, lead:lead_id(id, discord_username, discord_avatar), co_lead:co_lead_id(id, discord_username, discord_avatar)").order("name"),
      supabase.from("profiles").select("id, discord_id, discord_username, discord_avatar, org_role, team, teams, divisions, team_role, team_roles, sub_group, sub_groups, sort_order, sort_orders, perm_super_admin, perm_division_lead, perm_team_lead, admin_tier, discord_roles").order("discord_username"),
      supabase.from("vacancies").select("*"),
      supabase.from("team_applications").select("*, applicant:user_id(id, discord_username, discord_avatar)").order("created_at", { ascending: false }),
    ]);
    setTeams(t || []); setAllMembers(m || []); setVacancies(v || []); setApplications(apps || []); setLoading(false);
  }

  const allVisibleTeams = (isTeamLead && !isSuperAdmin && !isDivisionLead)
    ? teams.filter(t => t.name === adminProfile?.managed_team)
    : teams;
  const visibleTeams = allVisibleTeams.filter(t => !t.archived);
  const archivedTeams = allVisibleTeams.filter(t => t.archived);

  function getLocal(memberId, field, fallback) {
    return localEdits[memberId]?.[field] !== undefined ? localEdits[memberId][field] : fallback;
  }

  function setLocalAndDebounce(memberId, field, value, saveFn) {
    setLocalEdits(prev => ({ ...prev, [memberId]: { ...(prev[memberId] || {}), [field]: value } }));
    const key = `${memberId}_${field}`;
    clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => saveFn(value), 800);
  }

  async function handleFileUpload(e) {
    const raw = e.target.files?.[0];
    if (!raw) return;
    if (!["image/png", "image/jpeg", "image/gif", "image/webp"].includes(raw.type)) { alert("Only PNG, JPEG, GIF, WEBP allowed."); return; }
    setUploading(true);
    // Optimize team patches client-side before upload: patches render at ~80–160px
    // in nav / division tiles / profile chips, so 600px max is plenty.
    const file = await optimizeImageFile(raw, { maxDim: 600, quality: 0.85 });
    const ext = file.name.split(".").pop();
    const filename = `patches/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("team-assets").upload(filename, file, { contentType: file.type, upsert: false });
    if (error) { alert("Upload failed: " + error.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from("team-assets").getPublicUrl(filename);
    setForm(p => ({ ...p, patch_url: urlData.publicUrl }));
    setUploadPreview(urlData.publicUrl);
    setUploading(false);
  }

  async function saveTeam() {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description, about: form.about,
      tagline: form.tagline, operations: form.operations, icon: form.icon,
      color: form.color, patch_url: form.patch_url, sub_groups: form.sub_groups,
      discord_role_id: form.discord_role_id || null,
      discord_channel_id: form.discord_channel_id || null,
      discord_test_mode: form.discord_test_mode || false,
      discord_test_user_id: form.discord_test_user_id || null,
      nav_label: form.nav_label || null,
      digest_config: form.digest_config ? { ...form.digest_config, timezone: adminProfile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone } : null,
      divisions: toArray(form.divisions),
      // Keep legacy single division for backwards compat
      division: form.divisions[0] || null,
      ...(isSuperAdmin || isDivisionLead ? {
        lead_id: form.lead_id || null,
        co_lead_id: form.co_lead_id || null,
      } : {}),
    };
    if (editTeam) {
      await supabase.from("teams").update(payload).eq("id", editTeam.id);
      if ((isSuperAdmin || isDivisionLead) && form.lead_id) {
        const { data: lp } = await supabase.from("profiles").select("perm_super_admin, admin_tier").eq("id", form.lead_id).single();
        if (!lp?.perm_super_admin && lp?.admin_tier !== "super_admin") {
          await supabase.from("profiles").update({ managed_team: form.name, perm_team_lead: true, admin_tier: "team_lead", is_admin: true }).eq("id", form.lead_id);
        }
      }
    } else {
      const { data: newTeam } = await supabase.from("teams").insert([payload]).select().single();
      if ((isSuperAdmin || isDivisionLead) && form.lead_id && newTeam) {
        const { data: lp } = await supabase.from("profiles").select("perm_super_admin, admin_tier").eq("id", form.lead_id).single();
        if (!lp?.perm_super_admin && lp?.admin_tier !== "super_admin") {
          await supabase.from("profiles").update({ managed_team: newTeam.name, perm_team_lead: true, admin_tier: "team_lead", is_admin: true }).eq("id", form.lead_id);
        }
      }
    }
    setSaving(false); setShowForm(false); setEditTeam(null); setUploadPreview(null); setForm(emptyForm); loadData();
  }

  async function deleteTeam(id) {
    if (!canCreateDelete || !window.confirm("Delete this team? Members will be unassigned.")) return;
    await supabase.from("teams").delete().eq("id", id); loadData();
  }

  async function archiveTeam(id) {
    if (!canCreateDelete || !window.confirm("Archive this team? It will be hidden from the Hierarchy and member-facing views, but members and configuration are preserved. You can restore it from the Archived section below.")) return;
    await supabase.from("teams").update({ archived: true }).eq("id", id);
    if (managingTeam?.id === id) setManagingTeam(null);
    loadData();
  }

  async function restoreTeam(id) {
    if (!canCreateDelete) return;
    await supabase.from("teams").update({ archived: false }).eq("id", id);
    loadData();
  }

  function openEdit(team) {
    if (isTeamLead && !isSuperAdmin && !isDivisionLead && team.name !== adminProfile?.managed_team) return;
    setEditTeam(team); setUploadPreview(team.patch_url || null); setRoleSearch(""); setRoleDropdownOpen(false);
    setForm({
      name: team.name,
      divisions: toArray(team.divisions).length > 0 ? toArray(team.divisions) : (team.division ? [team.division] : []),
      description: team.description || "", about: team.about || "",
      tagline: team.tagline || "", operations: team.operations || [],
      icon: team.icon || "shield", color: team.color || "#ce93d8",
      patch_url: team.patch_url || "", lead_id: team.lead_id || "", co_lead_id: team.co_lead_id || "", sub_groups: team.sub_groups || [], discord_role_id: team.discord_role_id || "", discord_channel_id: team.discord_channel_id || "", discord_test_mode: team.discord_test_mode || false, discord_test_user_id: team.discord_test_user_id || "", nav_label: team.nav_label || "",
      digest_config: team.digest_config || { enabled: false, days: [], time: "09:00" },
    });
    setShowForm(true);
    fetchDiscordChannels();
  }

  async function addToTeam(memberId) {
    const member = allMembers.find(m => m.id === memberId);
    const existing = toArray(member?.teams);
    if (!existing.includes(managingTeam.name)) {
      const updated = [...existing, managingTeam.name];
      await supabase.from("profiles").update({ teams: updated, team: updated[0] }).eq("id", memberId);
    }
    loadData();
  }

  async function removeFromTeam(memberId) {
    const member = allMembers.find(m => m.id === memberId);
    const updated = toArray(member?.teams).filter(t => t !== managingTeam.name);
    await supabase.from("profiles").update({
      teams: updated, team: updated[0] || null,
      ...(updated.length === 0 ? { team_role: null, sub_group: null, sort_order: null } : {}),
    }).eq("id", memberId);
    setLocalEdits(prev => { const n = { ...prev }; delete n[memberId]; return n; });
    loadData();
  }

  async function updateTeamRole(memberId, role) {
    const member = allMembers.find(m => m.id === memberId);
    const existingRoles = member?.team_roles || {};
    const updated = { ...existingRoles, [managingTeam.name]: role };
    await supabase.from("profiles").update({ team_roles: updated, team_role: role }).eq("id", memberId);
  }
  async function updateSubGroup(memberId, subGroup) {
    const member = allMembers.find(m => m.id === memberId);
    const existing = member?.sub_groups || {};
    const updated = { ...existing, [managingTeam.name]: subGroup || null };
    await supabase.from("profiles").update({ sub_groups: updated, sub_group: subGroup || null }).eq("id", memberId);
    loadData();
  }
  async function updateSortOrder(memberId, order) {
    const member = allMembers.find(m => m.id === memberId);
    const existing = member?.sort_orders || {};
    const updated = { ...existing, [managingTeam.name]: order || null };
    await supabase.from("profiles").update({ sort_orders: updated, sort_order: order || null }).eq("id", memberId);
  }

  async function addVacancy() {
    if (!newVacancy.position.trim() || !newVacancy.sub_group.trim()) return;
    await supabase.from("vacancies").insert([{ team_id: managingTeam.id, ...newVacancy, quantity: parseInt(newVacancy.quantity) || 1 }]);
    setNewVacancy({ sub_group: "", position: "", quantity: 1 }); loadData();
  }

  async function deleteVacancy(id) { await supabase.from("vacancies").delete().eq("id", id); loadData(); }

  function addSubGroupToForm() {
    if (!newSubGroup.name.trim() || form.sub_groups.find(g => g.name === newSubGroup.name)) return;
    setForm(p => ({ ...p, sub_groups: [...p.sub_groups, { ...newSubGroup }] }));
    setNewSubGroup({ name: "", color: "#ef5350" });
  }

  const divColorMap = { "Industry": "#fcd657", "Security": "#ef5350" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div><SectionLabel>Admin</SectionLabel><h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6" }}>Team Builder</h2></div>
        {canCreateDelete && <ActionBtn onClick={() => { setEditTeam(null); setUploadPreview(null); setForm(emptyForm); setRoleSearch(""); setRoleDropdownOpen(false); setShowForm(true); fetchDiscordChannels(); }} color={ACCENT}>+ New Team</ActionBtn>}
      </div>

      {loading ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING TEAMS...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {visibleTeams.map(team => {
            const teamDivisions = toArray(team.divisions).length > 0 ? toArray(team.divisions) : (team.division ? [team.division] : []);
            const members = sortMembers(allMembers.filter(m => toArray(m.teams).includes(team.name)));
            const nonMembers = allMembers.filter(m => !toArray(m.teams).includes(team.name));
            const teamVacancies = vacancies.filter(v => v.team_id === team.id);
            const color = team.color || ACCENT;
            const subGroups = team.sub_groups || [];
            return (
              <div key={team.id} style={{ border: `1px solid ${color}33`, background: `${color}05`, overflow: "hidden" }}>
                <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", borderBottom: `1px solid ${color}22` }}>
                  {team.patch_url && <img src={team.patch_url} alt={team.name} style={{ width: 52, height: 52, objectFit: "contain", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />}
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: "#e8eaf6" }}>{team.name}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                      {teamDivisions.map(d => <span key={d} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "1px 7px", border: `1px solid ${divColorMap[d] || "#ce93d8"}55`, background: `${divColorMap[d] || "#ce93d8"}11`, color: divColorMap[d] || "#ce93d8", textTransform: "uppercase", letterSpacing: 1 }}>{d}</span>)}
                    </div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>{members.length} member{members.length !== 1 ? "s" : ""} &middot; {subGroups.length} group{subGroups.length !== 1 ? "s" : ""} &middot; {teamVacancies.length} vacanc{teamVacancies.length !== 1 ? "ies" : "y"}</div>
                    {subGroups.length > 0 && <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>{subGroups.map(g => <span key={g.name} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "2px 8px", border: `1px solid ${g.color}66`, background: `${g.color}12`, color: g.color, textTransform: "uppercase" }}>{g.name}</span>)}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <ActionBtn small onClick={() => { setManagingTeam(managingTeam?.id === team.id ? null : team); setActiveRosterTab("roster"); }} color={color}>{managingTeam?.id === team.id ? "Close" : "Manage"}</ActionBtn>
                    <ActionBtn small onClick={() => openEdit(team)} color={ACCENT}>Edit</ActionBtn>
                    {canCreateDelete && <ActionBtn small onClick={() => archiveTeam(team.id)} color="#9aa0a6">Archive</ActionBtn>}
                    {canCreateDelete && <ActionBtn small onClick={() => deleteTeam(team.id)} color="#ef5350">Delete</ActionBtn>}
                  </div>
                </div>

                {managingTeam?.id === team.id && (
                  <div>
                    <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.2)", flexWrap: "wrap" }}>
                      {(() => {
                        const pendingApps = applications.filter(a => a.team_id === team.id && a.status === "pending");
                        return [
                          { id: "roster", label: `Roster (${members.length})` },
                          { id: "add", label: "Add Members" },
                          { id: "vacancies", label: `Vacancies (${teamVacancies.length})` },
                          { id: "pending", label: `Pending (${pendingApps.length})`, highlight: pendingApps.length > 0 },
                        ].map(tab => (
                          <button key={tab.id} onClick={() => setActiveRosterTab(tab.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 18px", border: "none", cursor: "pointer", background: activeRosterTab === tab.id ? `${color}12` : "transparent", color: activeRosterTab === tab.id ? color : tab.highlight ? "#fcd657" : "rgba(232,234,246,0.35)", borderBottom: activeRosterTab === tab.id ? `2px solid ${color}` : "2px solid transparent", transition: "all 0.2s" }}>{tab.label}</button>
                        ));
                      })()}
                    </div>
                    <div style={{ padding: "20px 24px" }}>
                      {activeRosterTab === "roster" && (() => {
                        // Per-team sort order helper
                        const getTeamSortOrder = (m) => m.sort_orders?.[team.name] ?? m.sort_order ?? null;
                        const getTeamSubGrp = (m) => m.sub_groups?.[team.name] ?? m.sub_group ?? null;

                        const sortedMembers = [...members].sort((a, b) => {
                          const sa = parseFloat(getTeamSortOrder(a));
                          const sb = parseFloat(getTeamSortOrder(b));
                          if (!isNaN(sa) && !isNaN(sb) && sa !== sb) return sa - sb;
                          if (!isNaN(sa)) return -1;
                          if (!isNaN(sb)) return 1;
                          return (a.discord_username || "").localeCompare(b.discord_username || "");
                        });
                        const groupNames = subGroups.length > 0
                          ? [...new Set([...subGroups.map(g => g.name), ...sortedMembers.map(m => getTeamSubGrp(m)).filter(Boolean)])]
                          : [];
                        const ungrouped = sortedMembers.filter(m => !getTeamSubGrp(m));
                        const ungroupedVacancies = teamVacancies.filter(v => !v.sub_group || !subGroups.find(g => g.name === v.sub_group));

                        const renderMemberRow = (m) => {
                          const perTeamSubGrp = getTeamSubGrp(m);
                          const mg = subGroups.find(g => g.name === perTeamSubGrp);
                          const mc = mg?.color || "rgba(255,255,255,0.15)";
                          const memberTeamsList = toArray(m.teams);
                          const perTeamRole = (m.team_roles && m.team_roles[team.name]) || "";
                          const perTeamOrder = getTeamSortOrder(m) || "";
                          const localRoleKey = `teamrole_${team.name}`;
                          const localOrderKey = `sortorder_${team.name}`;
                          return (
                            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: mg ? `${mg.color}08` : "rgba(255,255,255,0.02)", border: `1px solid ${mg ? mg.color + "33" : "rgba(255,255,255,0.06)"}` }}>
                              <div style={{ width: 3, height: 36, borderRadius: 2, background: mc, flexShrink: 0 }} />
                              <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${mc}55` }}>
                                {m.discord_avatar ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontFamily: "'Orbitron'", fontSize: 10, color: mc }}>{m.discord_username?.charAt(0)}</span>}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.discord_username}</div>
                                {memberTeamsList.length > 1 && (
                                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 2 }}>
                                    {memberTeamsList.filter(t => t !== team.name).map(t => <span key={t} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, padding: "0px 4px", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.35)" }}>+{t}</span>)}
                                  </div>
                                )}
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 2 }}>
                                  <input
                                    value={getLocal(m.id, localRoleKey, perTeamRole)}
                                    onChange={e => setLocalAndDebounce(m.id, localRoleKey, e.target.value, val => updateTeamRole(m.id, val))}
                                    placeholder="Role in this team..."
                                    style={{ ...inputStyle, padding: "2px 6px", fontSize: 11, width: 140 }}
                                  />
                                  {subGroups.length > 0 && (
                                    <select value={perTeamSubGrp || ""} onChange={e => updateSubGroup(m.id, e.target.value)} style={{ ...selectStyle, padding: "2px 6px", fontSize: 11, width: 120 }}>
                                      <option value="">No group</option>
                                      {subGroups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                    </select>
                                  )}
                                  <input
                                    value={getLocal(m.id, localOrderKey, perTeamOrder)}
                                    onChange={e => setLocalAndDebounce(m.id, localOrderKey, e.target.value, val => updateSortOrder(m.id, val))}
                                    placeholder="Order..."
                                    title="Sort order for this team (e.g. 1, 2, 1.1, 1.2)"
                                    style={{ ...inputStyle, padding: "2px 6px", fontSize: 11, width: 70 }}
                                  />
                                </div>
                              </div>
                              <button onClick={() => removeFromTeam(m.id)} style={{ background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>&times;</button>
                            </div>
                          );
                        };

                        const renderVacancyRow = (v, vc) => (
                          <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: `${vc}05`, border: `1px dashed ${vc}44`, opacity: 0.75 }}>
                            <div style={{ width: 3, height: 36, borderRadius: 2, background: vc, flexShrink: 0, opacity: 0.5 }} />
                            <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, background: "transparent", border: `1px dashed ${vc}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span style={{ fontFamily: "'Space Mono'", fontSize: 10, color: vc }}>?</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: `${vc}99` }}>{v.position} &times;{v.quantity}</div>
                              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: `${vc}66`, letterSpacing: 1, textTransform: "uppercase" }}>vacant</div>
                            </div>
                          </div>
                        );

                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxHeight: 500, overflowY: "auto" }}>
                            {members.length === 0 && teamVacancies.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 32 }}>NO MEMBERS YET</div>}
                            {groupNames.map(groupName => {
                              const grp = subGroups.find(g => g.name === groupName);
                              const gc = grp?.color || color;
                              const grpMembers = sortedMembers.filter(m => getTeamSubGrp(m) === groupName);
                              const grpVacancies = teamVacancies.filter(v => v.sub_group === groupName);
                              if (grpMembers.length === 0 && grpVacancies.length === 0) return null;
                              return (
                                <div key={groupName}>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: gc, textTransform: "uppercase", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ width: 16, height: 1, background: gc }} /> {groupName} ({grpMembers.length} filled / {grpVacancies.reduce((s, v) => s + v.quantity, 0)} open)
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    {grpMembers.map(m => renderMemberRow(m))}
                                    {grpVacancies.map(v => renderVacancyRow(v, gc))}
                                  </div>
                                </div>
                              );
                            })}
                            {(ungrouped.length > 0 || ungroupedVacancies.length > 0) && (
                              <div>
                                {groupNames.length > 0 && (
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ width: 16, height: 1, background: "rgba(232,234,246,0.15)" }} /> Unassigned
                                  </div>
                                )}
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  {ungrouped.map(m => renderMemberRow(m))}
                                  {ungroupedVacancies.map(v => renderVacancyRow(v, color))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {activeRosterTab === "add" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <input
                            placeholder="Search members..."
                            value={addMemberSearch}
                            onChange={e => setAddMemberSearch(e.target.value)}
                            style={{ ...inputStyle, padding: "8px 12px", fontSize: 13 }}
                          />
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
                            {(() => {
                              const filtered = nonMembers.filter(m =>
                                !addMemberSearch || m.discord_username?.toLowerCase().includes(addMemberSearch.toLowerCase())
                              );
                              if (nonMembers.length === 0) return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 32 }}>ALL MEMBERS ASSIGNED</div>;
                              if (filtered.length === 0) return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 32 }}>NO MEMBERS MATCH</div>;
                              return filtered.map(m => (
                                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                                  <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {m.discord_avatar ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontFamily: "'Orbitron'", fontSize: 10, color: "rgba(232,234,246,0.4)" }}>{m.discord_username?.charAt(0)}</span>}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "rgba(232,234,246,0.7)" }}>{m.discord_username}</div>
                                    {toArray(m.teams).length > 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>{toArray(m.teams).join(", ")}</div>}
                                  </div>
                                  <button onClick={() => { addToTeam(m.id); setAddMemberSearch(""); }} style={{ background: "none", border: `1px solid ${color}55`, color, cursor: "pointer", fontSize: 18, flexShrink: 0, padding: "2px 10px", borderRadius: 2 }}>+</button>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                      )}

                      {activeRosterTab === "pending" && (() => {
                        const teamApps = applications.filter(a => a.team_id === team.id);
                        const pending = teamApps.filter(a => a.status === "pending");
                        const reviewed = teamApps.filter(a => a.status !== "pending");

                        async function approveApplication(app) {
                          await addToTeam(app.user_id);
                          await supabase.from("team_applications").update({ status: "approved" }).eq("id", app.id);
                          loadData();
                        }
                        async function denyApplication(appId) {
                          await supabase.from("team_applications").update({ status: "denied" }).eq("id", appId);
                          loadData();
                        }

                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {pending.length === 0 && (
                              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 32 }}>NO PENDING APPLICATIONS</div>
                            )}
                            {pending.map(app => (
                              <div key={app.id} style={{ padding: "14px 16px", border: `1px solid ${color}44`, background: `${color}06` }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                  <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: `1px solid ${color}44` }}>
                                    {app.applicant?.discord_avatar
                                      ? <img src={app.applicant.discord_avatar} alt={app.applicant.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                      : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 11, color }}>{app.applicant?.discord_username?.charAt(0)?.toUpperCase()}</div>}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{app.applicant?.discord_username}</div>
                                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>{new Date(app.created_at).toLocaleDateString()}</div>
                                  </div>
                                </div>
                                {app.selected_positions?.length > 0 && (
                                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                                    {app.selected_positions.map(p => (
                                      <span key={p} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "2px 8px", border: `1px solid ${color}55`, background: `${color}11`, color, letterSpacing: 1, textTransform: "uppercase" }}>{p}</span>
                                    ))}
                                  </div>
                                )}
                                {app.message && (
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.65)", marginBottom: 12, fontStyle: "italic", borderLeft: `2px solid ${color}44`, paddingLeft: 10 }}>{app.message}</div>
                                )}
                                <div style={{ display: "flex", gap: 8 }}>
                                  <ActionBtn small onClick={() => approveApplication(app)} color="#81c784">Approve</ActionBtn>
                                  <ActionBtn small onClick={() => denyApplication(app.id)} color="#ef5350">Deny</ActionBtn>
                                </div>
                              </div>
                            ))}
                            {reviewed.length > 0 && (
                              <div style={{ marginTop: 16 }}>
                                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.25)", textTransform: "uppercase", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 16, height: 1, background: "rgba(232,234,246,0.15)" }} /> Reviewed</div>
                                {reviewed.map(app => (
                                  <div key={app.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 4, opacity: 0.6 }}>
                                    <div style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)" }}>{app.applicant?.discord_username}</div>
                                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "2px 8px", border: `1px solid ${app.status === "approved" ? "#81c784" : "#ef5350"}55`, color: app.status === "approved" ? "#81c784" : "#ef5350", textTransform: "uppercase", letterSpacing: 1 }}>{app.status}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {activeRosterTab === "vacancies" && (
                        <div>
                          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1, marginBottom: 16 }}>Vacancies appear inline with their group in the Roster tab.</p>
                          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>Group</span>
                              {subGroups.length > 0 ? (
                                <select value={newVacancy.sub_group} onChange={e => setNewVacancy(p => ({ ...p, sub_group: e.target.value }))} style={{ ...selectStyle, width: 140, padding: "6px 10px" }}>
                                  <option value="">Select group...</option>
                                  {subGroups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                                </select>
                              ) : (
                                <input value={newVacancy.sub_group} onChange={e => setNewVacancy(p => ({ ...p, sub_group: e.target.value }))} style={{ ...inputStyle, width: 140, padding: "6px 10px" }} placeholder="Group name..." />
                              )}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>Position</span>
                              <input value={newVacancy.position} onChange={e => setNewVacancy(p => ({ ...p, position: e.target.value }))} style={{ ...inputStyle, width: 180, padding: "6px 10px" }} placeholder="e.g. Engineer" onKeyDown={e => { if (e.key === "Enter") addVacancy(); }} />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>Qty</span>
                              <input type="number" value={newVacancy.quantity} onChange={e => setNewVacancy(p => ({ ...p, quantity: e.target.value }))} style={{ ...inputStyle, width: 60, padding: "6px 10px" }} min="1" max="20" />
                            </div>
                            <ActionBtn small onClick={addVacancy} color={color}>+ Add</ActionBtn>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {teamVacancies.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 24 }}>NO VACANCIES DEFINED</div>}
                            {teamVacancies.map(v => {
                              const grp = subGroups.find(g => g.name === v.sub_group);
                              const vc = grp?.color || color;
                              return (
                                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", border: `1px solid ${vc}33`, background: `${vc}06` }}>
                                  <div style={{ width: 3, height: 28, borderRadius: 2, background: vc, flexShrink: 0 }} />
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: vc, width: 100, textTransform: "uppercase" }}>{v.sub_group}</div>
                                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6", flex: 1 }}>{v.position}</div>
                                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: vc }}>&times;{v.quantity}</div>
                                  <button onClick={() => deleteVacancy(v.id)} style={{ background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: 14 }}>&times;</button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {visibleTeams.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>NO TEAMS CREATED YET</div>}

          {/* Archived Teams */}
          {archivedTeams.length > 0 && (
            <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <div style={{ width: 24, height: 2, background: "rgba(232,234,246,0.4)" }} />
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>
                  Archived Teams ({archivedTeams.length})
                </span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.05)" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {archivedTeams.map(team => {
                  const teamDivisions = toArray(team.divisions).length > 0 ? toArray(team.divisions) : (team.division ? [team.division] : []);
                  const members = allMembers.filter(m => toArray(m.teams).includes(team.name));
                  const color = team.color || ACCENT;
                  return (
                    <div key={team.id} style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", opacity: 0.75 }}>
                      {team.patch_url && <img src={team.patch_url} alt={team.name} style={{ width: 36, height: 36, objectFit: "contain", flexShrink: 0, filter: "grayscale(60%)" }} onError={e => { e.target.style.display = "none"; }} />}
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, opacity: 0.6 }} />
                          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: "rgba(232,234,246,0.7)" }}>{team.name}</div>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "2px 8px", border: "1px solid rgba(232,234,246,0.2)", color: "rgba(232,234,246,0.5)", letterSpacing: 1, textTransform: "uppercase" }}>Archived</span>
                        </div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.35)", letterSpacing: 1, marginTop: 4 }}>
                          {members.length} member{members.length !== 1 ? "s" : ""}{teamDivisions.length > 0 ? ` · ${teamDivisions.join(", ")}` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        {canCreateDelete && <ActionBtn small onClick={() => restoreTeam(team.id)} color="#34d399">Restore</ActionBtn>}
                        {canCreateDelete && <ActionBtn small onClick={() => deleteTeam(team.id)} color="#ef5350">Delete</ActionBtn>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <Modal title={editTeam ? `Edit - ${editTeam.name}` : "Create New Team"} onClose={() => { setShowForm(false); setUploadPreview(null); }}>
          <Field label="Team Name"><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder="e.g. Iron Warden" /></Field>
          <Field label="Nav Label (optional)">
            <input value={form.nav_label || ""} onChange={e => setForm(p => ({ ...p, nav_label: e.target.value }))} style={inputStyle} placeholder="e.g. IW" />
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", marginTop: 6, letterSpacing: 1 }}>
              Short name shown in the navigation bar when a member sets this as their primary team. Leave blank to use the full team name.
            </div>
          </Field>
          <ChipPicker label="Divisions (select all that apply)" options={ALL_DIVISIONS} selected={toArray(form.divisions)} onChange={v => setForm(p => ({ ...p, divisions: v }))} colorMap={{ "Industry": "#fcd657", "Security": "#ef5350" }} />
          <Field label="Description"><textarea rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} placeholder="Short one-liner for hierarchy page..." /></Field>
          <Field label="Tagline"><input value={form.tagline} onChange={e => setForm(p => ({ ...p, tagline: e.target.value }))} style={inputStyle} placeholder="e.g. The ship is our platform." /></Field>
          <Field label="About the Team"><textarea rows={6} value={form.about} onChange={e => setForm(p => ({ ...p, about: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} placeholder="Full description. Separate paragraphs with a blank line." /></Field>
          <Field label="Team Icon (emoji)"><input value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} style={{ ...inputStyle, width: 80 }} placeholder="shield" /></Field>
          <Field label="Operations (comma separated)"><input value={Array.isArray(form.operations) ? form.operations.join(", ") : ""} onChange={e => setForm(p => ({ ...p, operations: e.target.value.split(",").map(o => o.trim()).filter(Boolean) }))} style={inputStyle} placeholder="e.g. Combat, Boarding, Defense" /></Field>
          <Field label="Sub-Groups / Squads">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {form.sub_groups.map(g => (
                <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", border: `1px solid ${g.color}44`, background: `${g.color}08` }}>
                  <div style={{ width: 16, height: 16, borderRadius: 2, background: g.color, flexShrink: 0 }} />
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6", flex: 1 }}>{g.name}</span>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: g.color }}>{g.color}</span>
                  <button onClick={() => setForm(p => ({ ...p, sub_groups: p.sub_groups.filter(sg => sg.name !== g.name) }))} style={{ background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: 14 }}>&times;</button>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input value={newSubGroup.name} onChange={e => setNewSubGroup(p => ({ ...p, name: e.target.value }))} style={{ ...inputStyle, flex: 1, minWidth: 120 }} placeholder="Group name (e.g. Red Team)" onKeyDown={e => { if (e.key === "Enter") addSubGroupToForm(); }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 200 }}>
                    {PRESET_COLORS.map(c => <div key={c} onClick={() => setNewSubGroup(p => ({ ...p, color: c }))} style={{ width: 20, height: 20, borderRadius: 3, background: c, cursor: "pointer", border: newSubGroup.color === c ? "2px solid #fff" : "1px solid transparent", boxShadow: newSubGroup.color === c ? `0 0 6px ${c}` : "none" }} />)}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="color" value={newSubGroup.color} onChange={e => setNewSubGroup(p => ({ ...p, color: e.target.value }))} style={{ width: 28, height: 28, padding: 0, border: "none", background: "none", cursor: "pointer" }} />
                    <input value={newSubGroup.color} onChange={e => setNewSubGroup(p => ({ ...p, color: e.target.value }))} style={{ ...inputStyle, width: 90, padding: "4px 8px", fontSize: 11, fontFamily: "'Space Mono', monospace" }} placeholder="#ffffff" />
                  </div>
                </div>
                <ActionBtn small onClick={addSubGroupToForm} color={newSubGroup.color}>+ Add Group</ActionBtn>
              </div>
            </div>
          </Field>
          <Field label="Team Patch / Logo">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {uploadPreview && <div style={{ width: 80, height: 80, border: "1px solid rgba(252,214,87,0.3)", padding: 4, display: "flex", alignItems: "center", justifyContent: "center" }}><img src={uploadPreview} alt="preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /></div>}
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button onClick={() => fileInputRef.current?.click()} style={{ padding: "8px 16px", background: "transparent", border: `1px solid ${ACCENT}55`, color: ACCENT, fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }} onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}15`; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  {uploading ? "Uploading..." : "Upload File"}
                </button>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)" }}>PNG, JPEG, GIF</span>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: "none" }} onChange={handleFileUpload} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
              </div>
              <input value={form.patch_url} onChange={e => { setForm(p => ({ ...p, patch_url: e.target.value })); setUploadPreview(e.target.value); }} style={inputStyle} placeholder="https://... (paste URL)" />
            </div>
          </Field>
          <Field label="Team Color">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {TEAM_COLORS.map(c => <div key={c} onClick={() => setForm(p => ({ ...p, color: c }))} style={{ width: 28, height: 28, borderRadius: "50%", background: c, cursor: "pointer", border: form.color === c ? "3px solid #fff" : "2px solid transparent", boxShadow: form.color === c ? `0 0 10px ${c}` : "none" }} />)}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} style={{ width: 28, height: 28, padding: 0, border: "none", background: "none", cursor: "pointer" }} />
                <input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} style={{ ...inputStyle, width: 90, padding: "4px 8px", fontSize: 11, fontFamily: "'Space Mono', monospace" }} placeholder="#ffffff" />
              </div>
            </div>
          </Field>
          <Field label="Discord Roles (optional)">
            <div className="role-dropdown-container" style={{ position: "relative" }}>
              <input
                value={roleSearch}
                onChange={e => { setRoleSearch(e.target.value); setRoleDropdownOpen(true); }}
                onFocus={() => { setRoleDropdownOpen(true); if (!channelsFetched) fetchDiscordChannels(); }}
                style={inputStyle}
                placeholder="Search roles..."
              />
              {/* Selected roles as tags */}
              {(() => {
                const ids = (form.discord_role_id || "").split(",").map(s => s.trim()).filter(Boolean);
                if (ids.length === 0) return null;
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {ids.map(rid => {
                      const r = discordRoles.find(dr => dr.id === rid);
                      return (
                        <div key={rid} style={{
                          padding: "4px 10px", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
                          background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", color: "#34d399",
                          display: "inline-flex", alignItems: "center", gap: 6,
                        }}>
                          {r?.color && r.color !== "#000000" && <div style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, flexShrink: 0 }} />}
                          {r ? r.name : rid}
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(52,211,153,0.5)" }}>[{rid}]</span>
                          <button onClick={() => { const next = ids.filter(x => x !== rid).join(","); setForm(p => ({ ...p, discord_role_id: next })); }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 14, cursor: "pointer", padding: 0, marginLeft: 2 }}>×</button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {roleDropdownOpen && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, maxHeight: 220, overflowY: "auto", background: "#0c0818", border: "1px solid rgba(252,214,87,0.25)", marginTop: 2 }}>
                  {channelsLoading ? (
                    <div style={{ padding: 16, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center" }}>LOADING ROLES...</div>
                  ) : (() => {
                    const filtered = discordRoles.filter(r => !roleSearch || r.name.toLowerCase().includes(roleSearch.toLowerCase()) || r.id.includes(roleSearch));
                    const selectedIds = (form.discord_role_id || "").split(",").map(s => s.trim()).filter(Boolean);
                    if (filtered.length === 0) return <div style={{ padding: 16, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center" }}>NO MATCHING ROLES</div>;
                    return filtered.map(r => {
                      const isSel = selectedIds.includes(r.id);
                      return (
                        <button key={r.id} onClick={() => {
                          const next = isSel ? selectedIds.filter(x => x !== r.id) : [...selectedIds, r.id];
                          setForm(p => ({ ...p, discord_role_id: next.join(",") }));
                          setRoleSearch("");
                        }} style={{
                          width: "100%", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                          background: isSel ? "rgba(52,211,153,0.1)" : "transparent",
                          border: "none", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", transition: "background 0.15s",
                        }}
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "rgba(252,214,87,0.08)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isSel ? "rgba(52,211,153,0.1)" : "transparent"; }}>
                          <div style={{ width: 16, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 12, color: isSel ? "#34d399" : "rgba(255,255,255,0.12)", flexShrink: 0 }}>{isSel ? "✓" : ""}</div>
                          {r.color && r.color !== "#000000" && <div style={{ width: 12, height: 12, borderRadius: "50%", background: r.color, flexShrink: 0 }} />}
                          <div style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 600, color: r.color && r.color !== "#000000" ? r.color : "#e8eaf6" }}>{r.name}</div>
                          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.25)" }}>[{r.id}]</div>
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", marginTop: 6, letterSpacing: 1 }}>
              Members with any of these Discord roles will be auto-added to this team on import
            </div>
          </Field>
          <Field label="Discord Event Channel (optional)">
            {discordChannels.length > 0 ? (
              <select value={form.discord_channel_id || ""} onChange={e => setForm(p => ({ ...p, discord_channel_id: e.target.value }))} style={selectStyle}>
                <option value="">-- No channel assigned --</option>
                {(() => {
                  const grouped = {};
                  const uncategorized = [];
                  discordChannels.forEach(ch => {
                    if (ch.category) {
                      if (!grouped[ch.category]) grouped[ch.category] = [];
                      grouped[ch.category].push(ch);
                    } else {
                      uncategorized.push(ch);
                    }
                  });
                  const elements = [];
                  Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).forEach(([cat, channels]) => {
                    elements.push(<optgroup key={cat} label={cat}>{channels.map(ch => <option key={ch.id} value={ch.id}>#{ch.name}</option>)}</optgroup>);
                  });
                  if (uncategorized.length > 0) {
                    uncategorized.forEach(ch => { elements.push(<option key={ch.id} value={ch.id}>#{ch.name}</option>); });
                  }
                  return elements;
                })()}
              </select>
            ) : (
              <input value={form.discord_channel_id || ""} onChange={e => setForm(p => ({ ...p, discord_channel_id: e.target.value }))} style={inputStyle} placeholder={channelsLoading ? "Loading channels..." : "e.g. 1305012345678901234"} />
            )}
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", marginTop: 6, letterSpacing: 1 }}>
              Team events will be posted to this Discord channel.
            </div>
            {/* Test mode toggle */}
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, discord_test_mode: !p.discord_test_mode }))}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
                  background: form.discord_test_mode ? "#fcd657" : "rgba(255,255,255,0.12)",
                  position: "relative", transition: "background 0.2s",
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: "50%", background: "#fff",
                  position: "absolute", top: 3,
                  left: form.discord_test_mode ? 21 : 3,
                  transition: "left 0.2s",
                }} />
              </button>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: form.discord_test_mode ? "#fcd657" : "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>
                Test Mode {form.discord_test_mode ? "ON" : "OFF"}
              </span>
            </div>
            {form.discord_test_mode && (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#fcd657", display: "block", marginBottom: 6 }}>SEND TEST DMs TO</label>
                <select value={form.discord_test_user_id || ""} onChange={e => setForm(p => ({ ...p, discord_test_user_id: e.target.value }))} style={{ ...selectStyle, borderColor: "#fcd65744" }}>
                  <option value="">-- Select test recipient --</option>
                  {allMembers.filter(m => m.discord_id).map(m => (
                    <option key={m.id} value={m.discord_id}>{m.discord_username}</option>
                  ))}
                </select>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#fcd657aa", marginTop: 6, letterSpacing: 1 }}>
                  Events will DM this user instead of posting to the channel. Turn off when done testing.
                </div>
              </div>
            )}
          </Field>
          {/* ── Team Events Digest ────────────────────────────────── */}
          <Field label="Weekly Events Digest">
            <div style={{ padding: 14, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.04)", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#a78bfa", textTransform: "uppercase" }}>Upcoming Events Alert</span>
                <div onClick={() => setForm(p => ({ ...p, digest_config: { ...p.digest_config, enabled: !p.digest_config.enabled } }))} style={{ width: 40, height: 22, borderRadius: 11, background: form.digest_config?.enabled ? "#a78bfa" : "rgba(255,255,255,0.1)", position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 3, left: form.digest_config?.enabled ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
                </div>
              </div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>
                The bot posts a summary of this team's events happening in the next 7 days to the team's Discord channel. Uses the channel and test mode settings above.
              </div>
              {form.digest_config?.enabled && (
                <>
                  <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.5)", textTransform: "uppercase", marginBottom: 6 }}>Post on</div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => {
                        const sel = (form.digest_config.days || []).includes(i);
                        return (
                          <button key={i} onClick={() => setForm(p => {
                            const days = p.digest_config.days || [];
                            return { ...p, digest_config: { ...p.digest_config, days: sel ? days.filter(x => x !== i) : [...days, i] } };
                          })} style={{
                            padding: "6px 8px", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, border: `1px solid ${sel ? "#a78bfa" : "rgba(255,255,255,0.1)"}`, background: sel ? "rgba(167,139,250,0.15)" : "transparent", color: sel ? "#a78bfa" : "rgba(232,234,246,0.4)", cursor: "pointer", transition: "all 0.2s",
                          }}>{d}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.5)", textTransform: "uppercase", marginBottom: 6 }}>Time ({(adminProfile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone).replace(/_/g, " ")})</div>
                    <input type="time" value={form.digest_config.time || "09:00"} onChange={e => setForm(p => ({ ...p, digest_config: { ...p.digest_config, time: e.target.value } }))} style={{ padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.3)", color: "#a78bfa", fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, outline: "none", colorScheme: "dark" }} />
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <button onClick={sendTeamDigestPreview} disabled={sendingTeamDigest} style={{
                      padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
                      background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.4)",
                      color: "#a78bfa", cursor: "pointer", transition: "all 0.2s", opacity: sendingTeamDigest ? 0.5 : 1,
                    }}>{sendingTeamDigest ? "Sending..." : "Send Preview Now"}</button>
                  </div>
                </>
              )}
            </div>
          </Field>
          {(isSuperAdmin || isDivisionLead) && (
            <Field label="Team Lead">
              <select value={form.lead_id || ""} onChange={e => setForm(p => ({ ...p, lead_id: e.target.value }))} style={selectStyle}>
                <option value="">-- No lead assigned --</option>
                {allMembers.map(m => <option key={m.id} value={m.id}>{m.discord_username} ({m.org_role})</option>)}
              </select>
            </Field>
          )}
          {(isSuperAdmin || isDivisionLead) && (
            <Field label="Co-Team Leader">
              <select value={form.co_lead_id || ""} onChange={e => setForm(p => ({ ...p, co_lead_id: e.target.value }))} style={selectStyle}>
                <option value="">-- No co-lead assigned --</option>
                {allMembers.map(m => <option key={m.id} value={m.id}>{m.discord_username} ({m.org_role})</option>)}
              </select>
            </Field>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <ActionBtn onClick={saveTeam} color={ACCENT}>{saving ? "Saving..." : editTeam ? "Save Changes" : "Create Team"}</ActionBtn>
            <ActionBtn onClick={() => { setShowForm(false); setUploadPreview(null); }} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIVISION VIEW
// ─────────────────────────────────────────────────────────────────────────────
function DivisionView() {
  const [teams, setTeams] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDiv, setActiveDiv] = useState(ALL_DIVISIONS[0]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [{ data: t }, { data: m }] = await Promise.all([
        supabase.from("teams").select("*").order("name"),
        supabase.from("profiles").select("id, discord_username, discord_avatar, org_role, teams, divisions, team_role, team_roles").order("discord_username"),
      ]);
      setTeams(t || []); setAllMembers(m || []); setLoading(false);
    }
    load();
  }, []);

  const divColorMap = { "Industry": "#fcd657", "Security": "#ef5350" };

  const divTeams = teams.filter(t => {
    const tDivs = toArray(t.divisions).length > 0 ? toArray(t.divisions) : (t.division ? [t.division] : []);
    return tDivs.includes(activeDiv);
  });

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <SectionLabel>Admin</SectionLabel>
        <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6", marginBottom: 20 }}>Divisions</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {ALL_DIVISIONS.map(d => {
            const active = activeDiv === d;
            const c = divColorMap[d] || ACCENT;
            return (
              <button key={d} onClick={() => setActiveDiv(d)} style={{ padding: "8px 20px", background: active ? `${c}22` : "transparent", border: `2px solid ${active ? c : `${c}44`}`, color: active ? c : `${c}88`, fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s" }}>{d}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING...</div>
      ) : divTeams.length === 0 ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>NO TEAMS IN {activeDiv.toUpperCase()} DIVISION</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {divTeams.map(team => {
            const color = team.color || ACCENT;
            const subGroups = team.sub_groups || [];
            const teamDivisions = toArray(team.divisions).length > 0 ? toArray(team.divisions) : (team.division ? [team.division] : []);
            const members = sortMembers(allMembers.filter(m => toArray(m.teams).includes(team.name)));

            return (
              <div key={team.id} style={{ border: `1px solid ${color}33`, background: `${color}04`, overflow: "hidden" }}>
                <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", borderBottom: `1px solid ${color}22` }}>
                  {team.patch_url && <img src={team.patch_url} alt={team.name} style={{ width: 48, height: 48, objectFit: "contain", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />}
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: "#e8eaf6" }}>{team.name}</div>
                    </div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 4 }}>
                      {teamDivisions.map(d => <span key={d} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "1px 7px", border: `1px solid ${divColorMap[d] || "#ce93d8"}55`, background: `${divColorMap[d] || "#ce93d8"}11`, color: divColorMap[d] || "#ce93d8", textTransform: "uppercase", letterSpacing: 1 }}>{d}</span>)}
                    </div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>{members.length} member{members.length !== 1 ? "s" : ""}{team.tagline ? ` · ${team.tagline}` : ""}</div>
                    {subGroups.length > 0 && <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>{subGroups.map(g => <span key={g.name} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "2px 8px", border: `1px solid ${g.color}66`, background: `${g.color}12`, color: g.color, textTransform: "uppercase" }}>{g.name}</span>)}</div>}
                  </div>
                </div>
                {members.length > 0 && (
                  <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {members.map(m => {
                      const memberTeams = toArray(m.teams);
                      const perTeamRole = (m.team_roles && m.team_roles[team.name]) || "";
                      return (
                        <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: `1px solid ${color}44` }}>
                            {m.discord_avatar ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 10, color }}>{m.discord_username?.charAt(0)?.toUpperCase()}</div>}
                          </div>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.discord_username}</span>
                            {perTeamRole && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: color, marginLeft: 8 }}>{perTeamRole}</span>}
                          </div>
                          {memberTeams.length > 1 && (
                            <div style={{ display: "flex", gap: 3 }}>
                              {memberTeams.filter(t => t !== team.name).map(t => <span key={t} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, padding: "1px 5px", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(232,234,246,0.3)" }}>+{t}</span>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EVENT MANAGER (with Active / Archived views + restore / permanent delete)
// ─────────────────────────────────────────────────────────────────────────────
function EventManager() {
  const { profile } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", type: "Combat", description: "", date: "", time: "", tags: "", allow_sharing: false, reminder: "discord", is_org_wide: true });
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("active"); // "active" | "archived"

  // Share channels settings
  const [allChannels, setAllChannels] = useState([]);
  const [shareChannelIds, setShareChannelIds] = useState([]);
  const [channelSearch, setChannelSearch] = useState("");
  const [savingChannels, setSavingChannels] = useState(false);
  const [channelsSaved, setChannelsSaved] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(true);

  // Weekly digest config
  const [digestConfig, setDigestConfig] = useState({
    enabled: false, days: [], time: "09:00", timezone: "",
    channel_id: "", test_mode: false, test_user_id: "",
  });
  const [savingDigest, setSavingDigest] = useState(false);
  const [digestSaved, setDigestSaved] = useState(false);
  const [digestMembers, setDigestMembers] = useState([]);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [digestTestSearch, setDigestTestSearch] = useState("");
  const [digestTestOpen, setDigestTestOpen] = useState(false);

  useEffect(() => {
    if (!digestTestOpen) return;
    function handleClick(e) {
      if (!e.target.closest(".digest-test-dropdown")) setDigestTestOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [digestTestOpen]);

  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Show the time input in the logged-in user's timezone
  const userTz = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => { loadEvents(); loadShareChannels(); loadDigestConfig(); }, []);

  async function loadShareChannels() {
    setLoadingChannels(true);
    try {
      // Load channels from Discord
      const chRes = await fetch("/api/discord-channels");
      if (chRes.ok) { const d = await chRes.json(); setAllChannels(d.channels || []); }
      // Load saved selection from app_settings
      const { data } = await supabase.from("app_settings").select("key, value").eq("key", "share_channels").single();
      if (data?.value) { try { setShareChannelIds(JSON.parse(data.value)); } catch { /* ignore parse error */ } }
    } catch (err) { console.warn("Failed to load share channels:", err.message); }
    setLoadingChannels(false);
  }

  async function saveShareChannels() {
    setSavingChannels(true);
    try {
      await supabase.from("app_settings").upsert({ key: "share_channels", value: JSON.stringify(shareChannelIds) }, { onConflict: "key" });
      setChannelsSaved(true); setTimeout(() => setChannelsSaved(false), 2000);
    } catch (err) { console.error("Failed to save share channels:", err); }
    setSavingChannels(false);
  }

  function toggleShareChannel(id) {
    setShareChannelIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  }

  async function loadDigestConfig() {
    try {
      const { data } = await supabase.from("app_settings").select("key, value").eq("key", "event_digest_config").single();
      if (data?.value) {
        try { setDigestConfig(prev => ({ ...prev, ...JSON.parse(data.value) })); } catch { /* ignore */ }
      }
      // Fetch members for test mode dropdown
      const mRes = await fetch("/api/discord-members");
      if (mRes.ok) { const d = await mRes.json(); setDigestMembers(d.members || []); }
    } catch (err) { console.warn("Failed to load digest config:", err.message); }
  }

  async function saveDigestConfig() {
    setSavingDigest(true);
    try {
      const cfg = { ...digestConfig, timezone: userTz };
      await supabase.from("app_settings").upsert({ key: "event_digest_config", value: JSON.stringify(cfg) }, { onConflict: "key" });
      setDigestSaved(true); setTimeout(() => setDigestSaved(false), 2000);
    } catch (err) { console.error("Failed to save digest config:", err); }
    setSavingDigest(false);
  }

  async function sendDigestPreview() {
    setSendingPreview(true);
    try {
      // Fetch upcoming org events in the next 7 days (org-wide or shareable only)
      const now = new Date();
      const weekOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const { data: upcoming } = await supabase.from("events")
        .select("*")
        .eq("archived", false)
        .gte("date", now.toISOString().slice(0, 10))
        .lte("date", weekOut.toISOString().slice(0, 10))
        .or("is_org_wide.eq.true,allow_sharing.eq.true")
        .order("date");
      const r = await fetch("/api/discord-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "send_digest", config: { ...digestConfig, timezone: userTz }, events: upcoming || [] }),
      });
      const data = await r.json();
      if (r.ok) { alert(`Digest sent! ${data.event_count || 0} events listed.`); }
      else { alert("Failed: " + (data.error || "Unknown error")); }
    } catch (err) { alert("Error: " + err.message); }
    setSendingPreview(false);
  }

  function toggleDigestDay(day) {
    setDigestConfig(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day].sort(),
    }));
  }

  async function loadEvents() { setLoading(true); const { data } = await supabase.from("events").select("*").order("date"); setEvents(data || []); setLoading(false); }
  async function saveEvent() { setSaving(true); await supabase.from("events").insert([{ ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [], created_by: profile.id, creator_timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone }]); setSaving(false); setShowForm(false); setForm({ title: "", type: "Combat", description: "", date: "", time: "", tags: "", allow_sharing: false, reminder: "discord", is_org_wide: true }); loadEvents(); }

  // Archive an event (soft delete)
  async function archiveEvent(id) {
    if (!window.confirm("Are you sure you want to archive this event? It will be removed from the events page but can be restored later.")) return;
    await supabase.from("events").update({ archived: true }).eq("id", id);
    loadEvents();
  }

  // Restore an archived event back to active
  async function restoreEvent(id) {
    if (!window.confirm("Are you sure you want to restore this event? It will appear on the events page again.")) return;
    await supabase.from("events").update({ archived: false }).eq("id", id);
    loadEvents();
  }

  // Permanently delete an event (irreversible)
  async function permanentlyDeleteEvent(id) {
    if (!window.confirm("⚠️ PERMANENT DELETE: Are you absolutely sure? This event and all its data will be gone forever and CANNOT be recovered.")) return;
    if (!window.confirm("Final confirmation: Permanently delete this event?")) return;
    await supabase.from("events").delete().eq("id", id);
    loadEvents();
  }

  const typeColors = { Combat: "#ef5350", Industry: "#fcd657", Salvage: "#a78bfa", Training: "#4fc3f7", Piracy: "#f87171", Exploration: "#34d399", Other: "#ce93d8" };

  const activeEvents = events.filter(e => !e.archived);
  const archivedEvents = events.filter(e => e.archived);
  const displayEvents = viewMode === "active" ? activeEvents : archivedEvents;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div><SectionLabel>Admin</SectionLabel><h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6" }}>Event Manager</h2></div>
        <ActionBtn onClick={() => setShowForm(true)} color={ACCENT}>+ Create Event</ActionBtn>
      </div>

      {/* Share Channels Config */}
      <div style={{ marginBottom: 32, padding: 20, border: "1px solid rgba(52,211,153,0.2)", background: "rgba(52,211,153,0.04)" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#34d399", textTransform: "uppercase", marginBottom: 8 }}>Share Channels</div>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", marginBottom: 16 }}>
          Select which Discord channels members can share events to. Members will see these options when clicking "Share" on an event card.
        </div>
        {loadingChannels ? (
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, padding: 16 }}>LOADING CHANNELS...</div>
        ) : (
          <>
            <input
              value={channelSearch} onChange={e => setChannelSearch(e.target.value)}
              placeholder="Search channels..."
              style={{ width: "100%", maxWidth: 340, padding: "8px 12px", marginBottom: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(52,211,153,0.2)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none" }}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 200, overflowY: "auto", marginBottom: 14 }}>
              {allChannels.filter(ch => !channelSearch || ch.name.toLowerCase().includes(channelSearch.toLowerCase()) || (ch.category || "").toLowerCase().includes(channelSearch.toLowerCase())).map(ch => {
                const sel = shareChannelIds.includes(ch.id);
                return (
                  <button key={ch.id} onClick={() => toggleShareChannel(ch.id)} style={{
                    padding: "5px 12px", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
                    background: sel ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.03)",
                    border: sel ? "1px solid rgba(52,211,153,0.5)" : "1px solid rgba(255,255,255,0.1)",
                    color: sel ? "#34d399" : "rgba(232,234,246,0.5)", cursor: "pointer", transition: "all 0.2s",
                  }}>
                    {sel ? "✓ " : ""}#{ch.name}{ch.category ? ` (${ch.category})` : ""}
                  </button>
                );
              })}
              {allChannels.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO CHANNELS FOUND</div>}
            </div>
            {shareChannelIds.length > 0 && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(52,211,153,0.6)", marginBottom: 10 }}>
                {shareChannelIds.length} channel{shareChannelIds.length !== 1 ? "s" : ""} selected
              </div>
            )}
            <button onClick={saveShareChannels} disabled={savingChannels} style={{
              padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
              background: channelsSaved ? "rgba(52,211,153,0.15)" : "rgba(52,211,153,0.1)",
              border: `1px solid ${channelsSaved ? "#34d399" : "rgba(52,211,153,0.4)"}`,
              color: channelsSaved ? "#34d399" : "#34d399", cursor: "pointer", transition: "all 0.2s",
            }}>{savingChannels ? "Saving..." : channelsSaved ? "✓ Saved" : "Save Channels"}</button>
          </>
        )}
      </div>

      {/* Weekly Digest Config */}
      <div style={{ marginBottom: 32, padding: 20, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.04)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#a78bfa", textTransform: "uppercase" }}>Upcoming Events Alert</div>
          <div onClick={() => setDigestConfig(p => ({ ...p, enabled: !p.enabled }))} style={{ width: 40, height: 22, borderRadius: 11, background: digestConfig.enabled ? "#a78bfa" : "rgba(255,255,255,0.1)", position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0 }}>
            <div style={{ position: "absolute", top: 3, left: digestConfig.enabled ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
          </div>
        </div>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", marginBottom: 16 }}>
          The bot posts a summary of all org events happening in the next 7 days. Signed-up members are listed under each event. Users can RSVP or get reminders directly from the digest.
        </div>

        {/* Day Picker */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 8 }}>Post Days</div>
          <div style={{ display: "flex", gap: 6 }}>
            {DAY_LABELS.map((label, i) => {
              const sel = digestConfig.days.includes(i);
              return (
                <button key={i} onClick={() => toggleDigestDay(i)} style={{
                  padding: "6px 12px", fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: sel ? 700 : 400,
                  background: sel ? "rgba(167,139,250,0.15)" : "rgba(255,255,255,0.03)",
                  border: sel ? "1px solid rgba(167,139,250,0.5)" : "1px solid rgba(255,255,255,0.1)",
                  color: sel ? "#a78bfa" : "rgba(232,234,246,0.4)", cursor: "pointer", transition: "all 0.2s",
                }}>{label}</button>
              );
            })}
          </div>
        </div>

        {/* Time Picker + Timezone */}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 6 }}>Post Time</div>
            <input type="time" value={digestConfig.time} onChange={e => setDigestConfig(p => ({ ...p, time: e.target.value }))} style={{ padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.3)", color: "#a78bfa", fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, outline: "none", colorScheme: "dark" }} />
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, paddingBottom: 10 }}>
            {userTz.replace(/_/g, " ")}
          </div>
        </div>

        {/* Channel Selection */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 6 }}>Post Channel</div>
          {loadingChannels ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>LOADING...</div>
          ) : (
            <select value={digestConfig.channel_id} onChange={e => setDigestConfig(p => ({ ...p, channel_id: e.target.value }))} style={{ padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.3)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none", minWidth: 220 }}>
              <option value="">Select channel...</option>
              {allChannels.map(ch => <option key={ch.id} value={ch.id}>#{ch.name}{ch.category ? ` (${ch.category})` : ""}</option>)}
            </select>
          )}
        </div>

        {/* Test Mode */}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <div onClick={() => setDigestConfig(p => ({ ...p, test_mode: !p.test_mode }))} style={{ width: 40, height: 22, borderRadius: 11, background: digestConfig.test_mode ? "#fcd657" : "rgba(255,255,255,0.1)", position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 3, left: digestConfig.test_mode ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
            </div>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: digestConfig.test_mode ? "#fcd657" : "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>Test Mode {digestConfig.test_mode ? "ON" : "OFF"}</span>
          </label>
          {digestConfig.test_mode && (
            <div className="digest-test-dropdown" style={{ position: "relative", minWidth: 240 }}>
              <input
                value={digestTestSearch}
                onChange={e => { setDigestTestSearch(e.target.value); setDigestTestOpen(true); }}
                onFocus={() => setDigestTestOpen(true)}
                placeholder={digestConfig.test_user_id ? (digestMembers.find(m => m.discord_id === digestConfig.test_user_id)?.discord_username || digestConfig.test_user_id) : "Search members..."}
                style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.3)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }}
              />
              {digestConfig.test_user_id && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                  {(() => { const m = digestMembers.find(x => x.discord_id === digestConfig.test_user_id); return m ? (
                    <div style={{ padding: "3px 10px", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600, background: "rgba(252,214,87,0.1)", border: "1px solid rgba(252,214,87,0.3)", color: "#fcd657", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {m.discord_avatar && <img src={m.discord_avatar} alt="" style={{ width: 16, height: 16, borderRadius: "50%" }} />}
                      {m.nickname || m.discord_username}
                      <button onClick={() => { setDigestConfig(p => ({ ...p, test_user_id: "" })); setDigestTestSearch(""); }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 14, cursor: "pointer", padding: 0, marginLeft: 4 }}>×</button>
                    </div>
                  ) : null; })()}
                </div>
              )}
              {digestTestOpen && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, maxHeight: 200, overflowY: "auto", background: "#0c0818", border: "1px solid rgba(252,214,87,0.25)", marginTop: 2 }}>
                  {digestMembers.filter(m => !digestTestSearch || (m.discord_username || "").toLowerCase().includes(digestTestSearch.toLowerCase()) || (m.nickname || "").toLowerCase().includes(digestTestSearch.toLowerCase()) || (m.discord_handle || "").toLowerCase().includes(digestTestSearch.toLowerCase())).slice(0, 50).map(m => (
                    <button key={m.discord_id} onClick={() => { setDigestConfig(p => ({ ...p, test_user_id: m.discord_id })); setDigestTestSearch(""); setDigestTestOpen(false); }} style={{
                      width: "100%", padding: "7px 12px", display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                      background: digestConfig.test_user_id === m.discord_id ? "rgba(252,214,87,0.1)" : "transparent",
                      border: "none", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(252,214,87,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = digestConfig.test_user_id === m.discord_id ? "rgba(252,214,87,0.1)" : "transparent"}>
                      {m.discord_avatar ? <img src={m.discord_avatar} alt="" style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0 }} /> : <div style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(252,214,87,0.2)", flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 600, color: "#e8eaf6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.nickname || m.discord_username}</div>
                        {m.nickname && m.discord_username !== m.nickname && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)" }}>{m.discord_handle}</div>}
                      </div>
                    </button>
                  ))}
                  {digestMembers.filter(m => !digestTestSearch || (m.discord_username || "").toLowerCase().includes(digestTestSearch.toLowerCase()) || (m.nickname || "").toLowerCase().includes(digestTestSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: 12, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center" }}>NO MATCHING MEMBERS</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button onClick={saveDigestConfig} disabled={savingDigest} style={{
            padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
            background: digestSaved ? "rgba(52,211,153,0.15)" : "rgba(167,139,250,0.1)",
            border: `1px solid ${digestSaved ? "#34d399" : "rgba(167,139,250,0.4)"}`,
            color: digestSaved ? "#34d399" : "#a78bfa", cursor: "pointer", transition: "all 0.2s",
          }}>{savingDigest ? "Saving..." : digestSaved ? "✓ Saved" : "Save Settings"}</button>
          <button onClick={sendDigestPreview} disabled={sendingPreview} style={{
            padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
            background: "rgba(252,214,87,0.08)", border: "1px solid rgba(252,214,87,0.3)",
            color: "#fcd657", cursor: "pointer", transition: "all 0.2s", opacity: sendingPreview ? 0.6 : 1,
          }}>{sendingPreview ? "Sending..." : "Send Preview Now"}</button>
        </div>
      </div>

      {/* Active / Archived toggle tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <button onClick={() => setViewMode("active")} style={{
          fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: viewMode === "active" ? 700 : 400,
          letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
          padding: "12px 20px", border: "none",
          background: viewMode === "active" ? `${ACCENT}12` : "transparent",
          borderBottom: viewMode === "active" ? `2px solid ${ACCENT}` : "2px solid transparent",
          color: viewMode === "active" ? ACCENT : "rgba(232,234,246,0.35)", transition: "all 0.2s",
        }}>Active ({activeEvents.length})</button>
        <button onClick={() => setViewMode("archived")} style={{
          fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: viewMode === "archived" ? 700 : 400,
          letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
          padding: "12px 20px", border: "none",
          background: viewMode === "archived" ? "rgba(239,83,80,0.08)" : "transparent",
          borderBottom: viewMode === "archived" ? "2px solid #ef5350" : "2px solid transparent",
          color: viewMode === "archived" ? "#ef5350" : "rgba(232,234,246,0.35)", transition: "all 0.2s",
        }}>Archived ({archivedEvents.length})</button>
      </div>

      {loading ? <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING EVENTS...</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {displayEvents.map(ev => { const color = typeColors[ev.type] || "#ce93d8"; const evDate = ev.date ? new Date(`${ev.date}T${ev.time || "00:00"}`) : null; return (
            <div key={ev.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", border: `1px solid ${color}22`, background: `${color}06`, flexWrap: "wrap", opacity: viewMode === "archived" ? 0.7 : 1 }}>
              <div style={{ width: 4, height: 40, background: color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: "#e8eaf6", marginBottom: 4 }}>
                  {ev.title}
                  {viewMode === "archived" && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "#ef5350", marginLeft: 10, textTransform: "uppercase" }}>ARCHIVED</span>}
                </div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>{ev.type} &middot; {evDate ? evDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "No date"}{ev.is_org_wide ? " · Org-Wide" : ev.team ? ` · ${ev.team}` : ""}{ev.allow_sharing ? " · Shareable" : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {viewMode === "active" ? (
                  <ActionBtn small onClick={() => archiveEvent(ev.id)} color="#ef5350">Archive</ActionBtn>
                ) : (
                  <>
                    <ActionBtn small onClick={() => restoreEvent(ev.id)} color="#34d399">Restore</ActionBtn>
                    <ActionBtn small onClick={() => permanentlyDeleteEvent(ev.id)} color="#ef5350">Delete Forever</ActionBtn>
                  </>
                )}
              </div>
            </div>
          ); })}
          {displayEvents.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 40 }}>
            {viewMode === "active" ? "NO ACTIVE EVENTS" : "NO ARCHIVED EVENTS"}
          </div>}
        </div>
      )}
      {showForm && (
        <Modal title="Create New Event" onClose={() => setShowForm(false)}>
          <Field label="Event Title"><input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={inputStyle} placeholder="e.g. Large Scale Mining Op" /></Field>
          <Field label="Type"><select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={selectStyle}>{EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
          <Field label="Date"><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} style={{ ...inputStyle, colorScheme: "dark" }} /></Field>
          <Field label="Time"><input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} style={{ ...inputStyle, colorScheme: "dark" }} /></Field>
          <Field label="Description"><textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} placeholder="Mission briefing..." /></Field>
          <Field label="Tags (comma separated)"><input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} style={inputStyle} placeholder="e.g. All Hands, Priority" /></Field>
          <Field label="Reminder"><select value={form.reminder} onChange={e => setForm(p => ({ ...p, reminder: e.target.value }))} style={selectStyle}><option value="discord">Discord</option><option value="email">Email</option><option value="both">Discord + Email</option><option value="none">None</option></select></Field>
          <div style={{ display: "flex", gap: 24, marginBottom: 18, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <div onClick={() => setForm(p => ({ ...p, is_org_wide: !p.is_org_wide }))} style={{ width: 40, height: 22, borderRadius: 11, background: form.is_org_wide ? ACCENT : "rgba(255,255,255,0.1)", position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 3, left: form.is_org_wide ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
              </div>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.6)", textTransform: "uppercase" }}>Org-Wide Event</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <div onClick={() => setForm(p => ({ ...p, allow_sharing: !p.allow_sharing }))} style={{ width: 40, height: 22, borderRadius: 11, background: form.allow_sharing ? "#34d399" : "rgba(255,255,255,0.1)", position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 3, left: form.allow_sharing ? 20 : 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
              </div>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.6)", textTransform: "uppercase" }}>Allow External Sharing</span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn onClick={saveEvent} color={ACCENT}>{saving ? "Saving..." : "Create Event"}</ActionBtn>
            <ActionBtn onClick={() => setShowForm(false)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MERIT SYSTEM
// ─────────────────────────────────────────────────────────────────────────────
function MeritSystem() {
  const { profile } = useAuth();
  const { isSuperAdmin } = getPerms(profile);
  const [members, setMembers] = useState([]);
  const [merits, setMerits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ member_id: "", points: 10, reason: "" });
  const [memberQuery, setMemberQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);

  // Attendance reward settings
  const [attendanceSettings, setAttendanceSettings] = useState({ team_event_merits: 0, org_event_merits: 0 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => { loadData(); loadSettings(); }, []);
  async function loadData() {
    setLoading(true);
    const [{ data: m }, { data: mer }] = await Promise.all([
      supabase.from("profiles").select("id, discord_username, discord_avatar").order("discord_username"),
      supabase.from("merits").select("*, profiles!merits_member_id_fkey(discord_username, discord_avatar)").order("created_at", { ascending: false }),
    ]);
    setMembers(m || []); setMerits(mer || []); setLoading(false);
  }
  async function loadSettings() {
    try {
      const { data } = await supabase.from("app_settings").select("key, value").in("key", ["team_event_merits", "org_event_merits"]);
      if (data) {
        const s = {};
        data.forEach(r => { s[r.key] = parseInt(r.value) || 0; });
        setAttendanceSettings(prev => ({ ...prev, ...s }));
      }
    } catch (err) { console.warn("app_settings table not available:", err.message); }
  }
  async function saveSettings() {
    setSavingSettings(true);
    try {
      await Promise.all([
        supabase.from("app_settings").upsert({ key: "team_event_merits", value: String(attendanceSettings.team_event_merits) }, { onConflict: "key" }),
        supabase.from("app_settings").upsert({ key: "org_event_merits", value: String(attendanceSettings.org_event_merits) }, { onConflict: "key" }),
      ]);
      setSettingsSaved(true); setTimeout(() => setSettingsSaved(false), 2000);
    } catch (err) { console.error("Failed to save settings:", err); }
    setSavingSettings(false);
  }
  async function awardMerit() { setSaving(true); await supabase.from("merits").insert([{ ...form, awarded_by: profile.id, points: parseInt(form.points) }]); setSaving(false); setShowForm(false); setForm({ member_id: "", points: 10, reason: "" }); setMemberQuery(""); loadData(); }
  const totals = merits.reduce((acc, m) => { const name = m.profiles?.discord_username || "Unknown"; acc[name] = (acc[name] || 0) + m.points; return acc; }, {});
  const leaderboard = Object.entries(totals).sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div><SectionLabel>Admin</SectionLabel><h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6" }}>Merit System</h2></div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {isSuperAdmin && (
            <ActionBtn onClick={() => setShowAdjust(true)} color="#a78bfa">⚖ Adjust Balances</ActionBtn>
          )}
          <ActionBtn onClick={() => setShowForm(true)} color="#fcd657">+ Award Merit</ActionBtn>
        </div>
      </div>

      {/* Attendance Reward Settings */}
      <div style={{ marginBottom: 32, padding: 20, border: "1px solid rgba(252,214,87,0.2)", background: "rgba(252,214,87,0.04)" }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#fcd657", textTransform: "uppercase", marginBottom: 16 }}>Attendance Rewards</div>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", marginBottom: 16 }}>
          Merits are automatically awarded to members marked as attended after an event review is saved. Set to 0 to disable.
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 6 }}>Team Events</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="number" min="0" max="1000" value={attendanceSettings.team_event_merits} onChange={e => setAttendanceSettings(s => ({ ...s, team_event_merits: parseInt(e.target.value) || 0 }))} style={{ width: 80, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.3)", color: "#fcd657", fontFamily: "'Orbitron', sans-serif", fontSize: 16, fontWeight: 700, textAlign: "center", outline: "none" }} />
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>merits / attendee</span>
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 6 }}>Org Events</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="number" min="0" max="1000" value={attendanceSettings.org_event_merits} onChange={e => setAttendanceSettings(s => ({ ...s, org_event_merits: parseInt(e.target.value) || 0 }))} style={{ width: 80, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(206,147,216,0.3)", color: "#ce93d8", fontFamily: "'Orbitron', sans-serif", fontSize: 16, fontWeight: 700, textAlign: "center", outline: "none" }} />
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>merits / attendee</span>
            </div>
          </div>
          <button onClick={saveSettings} disabled={savingSettings} style={{ padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", background: settingsSaved ? "rgba(52,211,153,0.15)" : "rgba(252,214,87,0.15)", border: `1px solid ${settingsSaved ? "#34d399" : "#fcd657"}`, color: settingsSaved ? "#34d399" : "#fcd657", cursor: "pointer", transition: "all 0.2s" }}>{savingSettings ? "Saving..." : settingsSaved ? "✓ Saved" : "Save Settings"}</button>
        </div>
      </div>

      {loading ? <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING...</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
          <div>
            <SectionLabel color="#fcd657">Leaderboard</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
              {leaderboard.map(([name, pts], i) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", border: `1px solid ${i === 0 ? "#fcd65744" : "rgba(255,255,255,0.07)"}`, background: i === 0 ? "rgba(252,214,87,0.06)" : "rgba(255,255,255,0.02)" }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 18, color: i === 0 ? "#fcd657" : i === 1 ? "#9ca3af" : i === 2 ? "#b45309" : "rgba(255,255,255,0.3)", width: 28, textAlign: "center" }}>#{i + 1}</div>
                  <div style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6" }}>{name}</div>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: "#fcd657" }}>{pts} pts</div>
                </div>
              ))}
              {leaderboard.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 32 }}>NO MERITS AWARDED YET</div>}
            </div>
          </div>
          <div>
            <SectionLabel color="#ce93d8">Recent Awards</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
              {merits.slice(0, 10).map(m => (
                <div key={m.id} style={{ padding: "10px 14px", border: "1px solid rgba(206,147,216,0.15)", background: "rgba(206,147,216,0.04)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{m.profiles?.discord_username}</span>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, color: "#fcd657" }}>+{m.points}</span>
                  </div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.45)" }}>{m.reason}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showForm && (
        <Modal title="Award Merit Points" onClose={() => setShowForm(false)}>
          <Field label="Member">{(() => {
            const selected = members.find(m => m.id === form.member_id);
            const filtered = (members || [])
              .filter(m => !memberQuery || (m.discord_username || "").toLowerCase().includes(memberQuery.toLowerCase()))
              .slice(0, 60);
            return (
              <div>
                {selected ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(252,214,87,0.08)", border: "1px solid rgba(252,214,87,0.4)" }}>
                    {selected.discord_avatar && <img src={selected.discord_avatar} alt={selected.discord_username} style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }} loading="lazy" />}
                    <span style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{selected.discord_username}</span>
                    <button type="button" onClick={() => { setForm(p => ({ ...p, member_id: "" })); setMemberQuery(""); }}
                      style={{ background: "transparent", border: "1px solid rgba(239,83,80,0.4)", color: "#ef5350", fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, padding: "3px 8px", cursor: "pointer", textTransform: "uppercase" }}>Change</button>
                  </div>
                ) : (
                  <div>
                    <input type="text" autoFocus value={memberQuery} onChange={e => setMemberQuery(e.target.value)} placeholder="Search by username…"
                      style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.3)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, boxSizing: "border-box", outline: "none", marginBottom: 6 }} />
                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.015)" }}>
                      {filtered.length === 0 ? (
                        <div style={{ padding: 16, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>No matches</div>
                      ) : filtered.map(m => (
                        <div key={m.id} onClick={() => { setForm(p => ({ ...p, member_id: m.id })); setMemberQuery(""); }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(252,214,87,0.1)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          {m.discord_avatar
                            ? <img src={m.discord_avatar} alt={m.discord_username} style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} loading="lazy" />
                            : <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#0d1120", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 10, color: "rgba(232,234,246,0.5)", flexShrink: 0 }}>{(m.discord_username || "?").charAt(0).toUpperCase()}</div>}
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>{m.discord_username}</span>
                        </div>
                      ))}
                    </div>
                    {members && members.length > 60 && !memberQuery && (
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.35)", marginTop: 4 }}>Showing first 60 — start typing to filter</div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}</Field>
          <Field label="Points"><input type="number" value={form.points} onChange={e => setForm(p => ({ ...p, points: e.target.value }))} style={inputStyle} min="1" max="1000" /></Field>
          <Field label="Reason"><input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} style={inputStyle} placeholder="e.g. Outstanding performance in mining op" /></Field>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <ActionBtn onClick={awardMerit} color="#fcd657">{saving ? "Awarding..." : "Award Points"}</ActionBtn>
            <ActionBtn onClick={() => setShowForm(false)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}
      {showAdjust && (
        <AdjustBalancesModal
          members={members}
          merits={merits}
          onClose={() => setShowAdjust(false)}
          onCommit={async (memberId, delta, reason) => {
            await supabase.from("merits").insert([{ member_id: memberId, points: delta, reason: reason || "Manual adjustment", awarded_by: profile.id }]);
            loadData();
          }}
        />
      )}
    </div>
  );
}

// ─── Adjust Balances Modal ───────────────────────────────────────────────
// Super-admin only. Lets the user search for any member, see their current
// merit totals (computed from the live merits list), and apply a +/- delta
// with a free-text reason. Writes a single new row to the merits table — we
// keep the audit trail and let merit_balances view recompute.
function AdjustBalancesModal({ members, merits, onClose, onCommit }) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Pre-compute each member's current total from the merits array so we don't
  // have to fetch merit_balances separately. Stable across modal lifetime.
  const totals = useMemo(() => {
    const out = {};
    (merits || []).forEach(m => { out[m.member_id] = (out[m.member_id] || 0) + (m.points || 0); });
    return out;
  }, [merits]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (members || [])
      .map(m => ({ ...m, total: totals[m.id] || 0 }))
      .filter(m => !q || (m.discord_username || "").toLowerCase().includes(q));
    // Sort: matched members with non-zero balances first, then by username.
    return list.sort((a, b) => {
      if ((b.total !== 0) - (a.total !== 0)) return (b.total !== 0) - (a.total !== 0);
      return (a.discord_username || "").localeCompare(b.discord_username || "");
    });
  }, [members, totals, query]);

  const selected = (members || []).find(m => m.id === selectedId);
  const selectedTotal = selected ? (totals[selected.id] || 0) : 0;
  const newTotal = selectedTotal + (Number(delta) || 0);
  // Merit balances should never go below zero. Block submit and surface a
  // warning if the deduction would push the user negative.
  const wouldGoNegative = newTotal < 0;
  const maxDeduction = -selectedTotal; // largest negative delta allowed

  async function commit() {
    if (!selectedId || !delta) return;
    if (wouldGoNegative) return;
    setBusy(true);
    await onCommit(selectedId, Number(delta), reason.trim());
    setBusy(false);
    setSelectedId("");
    setDelta(0);
    setReason("");
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 12200, display: "flex", justifyContent: "center", alignItems: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(640px, 100vw)", maxHeight: "92vh",
        background: "#0a0d18", border: "2px solid rgba(167,139,250,0.55)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
        padding: "clamp(16px, 3vw, 24px)",
        display: "flex", flexDirection: "column", gap: 14,
        boxSizing: "border-box",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#a78bfa", textTransform: "uppercase", marginBottom: 4 }}>Super Admin</div>
            <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, color: "#e8eaf6", margin: 0 }}>Adjust Merit Balances</h3>
          </div>
          <button onClick={onClose} type="button"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.6)", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, padding: "6px 12px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
        </div>

        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search members by username…"
          style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.35)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, boxSizing: "border-box", outline: "none" }} />

        {/* Scrollable member list */}
        <div style={{ flex: "1 1 auto", minHeight: 200, maxHeight: 320, overflowY: "auto", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.015)" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>
              No members match
            </div>
          ) : filtered.map(m => {
            const isSelected = m.id === selectedId;
            return (
              <div key={m.id} onClick={() => setSelectedId(m.id)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", cursor: "pointer", background: isSelected ? "rgba(167,139,250,0.18)" : "transparent", borderLeft: `3px solid ${isSelected ? "#a78bfa" : "transparent"}`, transition: "background 0.15s" }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                {m.discord_avatar ? (
                  <img src={m.discord_avatar} alt={m.discord_username} style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} loading="lazy" />
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#0d1120", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.5)", flexShrink: 0 }}>{(m.discord_username || "?").charAt(0).toUpperCase()}</div>
                )}
                <span style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.discord_username || "—"}</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: m.total > 0 ? "#fcd657" : m.total < 0 ? "#ef5350" : "rgba(232,234,246,0.4)", letterSpacing: 0.5 }}>{m.total} pts</span>
              </div>
            );
          })}
        </div>

        {/* Adjustment controls */}
        <div style={{ padding: 14, border: "1px solid rgba(167,139,250,0.35)", background: "rgba(167,139,250,0.06)" }}>
          {selected ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>Adjusting</span>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, color: "#a78bfa" }}>{selected.discord_username}</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.5)" }}>Current: <strong style={{ color: "#fcd657" }}>{selectedTotal} pts</strong></span>
                {Number(delta) !== 0 && (
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.5)" }}>→ New: <strong style={{ color: wouldGoNegative ? "#ef5350" : "#34d399" }}>{newTotal} pts</strong></span>
                )}
                {wouldGoNegative && (
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.2, color: "#ef5350", textTransform: "uppercase", padding: "2px 6px", border: "1px solid rgba(239,83,80,0.45)" }}>
                    Cannot go below 0 — max deduction is {maxDeduction} pts
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button onClick={() => setDelta(d => (Number(d) || 0) - 10)} type="button"
                  style={{ padding: "6px 12px", background: "rgba(239,83,80,0.12)", border: "1px solid rgba(239,83,80,0.5)", color: "#ef5350", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.2, cursor: "pointer" }}>−10</button>
                <input type="number" value={delta} onChange={e => setDelta(e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
                  style={{ width: 100, padding: "7px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(167,139,250,0.4)", color: "#e8eaf6", fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, textAlign: "center" }} />
                <button onClick={() => setDelta(d => (Number(d) || 0) + 10)} type="button"
                  style={{ padding: "6px 12px", background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.5)", color: "#34d399", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.2, cursor: "pointer" }}>+10</button>
                <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional, becomes audit-trail entry)"
                  style={{ flex: "1 1 220px", minWidth: 200, padding: "7px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }} />
              </div>
              <button onClick={commit} type="button" disabled={busy || !Number(delta) || wouldGoNegative}
                style={{ alignSelf: "flex-start", padding: "8px 18px", background: (busy || wouldGoNegative) ? "rgba(167,139,250,0.1)" : "rgba(167,139,250,0.2)", border: `1px solid ${wouldGoNegative ? "#ef5350" : "#a78bfa"}`, color: wouldGoNegative ? "#ef5350" : "#a78bfa", fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: (busy || !Number(delta) || wouldGoNegative) ? "not-allowed" : "pointer", opacity: (busy || !Number(delta) || wouldGoNegative) ? 0.5 : 1 }}>
                {busy ? "Applying…" : wouldGoNegative ? "Below 0 not allowed" : `Apply ${Number(delta) > 0 ? "+" : ""}${Number(delta) || 0} pts`}
              </button>
            </div>
          ) : (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.2, color: "rgba(232,234,246,0.45)", textAlign: "center", textTransform: "uppercase", padding: 8 }}>
              Select a member from the list above to adjust their balance.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MAIN ADMIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// ORG ROLE MANAGER
// ─────────────────────────────────────────────────────────────────────────────
function OrgRoleManager() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", sort_value: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadRoles(); }, []);

  async function loadRoles() {
    setLoading(true);
    const { data } = await supabase.from("org_roles").select("*").order("sort_value");
    setRoles(data || []);
    setLoading(false);
  }

  async function saveRole() {
    if (!form.name.trim() || !form.sort_value) return;
    setSaving(true);
    const payload = { name: form.name.trim(), sort_value: parseFloat(form.sort_value), description: form.description.trim() || null };
    if (editing) {
      await supabase.from("org_roles").update(payload).eq("id", editing.id);
      if (editing.name !== payload.name) {
        await supabase.from("profiles").update({ org_role: payload.name }).eq("org_role", editing.name);
      }
      await supabase.from("profiles").update({ org_role_sort: payload.sort_value }).eq("org_role", payload.name);
    } else {
      await supabase.from("org_roles").insert([payload]);
    }
    setSaving(false);
    setEditing(null);
    setCreating(false);
    setForm({ name: "", sort_value: "", description: "" });
    loadRoles();
  }

  async function deleteRole(role) {
    await supabase.from("org_roles").delete().eq("id", role.id);
    await supabase.from("profiles").update({ org_role_sort: null }).eq("org_role", role.name);
    setDeleteConfirm(null);
    loadRoles();
  }

  function openEdit(role) {
    setEditing(role);
    setCreating(false);
    setForm({ name: role.name, sort_value: role.sort_value.toString(), description: role.description || "" });
  }

  const command  = roles.filter(r => r.sort_value >= 1 && r.sort_value < 2);
  const divLeads = roles.filter(r => r.sort_value >= 2 && r.sort_value < 3);
  const teamLeads= roles.filter(r => r.sort_value >= 3 && r.sort_value < 4);
  const other    = roles.filter(r => r.sort_value < 1 || r.sort_value >= 4);

  function RoleRow({ role, tierColor }) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", border: `1px solid ${tierColor}22`, background: `${tierColor}05`, marginBottom: 6 }}>
        <div style={{ width: 52, textAlign: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, color: tierColor }}>{role.sort_value}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{role.name}</div>
          {role.description && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", marginTop: 2 }}>{role.description}</div>}
        </div>
        <ActionBtn small onClick={() => openEdit(role)} color={ACCENT}>Edit</ActionBtn>
        <ActionBtn small onClick={() => setDeleteConfirm(role)} color="#ef5350">Delete</ActionBtn>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div>
          <SectionLabel>Admin</SectionLabel>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6" }}>Org Role Manager</h2>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 2, marginTop: 6 }}>SORT 1.x = COMMAND · 2.x = DIVISION LEADS · 3.x = TEAM LEADS</p>
        </div>
        <ActionBtn onClick={() => { setEditing(null); setCreating(true); setForm({ name: "", sort_value: "", description: "" }); }} color={ACCENT}>+ New Role</ActionBtn>
      </div>

      {loading ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {[
            { key: "command",   label: "Command",        roles: command,   color: "#fcd657" },
            { key: "divLeads",  label: "Division Leads", roles: divLeads,  color: "#4fc3f7" },
            { key: "teamLeads", label: "Team Leads",     roles: teamLeads, color: "#81c784" },
            { key: "other",     label: "Other",          roles: other,     color: "rgba(232,234,246,0.4)" },
          ].filter(t => t.roles.length > 0).map(tier => (
            <div key={tier.key}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: tier.color, textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 1, background: tier.color }} /> {tier.label} ({tier.roles.length})
              </div>
              {tier.roles.map(r => <RoleRow key={r.id} role={r} tierColor={tier.color} />)}
            </div>
          ))}
          {roles.length === 0 && (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>NO ROLES YET — CREATE ONE ABOVE</div>
          )}
        </div>
      )}

      {(creating || editing) && (
        <Modal title={editing ? `Edit — ${editing.name}` : "Create New Role"} onClose={() => { setCreating(false); setEditing(null); }}>
          <Field label="Role Name"><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder="e.g. Director of Industry" /></Field>
          <Field label="Sort Value">
            <input type="number" step="0.1" min="0" value={form.sort_value} onChange={e => setForm(p => ({ ...p, sort_value: e.target.value }))} style={inputStyle} placeholder="e.g. 2.1" />
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", marginTop: 6, letterSpacing: 1 }}>1.x = Command · 2.x = Division Leads · 3.x = Team Leads</div>
          </Field>
          <Field label="Description (optional)"><input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={inputStyle} placeholder="Short description..." /></Field>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <ActionBtn onClick={saveRole} color={ACCENT}>{saving ? "Saving..." : editing ? "Save Changes" : "Create Role"}</ActionBtn>
            <ActionBtn onClick={() => { setCreating(false); setEditing(null); }} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <Modal title="Delete Role" onClose={() => setDeleteConfirm(null)}>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.7)", marginBottom: 8 }}>Delete <span style={{ color: "#ef5350", fontWeight: 700 }}>{deleteConfirm.name}</span>?</p>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, marginBottom: 24 }}>Members with this role will have their sort value cleared.</p>
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn onClick={() => deleteRole(deleteConfirm)} color="#ef5350">Confirm Delete</ActionBtn>
            <ActionBtn onClick={() => setDeleteConfirm(null)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FLEET MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
function FleetManager() {
  const { ships: preloadedShips, profiles: preloadedProfiles, loaded: preloaded, refreshFleet } = useFleetData();
  const [ships, setShips] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteConfirmShip, setDeleteConfirmShip] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [activeManufacturerTab, setActiveManufacturerTab] = useState("all");

  // Manufacturer management
  const [mfrExpanded, setMfrExpanded] = useState(false);
  const [newMfrName, setNewMfrName] = useState("");
  const [editingMfr, setEditingMfr] = useState(null);
  const [editMfrName, setEditMfrName] = useState("");
  const [deleteMfrConfirm, setDeleteMfrConfirm] = useState(null);

  const [form, setForm] = useState({ name: "", manufacturer: "", pledge_url: "", image_url: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  // Base64 → Storage migration
  const [migrating, setMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(null); // { done, total, current, errors }

  const base64Ships = ships.filter(s => s.image_url && s.image_url.startsWith("data:"));

  async function migrateBase64ToStorage() {
    if (base64Ships.length === 0) return;
    if (!window.confirm(`Migrate ${base64Ships.length} base64 images to Supabase Storage? This will free up ~${Math.round(base64Ships.reduce((sum, s) => sum + (s.image_url?.length || 0), 0) / 1024 / 1024)} MB from your database.`)) return;

    setMigrating(true);
    const progress = { done: 0, total: base64Ships.length, current: "", errors: [] };
    setMigrationProgress({ ...progress });

    for (const ship of base64Ships) {
      progress.current = ship.name;
      setMigrationProgress({ ...progress });

      try {
        // Parse the data URL
        const match = ship.image_url.match(/^data:(image\/\w+);base64,(.+)$/);
        if (!match) { progress.errors.push(`${ship.name}: invalid data URL format`); progress.done++; continue; }

        const mimeType = match[1];
        const base64Data = match[2];
        const ext = mimeType === "image/jpeg" ? "jpg" : mimeType === "image/png" ? "png" : "webp";

        // Convert base64 to Blob
        const byteChars = atob(base64Data);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArray], { type: mimeType });

        // Upload to Storage
        const fileName = `ship_${ship.id}_${Date.now()}.${ext}`;
        const optBlob = await optimizeImageFile(new File([blob], fileName, { type: mimeType }), { maxDim: 1000, quality: 0.85 });
        const { error: uploadErr } = await supabase.storage.from("ship-images").upload(fileName, optBlob, { contentType: optBlob.type || mimeType, upsert: false });
        if (uploadErr) { progress.errors.push(`${ship.name}: upload failed — ${uploadErr.message}`); progress.done++; continue; }

        // Get public URL
        const { data: urlData } = supabase.storage.from("ship-images").getPublicUrl(fileName);
        const publicUrl = urlData?.publicUrl;
        if (!publicUrl) { progress.errors.push(`${ship.name}: could not get public URL`); progress.done++; continue; }

        // Update database row
        const { error: updateErr } = await supabase.from("ships").update({ image_url: publicUrl }).eq("id", ship.id);
        if (updateErr) { progress.errors.push(`${ship.name}: DB update failed — ${updateErr.message}`); progress.done++; continue; }

        progress.done++;
        setMigrationProgress({ ...progress });
      } catch (err) {
        progress.errors.push(`${ship.name}: ${err.message}`);
        progress.done++;
      }
    }

    progress.current = "";
    setMigrationProgress({ ...progress });
    setMigrating(false);
    // Refresh data
    refreshFleet();
    loadManufacturers();
  }

  // Use preloaded ships/profiles from context, fetch manufacturers separately
  useEffect(() => {
    if (preloaded && preloadedShips.length >= 0) {
      setShips(preloadedShips);
      setProfiles(preloadedProfiles);
      // Still need to fetch manufacturers (admin-only table not in context)
      loadManufacturers();
    } else {
      loadAll();
    }
  }, [preloaded, preloadedShips, preloadedProfiles]);

  async function loadManufacturers() {
    const { data: mfrData } = await supabase.from("manufacturers").select("*").order("name");
    setManufacturers(mfrData || []);
    setLoading(false);
  }

  async function loadAll() {
    setLoading(true);
    const [{ data: shipsData }, { data: profilesData }, { data: mfrData }] = await Promise.all([
      supabase.from("ships").select(SHIPS_LIST_COLS).order("name"),
      supabase.from("profiles").select("id, ships"),
      supabase.from("manufacturers").select("*").order("name"),
    ]);
    setShips(shipsData || []);
    setProfiles(profilesData || []);
    setManufacturers(mfrData || []);
    setLoading(false);
  }

  // ── Manufacturer CRUD ──────────────────────────────────────────────────────
  async function addManufacturer() {
    if (!newMfrName.trim()) return;
    await supabase.from("manufacturers").insert([{ name: newMfrName.trim() }]);
    setNewMfrName("");
    const { data } = await supabase.from("manufacturers").select("*").order("name");
    setManufacturers(data || []);
  }

  async function saveManufacturerEdit() {
    if (!editMfrName.trim() || !editingMfr) return;
    const oldName = editingMfr.name;
    const newName = editMfrName.trim();
    await supabase.from("manufacturers").update({ name: newName }).eq("id", editingMfr.id);
    // Update all ships that use the old name
    if (oldName !== newName) {
      const shipsToUpdate = ships.filter(s => s.manufacturer === oldName);
      for (const ship of shipsToUpdate) {
        await supabase.from("ships").update({ manufacturer: newName }).eq("id", ship.id);
      }
    }
    setEditingMfr(null);
    setEditMfrName("");
    refreshFleet();
    loadManufacturers();
  }

  async function deleteManufacturer(mfr) {
    await supabase.from("manufacturers").delete().eq("id", mfr.id);
    setDeleteMfrConfirm(null);
    const { data } = await supabase.from("manufacturers").select("*").order("name");
    setManufacturers(data || []);
    if (activeManufacturerTab === mfr.name) setActiveManufacturerTab("all");
  }

  // ── Ship helpers ───────────────────────────────────────────────────────────
  function getOwnerCount(shipId) {
    let owners = 0;
    let total = 0;
    (profiles || []).forEach(p => {
      const userShips = Array.isArray(p.ships) ? p.ships : [];
      userShips.forEach(entry => {
        if (typeof entry === "object" && entry !== null && entry.ship_id === shipId) {
          owners += 1;
          total += (entry.quantity || 1);
        }
      });
    });
    return { owners, total };
  }

  function openAdd() {
    setForm({ name: "", manufacturer: activeManufacturerTab !== "all" ? activeManufacturerTab : "", pledge_url: "", image_url: "" });
    setImageFile(null);
    setImagePreview(null);
    setShowAdd(true);
    setEditing(null);
  }

  function openEdit(ship) {
    setForm({ name: ship.name || "", manufacturer: ship.manufacturer || "", pledge_url: ship.pledge_url || "", image_url: ship.image_url || "" });
    setImageFile(null);
    setImagePreview(ship.image_url || null);
    setEditing(ship);
    setShowAdd(false);
  }

  function processImageFile(file) {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    const validExts = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"];
    if (!file.type.startsWith("image/") && !validExts.includes(ext)) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  function handleImageSelect(e) { processImageFile(e.target.files[0]); }
  function handleDrop(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); processImageFile(e.dataTransfer.files[0]); }
  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragOver(true); }
  function handleDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); }

  async function uploadImage(file) {
    const ext = file.name.split(".").pop();
    const fileName = `ship_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    try {
      const { data, error } = await supabase.storage.from("ship-images").upload(fileName, file, { cacheControl: "3600", upsert: false });
      if (error) { console.error("Storage upload error:", error); return null; }
      const { data: urlData } = supabase.storage.from("ship-images").getPublicUrl(fileName);
      return urlData?.publicUrl || null;
    } catch (err) { console.error("Upload exception:", err); return null; }
  }

  function fileToDataUrl(file) {
    return new Promise((resolve) => { const r = new FileReader(); r.onload = (e) => resolve(e.target.result); r.readAsDataURL(file); });
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);

    let imageUrl = form.image_url;
    if (imageFile) {
      const uploaded = await uploadImage(imageFile);
      if (uploaded) { imageUrl = uploaded; }
      else { alert("Image upload failed. The ship will be saved without an image. Check that the 'ship-images' storage bucket exists in Supabase."); }
    }

    const payload = { name: form.name.trim(), manufacturer: form.manufacturer, pledge_url: form.pledge_url.trim(), image_url: imageUrl };

    if (editing) { await supabase.from("ships").update(payload).eq("id", editing.id); }
    else { await supabase.from("ships").insert([payload]); }

    setSaving(false);
    setEditing(null);
    setShowAdd(false);
    refreshFleet();
    loadManufacturers();
  }

  async function handleDelete(ship) {
    await supabase.from("ships").delete().eq("id", ship.id);
    setDeleteConfirmShip(null);
    refreshFleet();
    loadManufacturers();
  }

  // Filter ships by search AND active manufacturer tab
  const filtered = ships.filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.manufacturer || "").toLowerCase().includes(search.toLowerCase());
    const matchTab = activeManufacturerTab === "all" || s.manufacturer === activeManufacturerTab;
    return matchSearch && matchTab;
  });

  if (loading) return <div style={{ padding: 40, fontFamily: "'Orbitron'", fontSize: 13, letterSpacing: 3, color: `${ACCENT}66` }}>LOADING...</div>;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <SectionLabel>Fleet Registry</SectionLabel>
        <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(24px, 3vw, 36px)", color: "#e8eaf6", marginBottom: 8 }}>
          Ship <span style={{ color: ACCENT }}>Management</span>
        </h2>
        <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.45)" }}>
          Add, edit, and remove ships from the org fleet registry.
        </p>
      </div>

      {/* ── Base64 Migration Banner ─────────────────────────────────────────── */}
      {base64Ships.length > 0 && !migrating && !migrationProgress && (
        <div style={{ marginBottom: 24, padding: "16px 20px", border: "1px solid rgba(239,83,80,0.35)", background: "rgba(239,83,80,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, color: "#ef5350", letterSpacing: 1, marginBottom: 4 }}>
              DATABASE HEALTH WARNING
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.6)", lineHeight: 1.6 }}>
              {base64Ships.length} ship image{base64Ships.length !== 1 ? "s are" : " is"} stored as base64 in the database (~{Math.round(base64Ships.reduce((sum, s) => sum + (s.image_url?.length || 0), 0) / 1024 / 1024)} MB).
              This is slowing down your site and straining the database. Migrate them to Storage to fix this.
            </div>
          </div>
          <ActionBtn onClick={migrateBase64ToStorage} color="#ef5350">Migrate to Storage</ActionBtn>
        </div>
      )}

      {/* Migration progress */}
      {(migrating || migrationProgress) && (
        <div style={{ marginBottom: 24, padding: "16px 20px", border: `1px solid ${migrating ? "#fcd65755" : migrationProgress?.errors?.length > 0 ? "#ef535055" : "#34d39955"}`, background: migrating ? "rgba(252,214,87,0.04)" : migrationProgress?.errors?.length > 0 ? "rgba(239,83,80,0.04)" : "rgba(52,211,153,0.04)" }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, color: migrating ? "#fcd657" : migrationProgress?.errors?.length > 0 ? "#ef5350" : "#34d399", letterSpacing: 1, marginBottom: 8 }}>
            {migrating ? "MIGRATING IMAGES..." : "MIGRATION COMPLETE"}
          </div>
          {migrationProgress && (
            <>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.6)", marginBottom: 8 }}>
                {migrationProgress.done} / {migrationProgress.total} processed
                {migrationProgress.current && <span style={{ color: "#fcd657" }}> — {migrationProgress.current}</span>}
              </div>
              {/* Progress bar */}
              <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.06)", marginBottom: 8 }}>
                <div style={{ width: `${(migrationProgress.done / migrationProgress.total) * 100}%`, height: "100%", background: migrating ? "#fcd657" : "#34d399", transition: "width 0.3s" }} />
              </div>
              {migrationProgress.errors.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#ef5350", marginBottom: 4 }}>{migrationProgress.errors.length} error{migrationProgress.errors.length !== 1 ? "s" : ""}:</div>
                  {migrationProgress.errors.map((e, i) => <div key={i} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(239,83,80,0.7)", paddingLeft: 12 }}>{e}</div>)}
                </div>
              )}
              {!migrating && (
                <div style={{ marginTop: 10 }}>
                  <ActionBtn small onClick={() => setMigrationProgress(null)} color={ACCENT}>Dismiss</ActionBtn>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Manufacturer Management (collapsible) ───────────────────────────── */}
      <div style={{ marginBottom: 24, border: `1px solid ${mfrExpanded ? `${ACCENT}33` : "rgba(255,255,255,0.06)"}`, background: mfrExpanded ? `${ACCENT}04` : "rgba(255,255,255,0.01)", transition: "all 0.2s" }}>
        <button onClick={() => setMfrExpanded(!mfrExpanded)} style={{ width: "100%", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700, color: ACCENT, letterSpacing: 1.5 }}>MANUFACTURERS</span>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)" }}>({manufacturers.length})</span>
          </div>
          <span style={{ fontFamily: "'Orbitron'", fontSize: 14, color: "rgba(232,234,246,0.4)", transition: "transform 0.2s", transform: mfrExpanded ? "rotate(180deg)" : "rotate(0)" }}>▾</span>
        </button>

        {mfrExpanded && (
          <div style={{ padding: "0 18px 18px" }}>
            {/* Add new manufacturer */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input type="text" placeholder="New manufacturer name..." value={newMfrName} onChange={e => setNewMfrName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") addManufacturer(); }}
                style={{ ...inputStyle, flex: 1 }} />
              <ActionBtn onClick={addManufacturer} small>+ Add</ActionBtn>
            </div>

            {/* Manufacturer list */}
            {manufacturers.length === 0 ? (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 1, padding: "12px 0" }}>NO MANUFACTURERS — ADD YOUR FIRST</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {manufacturers.map(mfr => {
                  const shipCount = ships.filter(s => s.manufacturer === mfr.name).length;
                  const isEditing = editingMfr?.id === mfr.id;
                  return (
                    <div key={mfr.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", transition: "all 0.15s" }}>
                      {isEditing ? (
                        <>
                          <input type="text" value={editMfrName} onChange={e => setEditMfrName(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") saveManufacturerEdit(); if (e.key === "Escape") setEditingMfr(null); }}
                            style={{ ...inputStyle, flex: 1, padding: "6px 10px", fontSize: 13 }} autoFocus />
                          <ActionBtn onClick={saveManufacturerEdit} small>Save</ActionBtn>
                          <ActionBtn onClick={() => setEditingMfr(null)} small color="rgba(255,255,255,0.3)">✕</ActionBtn>
                        </>
                      ) : (
                        <>
                          <div style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{mfr.name}</div>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)", letterSpacing: 1 }}>{shipCount} SHIPS</span>
                          <ActionBtn onClick={() => { setEditingMfr(mfr); setEditMfrName(mfr.name); }} small>Edit</ActionBtn>
                          <ActionBtn onClick={() => setDeleteMfrConfirm(mfr)} small color="#ef5350">Del</ActionBtn>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Manufacturer Tabs ───────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, overflowX: "auto", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <button onClick={() => setActiveManufacturerTab("all")}
          style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", padding: "10px 18px", border: "none", cursor: "pointer", background: activeManufacturerTab === "all" ? `${ACCENT}12` : "transparent", color: activeManufacturerTab === "all" ? ACCENT : "rgba(232,234,246,0.35)", borderBottom: activeManufacturerTab === "all" ? `2px solid ${ACCENT}` : "2px solid transparent", transition: "all 0.2s", whiteSpace: "nowrap" }}>
          All ({ships.length})
        </button>
        {manufacturers.map(mfr => {
          const count = ships.filter(s => s.manufacturer === mfr.name).length;
          return (
            <button key={mfr.id} onClick={() => setActiveManufacturerTab(mfr.name)}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", padding: "10px 18px", border: "none", cursor: "pointer", background: activeManufacturerTab === mfr.name ? `${ACCENT}12` : "transparent", color: activeManufacturerTab === mfr.name ? ACCENT : "rgba(232,234,246,0.35)", borderBottom: activeManufacturerTab === mfr.name ? `2px solid ${ACCENT}` : "2px solid transparent", transition: "all 0.2s", whiteSpace: "nowrap" }}>
              {mfr.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <input type="text" placeholder="Search ships..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
        <ActionBtn onClick={openAdd}>+ Add Ship</ActionBtn>
      </div>

      {/* Add / Edit form */}
      {(showAdd || editing) && (
        <div style={{ padding: "28px 24px", border: `1px solid ${ACCENT}33`, background: `${ACCENT}06`, marginBottom: 24 }}>
          <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 15, color: "#e8eaf6", marginBottom: 20 }}>
            {editing ? "Edit Ship" : "Add New Ship"}
          </h3>

          <Field label="Ship Name *">
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Constellation Andromeda" style={inputStyle} />
          </Field>

          <Field label="Manufacturer">
            <select value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))} style={selectStyle}>
              <option value="">— Select Manufacturer —</option>
              {manufacturers.map(mfr => (
                <option key={mfr.id} value={mfr.name}>{mfr.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Pledge Store URL">
            <input type="text" value={form.pledge_url} onChange={e => setForm(f => ({ ...f, pledge_url: e.target.value }))} placeholder="https://robertsspaceindustries.com/pledge/..." style={inputStyle} />
          </Field>

          <Field label="Ship Image">
            <input type="file" accept="image/*,.heic,.heif" ref={fileInputRef} onChange={handleImageSelect} style={{ display: "none" }} />
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? ACCENT : "rgba(252,214,87,0.25)"}`,
                background: dragOver ? `${ACCENT}12` : "rgba(255,255,255,0.02)",
                padding: imagePreview ? "16px" : "36px 24px",
                cursor: "pointer",
                transition: "all 0.2s",
                textAlign: "center",
              }}
            >
              {imagePreview ? (
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ width: 180, aspectRatio: "16/9", overflow: "hidden", border: `1px solid ${ACCENT}33`, flexShrink: 0 }}>
                    <img src={imagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <div style={{ textAlign: "left" }}>
                    {imageFile && <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6", marginBottom: 4 }}>{imageFile.name}</div>}
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>DRAG NEW IMAGE OR CLICK TO REPLACE</div>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 28, color: dragOver ? ACCENT : "rgba(252,214,87,0.25)", marginBottom: 12, transition: "color 0.2s" }}>+</div>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: dragOver ? ACCENT : "rgba(232,234,246,0.5)", marginBottom: 6, transition: "color 0.2s" }}>
                    Drag &amp; drop an image here, or click to browse
                  </div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.25)", letterSpacing: 1 }}>
                    JPG, PNG, WEBP, GIF, OR HEIC
                  </div>
                </div>
              )}
            </div>
          </Field>

          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <ActionBtn onClick={handleSave}>{saving ? "Saving..." : editing ? "Update Ship" : "Add Ship"}</ActionBtn>
            <ActionBtn onClick={() => { setEditing(null); setShowAdd(false); }} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </div>
      )}

      {/* Ships list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map(ship => {
          const { owners, total } = getOwnerCount(ship.id);
          return (
            <div key={ship.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 18px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}33`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}>
              <div style={{ width: 80, height: 45, flexShrink: 0, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.3)" }}>
                {ship.image_url ? (
                  <img src={ship.image_url} alt={ship.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 16, color: "rgba(252,214,87,0.15)" }}>◇</div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ship.name}</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>{ship.manufacturer || "—"}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: ACCENT }}>{total}</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>{owners} OWNERS</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <ActionBtn onClick={() => openEdit(ship)} small>Edit</ActionBtn>
                <ActionBtn onClick={() => setDeleteConfirmShip(ship)} small color="#ef5350">Del</ActionBtn>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
          {search ? "NO SHIPS MATCH" : activeManufacturerTab !== "all" ? `NO SHIPS FROM ${activeManufacturerTab.toUpperCase()}` : "NO SHIPS IN REGISTRY — ADD YOUR FIRST SHIP"}
        </div>
      )}

      {/* Delete ship confirmation */}
      {deleteConfirmShip && (
        <Modal title="Delete Ship" onClose={() => setDeleteConfirmShip(null)}>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.7)", marginBottom: 8 }}>
            Delete <span style={{ color: "#ef5350", fontWeight: 700 }}>{deleteConfirmShip.name}</span>?
          </p>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, marginBottom: 24 }}>
            This will remove the ship from the registry. Members who own this ship will need to update their fleet.
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn onClick={() => handleDelete(deleteConfirmShip)} color="#ef5350">Confirm Delete</ActionBtn>
            <ActionBtn onClick={() => setDeleteConfirmShip(null)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}

      {/* Delete manufacturer confirmation */}
      {deleteMfrConfirm && (
        <Modal title="Delete Manufacturer" onClose={() => setDeleteMfrConfirm(null)}>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.7)", marginBottom: 8 }}>
            Delete <span style={{ color: "#ef5350", fontWeight: 700 }}>{deleteMfrConfirm.name}</span>?
          </p>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, marginBottom: 24 }}>
            Ships assigned to this manufacturer will keep their current value but it will no longer appear in the dropdown.
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn onClick={() => deleteManufacturer(deleteMfrConfirm)} color="#ef5350">Confirm Delete</ActionBtn>
            <ActionBtn onClick={() => setDeleteMfrConfirm(null)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BLUEPRINT MANAGER — admin UI to update images / categorization
// ─────────────────────────────────────────────────────────────────────────────
function BlueprintManager() {
  const { blueprints: preloadedBps, profiles: preloadedProfiles, loaded: preloaded, refreshBlueprints } = useFleetData();
  const [blueprints, setBlueprints] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterFamily, setFilterFamily] = useState("");
  const [hideWithImage, setHideWithImage] = useState(false);
  const [visibleCount, setVisibleCount] = useState(200);

  // Reset paging when filters change
  React.useEffect(() => { setVisibleCount(200); }, [search, filterGroup, filterCategory, filterFamily, hideWithImage]);

  const [form, setForm] = useState({ name: "", item_group: "", category: "", family: "", image_url: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  // ─── In-app trigger of the scunpacked sync workflow (admin only) ─────────
  // Calls the `trigger-scunpacked-sync` edge function which dispatches the
  // GitHub Actions workflow via the workflow_dispatch API.
  const [syncing, setSyncing] = useState(false);
  const [syncTarget, setSyncTarget] = useState("both");
  const [syncMsg, setSyncMsg] = useState(null); // { ok, text } | null

  async function triggerSync() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error("Not signed in");
      const edgeUrl = `${process.env.REACT_APP_SUPABASE_URL || "https://negtawswkqbvvwobozdh.supabase.co"}/functions/v1/trigger-scunpacked-sync`;
      const r = await fetch(edgeUrl, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY || "",
        },
        body: JSON.stringify({ target: syncTarget, skip_wiki: false }),
      });
      const payload = await r.json().catch(() => ({}));
      if (!r.ok || !payload.ok) {
        const detail = payload.hint || payload.detail || payload.error || `HTTP ${r.status}`;
        setSyncMsg({ ok: false, text: `Sync trigger failed — ${detail}` });
      } else {
        setSyncMsg({ ok: true, text: payload.message || "Workflow triggered." });
      }
    } catch (e) {
      setSyncMsg({ ok: false, text: e.message || String(e) });
    }
    setSyncing(false);
  }

  React.useEffect(() => {
    if (preloaded) {
      setBlueprints(preloadedBps || []);
      setProfiles(preloadedProfiles || []);
      setLoading(false);
    } else {
      loadBlueprints();
    }
  }, [preloaded, preloadedBps, preloadedProfiles]);

  async function loadBlueprints() {
    setLoading(true);
    const [bpData, { data: profData }] = await Promise.all([
      loadAllBlueprints(),
      supabase.from("profiles").select("id, blueprints"),
    ]);
    setBlueprints(bpData || []);
    setProfiles(profData || []);
    setLoading(false);
  }

  // Owner counts (boolean ownership: each profile contributes ≤1)
  const ownerCounts = React.useMemo(() => {
    const data = {};
    blueprints.forEach(b => { data[b.id] = { owners: 0 }; });
    profiles.forEach(p => {
      const list = Array.isArray(p.blueprints) ? p.blueprints : [];
      const seen = new Set();
      list.forEach(entry => {
        const id = typeof entry === "string" ? entry : (entry && entry.blueprint_id);
        if (id && data[id] && !seen.has(id)) {
          seen.add(id);
          data[id].owners += 1;
        }
      });
    });
    return data;
  }, [blueprints, profiles]);

  const groups = React.useMemo(
    () => [...new Set(blueprints.map(b => b.item_group).filter(Boolean))].sort(),
    [blueprints]
  );
  const categories = React.useMemo(() => {
    const pool = filterGroup ? blueprints.filter(b => b.item_group === filterGroup) : blueprints;
    return [...new Set(pool.map(b => b.category).filter(Boolean))].sort();
  }, [blueprints, filterGroup]);
  const families = React.useMemo(() => {
    let pool = filterGroup ? blueprints.filter(b => b.item_group === filterGroup) : blueprints;
    if (filterCategory) pool = pool.filter(b => b.category === filterCategory);
    return [...new Set(pool.map(b => b.family).filter(Boolean))].sort();
  }, [blueprints, filterGroup, filterCategory]);

  // Drop narrower filters when they fall out of the active group/category.
  React.useEffect(() => {
    if (filterCategory && !categories.includes(filterCategory)) setFilterCategory("");
  }, [categories, filterCategory]);
  React.useEffect(() => {
    if (filterFamily && !families.includes(filterFamily)) setFilterFamily("");
  }, [families, filterFamily]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return blueprints.filter(b => {
      if (filterGroup && b.item_group !== filterGroup) return false;
      if (filterCategory && b.category !== filterCategory) return false;
      if (filterFamily && b.family !== filterFamily) return false;
      if (hideWithImage && b.image_url) return false;
      if (!q) return true;
      const hay = `${b.name} ${b.item_group || ""} ${b.category || ""} ${b.family || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [blueprints, filterGroup, filterCategory, filterFamily, hideWithImage, search]);

  function startEdit(bp) {
    setEditing(bp);
    setForm({
      name: bp.name || "",
      item_group: bp.item_group || "",
      category: bp.category || "",
      family: bp.family || "",
      image_url: bp.image_url || "",
    });
    setImageFile(null);
    setImagePreview(bp.image_url || null);
  }

  function cancelEdit() {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setForm({ name: "", item_group: "", category: "", family: "", image_url: "" });
  }

  function handleImageFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Please pick an image file."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  async function saveBlueprint() {
    if (!editing) return;
    setSaving(true);
    let imageUrl = form.image_url || null;
    try {
      if (imageFile) {
        const ext = imageFile.name.split(".").pop() || "png";
        const fileName = `${editing.id}-${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage.from("blueprint-images").upload(fileName, imageFile, { cacheControl: "3600", upsert: false });
        if (uploadErr) throw uploadErr;
        const { data: urlData } = supabase.storage.from("blueprint-images").getPublicUrl(fileName);
        imageUrl = urlData?.publicUrl || imageUrl;
      }
      const payload = {
        name: form.name || editing.name,
        item_group: form.item_group || null,
        category: form.category || null,
        family: form.family || null,
        image_url: imageUrl,
      };
      const { error } = await supabase.from("blueprints").update(payload).eq("id", editing.id);
      if (error) throw error;
      await loadBlueprints();
      if (refreshBlueprints) refreshBlueprints();
      cancelEdit();
    } catch (e) {
      alert("Save failed: " + (e.message || String(e)) + "\nIf the upload failed, make sure the 'blueprint-images' Storage bucket exists in Supabase (run sql/blueprints.sql to create it).");
    }
    setSaving(false);
  }

  async function clearImage() {
    if (!editing) return;
    if (!window.confirm("Remove the image from this blueprint?")) return;
    setSaving(true);
    await supabase.from("blueprints").update({ image_url: null }).eq("id", editing.id);
    await loadBlueprints();
    if (refreshBlueprints) refreshBlueprints();
    setForm(p => ({ ...p, image_url: "" }));
    setImagePreview(null);
    setImageFile(null);
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 60, textAlign: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 12, color: "rgba(252,214,87,0.6)", letterSpacing: 3 }}>LOADING BLUEPRINTS…</div>;

  const totalWithImages = blueprints.filter(b => !!b.image_url).length;

  // ── scunpacked sync snapshot ───────────────────────────────────────────────
  // Powered by the GitHub Actions workflow .github/workflows/sync-scunpacked.yml.
  // `last_synced_at` and `sync_status` are stamped on each row by the importer.
  const syncStats = (() => {
    let lastSynced = null;
    const byStatus = { synced: 0, needs_alias: 0, wiki_failed: 0, never: 0 };
    blueprints.forEach(b => {
      if (b.last_synced_at) {
        const t = new Date(b.last_synced_at).getTime();
        if (!lastSynced || t > lastSynced) lastSynced = t;
      }
      const s = b.sync_status;
      if (s && byStatus[s] !== undefined) byStatus[s]++;
      else if (!b.source_uuid) byStatus.never++;
    });
    return { lastSynced, byStatus };
  })();
  const lastSyncedLabel = (() => {
    if (!syncStats.lastSynced) return "Never";
    const diffMs = Date.now() - syncStats.lastSynced;
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "Just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.round(hr / 24);
    return `${d}d ago`;
  })();

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 40, height: 2, background: "#fcd657" }} />
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "#fcd657", textTransform: "uppercase" }}>Blueprint Catalog</span>
        <div style={{ flex: 1, height: 1, background: "rgba(252,214,87,0.15)" }} />
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>
          {totalWithImages} / {blueprints.length} with images
        </span>
      </div>

      {/* scunpacked-data sync snapshot — driven by .github/workflows/sync-scunpacked.yml */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap", alignItems: "center", padding: "10px 14px", border: "1px solid rgba(252,214,87,0.18)", background: "rgba(252,214,87,0.04)" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.5)", textTransform: "uppercase" }}>
            scunpacked sync
          </span>
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 12, color: syncStats.lastSynced ? "#34d399" : "rgba(232,234,246,0.4)", letterSpacing: 1 }}>
            {lastSyncedLabel}
          </span>
        </div>
        <div style={{ flex: 1 }} />
        {[
          { label: "Synced",       value: syncStats.byStatus.synced,       color: "#34d399", filter: "synced" },
          { label: "Needs alias",  value: syncStats.byStatus.needs_alias,  color: "#fcd657", filter: "needs_alias" },
          { label: "Wiki failed",  value: syncStats.byStatus.wiki_failed,  color: "#fb923c", filter: "wiki_failed" },
          { label: "Pre-sync",     value: syncStats.byStatus.never,        color: "rgba(232,234,246,0.4)", filter: "never" },
        ].map(stat => (
          <div key={stat.label} style={{ display: "inline-flex", alignItems: "baseline", gap: 6, padding: "4px 10px", border: `1px solid ${stat.color}33`, background: `${stat.color}0d` }}>
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 12, color: stat.color }}>{stat.value}</span>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>{stat.label}</span>
          </div>
        ))}
        <a href="https://github.com/StarCitizenWiki/scunpacked-data" target="_blank" rel="noopener noreferrer"
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(252,214,87,0.7)", textDecoration: "none", textTransform: "uppercase", marginLeft: 6 }}>
          source ↗
        </a>

        {/* Sync now — fires GitHub Actions workflow_dispatch via edge function */}
        <select value={syncTarget} onChange={e => setSyncTarget(e.target.value)} disabled={syncing}
          style={{ padding: "5px 8px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase" }}>
          <option value="both">Both</option>
          <option value="blueprints">Blueprints</option>
          <option value="ships">Ships</option>
        </select>
        <button onClick={triggerSync} disabled={syncing}
          style={{ padding: "5px 12px", background: syncing ? "rgba(252,214,87,0.1)" : "#fcd657", border: "1px solid #fcd657", color: syncing ? "#fcd657" : "#05070f", fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", cursor: syncing ? "wait" : "pointer" }}>
          {syncing ? "Triggering…" : "Sync now"}
        </button>
      </div>

      {syncMsg && (
        <div style={{ marginBottom: 18, padding: "8px 14px", border: `1px solid ${syncMsg.ok ? "rgba(52,211,153,0.4)" : "rgba(239,83,80,0.4)"}`, background: syncMsg.ok ? "rgba(52,211,153,0.08)" : "rgba(239,83,80,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: syncMsg.ok ? "#34d399" : "#ef5350" }}>
            {syncMsg.ok ? "✓" : "✕"} {syncMsg.text}
          </span>
          {syncMsg.ok && (
            <a href={`https://github.com/SpartanBoy45/Starkeeper_Industries/actions/workflows/sync-scunpacked.yml`} target="_blank" rel="noopener noreferrer"
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(252,214,87,0.7)", textDecoration: "none", textTransform: "uppercase", marginLeft: "auto" }}>
              view run ↗
            </a>
          )}
          <button onClick={() => setSyncMsg(null)} style={{ background: "transparent", border: "none", color: "rgba(232,234,246,0.5)", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Filters — Group → Category → Family (each narrows the next) */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <input type="text" placeholder="Search blueprints…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
        <select value={filterGroup} onChange={e => { setFilterGroup(e.target.value); setFilterCategory(""); setFilterFamily(""); }}
          style={{ padding: "8px 12px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, minWidth: 140 }}>
          <option value="">All Groups</option>
          {groups.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setFilterFamily(""); }}
          style={{ padding: "8px 12px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, minWidth: 140 }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterFamily} onChange={e => setFilterFamily(e.target.value)}
          style={{ padding: "8px 12px", background: "#0a0610", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, minWidth: 140 }}>
          <option value="">All Families</option>
          {families.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.55)", letterSpacing: 1, textTransform: "uppercase", cursor: "pointer" }}>
          <input type="checkbox" checked={hideWithImage} onChange={e => setHideWithImage(e.target.checked)} />
          Needs image
        </label>
      </div>

      {/* List — fills the remaining viewport height so long catalogs scroll
          inside their own container instead of being clipped to a fixed 540px. */}
      <div style={{ border: "1px solid rgba(255,255,255,0.06)", maxHeight: "calc(100vh - 320px)", minHeight: 400, overflowY: "auto" }}>
        {filtered.slice(0, visibleCount).map(bp => {
          const owners = ownerCounts[bp.id] || { owners: 0 };
          const isEditing = editing && editing.id === bp.id;
          return (
            <div key={bp.id}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: isEditing ? "default" : "pointer", background: isEditing ? "rgba(252,214,87,0.06)" : "transparent" }}
              onClick={() => !isEditing && startEdit(bp)}>
              <div style={{ width: 44, height: 44, flexShrink: 0, overflow: "hidden", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                {bp.image_url ? (
                  <img src={bp.image_url} alt={bp.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 18, color: "rgba(252,214,87,0.18)" }}>◇</div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bp.name}</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.35)", letterSpacing: 1, textTransform: "uppercase" }}>
                  {bp.item_group ? `${bp.item_group} · ` : ""}{bp.category}{bp.family ? ` · ${bp.family}` : ""}
                </div>
              </div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>
                {owners.owners > 0 ? `${owners.owners} ${owners.owners === 1 ? "OWNER" : "OWNERS"}` : "—"}
              </div>
              {!bp.image_url && (
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "#ef5350", letterSpacing: 1 }}>NO IMG</span>
              )}
            </div>
          );
        })}
        {filtered.length > visibleCount && (
          <div style={{ display: "flex", justifyContent: "center", padding: 12, gap: 8, flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, alignSelf: "center" }}>
              Showing {visibleCount} of {filtered.length}
            </span>
            <button onClick={() => setVisibleCount(c => c + 200)}
              style={{ padding: "6px 14px", background: "transparent", border: "1px solid rgba(252,214,87,0.4)", color: "#fcd657", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, cursor: "pointer", textTransform: "uppercase" }}>
              Load 200 More
            </button>
            <button onClick={() => setVisibleCount(filtered.length)}
              style={{ padding: "6px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.6)", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, cursor: "pointer", textTransform: "uppercase" }}>
              Show All
            </button>
          </div>
        )}
        {filtered.length === 0 && (
          <div style={{ padding: 30, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: 2 }}>NO BLUEPRINTS MATCH</div>
        )}
      </div>

      {/* Edit panel */}
      {editing && (
        <div style={{ marginTop: 22, padding: 18, border: "1px solid rgba(252,214,87,0.35)", background: "rgba(252,214,87,0.04)" }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, color: "#fcd657", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14 }}>
            Editing — {editing.name}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 280px) 1fr", gap: 18 }}>
            {/* Image side */}
            <div>
              <div onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleImageFile(e.dataTransfer.files[0]); }}
                onClick={() => fileInputRef.current?.click()}
                style={{ width: "100%", aspectRatio: "1/1", border: `1px dashed ${dragOver ? "#fcd657" : "rgba(255,255,255,0.15)"}`, background: dragOver ? "rgba(252,214,87,0.06)" : "rgba(255,255,255,0.02)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", position: "relative" }}>
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ textAlign: "center", padding: 12 }}>
                    <div style={{ fontFamily: "'Orbitron'", fontSize: 36, color: "rgba(252,214,87,0.25)", marginBottom: 6 }}>+</div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase" }}>Drop or click to upload</div>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => handleImageFile(e.target.files?.[0])} />
              <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                {(imagePreview || form.image_url) && (
                  <button onClick={clearImage} disabled={saving}
                    style={{ flex: 1, padding: "6px 10px", background: "transparent", border: "1px solid rgba(239,83,80,0.4)", color: "#ef5350", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, cursor: "pointer", textTransform: "uppercase" }}>
                    Clear Image
                  </button>
                )}
              </div>
              <input type="text" placeholder="Or paste an image URL…" value={form.image_url}
                onChange={e => { setForm(p => ({ ...p, image_url: e.target.value })); setImagePreview(e.target.value || null); setImageFile(null); }}
                style={{ width: "100%", marginTop: 8, padding: "6px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.2)", color: "#e8eaf6", fontFamily: "'Space Mono', monospace", fontSize: 10 }} />
            </div>

            {/* Fields side */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>Name
                <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
              </label>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>Group
                <input type="text" list="bp-grp-list" value={form.item_group} onChange={e => setForm(p => ({ ...p, item_group: e.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
                <datalist id="bp-grp-list">{groups.map(g => <option key={g} value={g} />)}</datalist>
              </label>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>Category
                <input type="text" list="bp-cat-list" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
                <datalist id="bp-cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
              </label>
              <label style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>Family
                <input type="text" list="bp-fam-list" value={form.family} onChange={e => setForm(p => ({ ...p, family: e.target.value }))}
                  style={{ width: "100%", marginTop: 4, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(252,214,87,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }} />
                <datalist id="bp-fam-list">{families.map(f => <option key={f} value={f} />)}</datalist>
              </label>

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={saveBlueprint} disabled={saving}
                  style={{ padding: "10px 18px", background: "transparent", border: "2px solid #fcd657", color: "#fcd657", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, cursor: saving ? "wait" : "pointer", textTransform: "uppercase" }}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={cancelEdit}
                  style={{ padding: "10px 18px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.6)", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, cursor: "pointer", textTransform: "uppercase" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOME BASE MANAGER
// ─────────────────────────────────────────────────────────────────────────────
function HomeBaseManager() {
  const [homeBases, setHomeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [newSystem, setNewSystem] = useState("");
  const [showAddLocation, setShowAddLocation] = useState(null);
  const [newLocation, setNewLocation] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadHomeBases(); }, []);

  async function loadHomeBases() {
    setLoading(true);
    const { data } = await supabase.from("home_bases").select("*").order("system");
    setHomeBases(data || []);
    setLoading(false);
  }

  async function addSystem() {
    if (!newSystem.trim()) return;
    // Insert a placeholder row for the system (location will be empty string as a system marker)
    const { error } = await supabase.from("home_bases").insert([{ system: newSystem.trim(), location: "__system__" }]);
    if (error) { console.error("Add system error:", error); alert("Failed to add system: " + error.message); return; }
    setNewSystem("");
    setShowAddSystem(false);
    loadHomeBases();
  }

  async function addLocation(system) {
    if (!newLocation.trim()) return;
    // If the system only has a placeholder row, delete it first
    const placeholder = homeBases.find(hb => hb.system === system && hb.location === "__system__");
    if (placeholder) {
      await supabase.from("home_bases").delete().eq("id", placeholder.id);
    }
    const { error } = await supabase.from("home_bases").insert([{ system, location: newLocation.trim() }]);
    if (error) { console.error("Add location error:", error); alert("Failed to add location: " + error.message); return; }
    setNewLocation("");
    setShowAddLocation(null);
    loadHomeBases();
  }

  async function updateLocation(id, newValue) {
    if (!newValue.trim()) return;
    await supabase.from("home_bases").update({ location: newValue.trim() }).eq("id", id);
    setEditingId(null);
    setEditingValue("");
    loadHomeBases();
  }

  async function deleteLocation(id) {
    await supabase.from("home_bases").delete().eq("id", id);
    setDeleteConfirm(null);
    loadHomeBases();
  }

  async function deleteSystem(system) {
    await supabase.from("home_bases").delete().eq("system", system);
    setDeleteConfirm(null);
    loadHomeBases();
  }

  // Group by system (filter out __system__ placeholders from display)
  const grouped = {};
  homeBases.forEach(hb => {
    if (!grouped[hb.system]) grouped[hb.system] = [];
    if (hb.location && hb.location !== "__system__") grouped[hb.system].push(hb);
  });
  // Also include systems that only have a placeholder
  homeBases.forEach(hb => {
    if (!grouped[hb.system]) grouped[hb.system] = [];
  });
  const systems = Object.keys(grouped).sort();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div><SectionLabel>Admin</SectionLabel><h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 26, color: "#e8eaf6" }}>Home Bases</h2></div>
        <ActionBtn onClick={() => setShowAddSystem(true)} color="#fcd657">+ Add System</ActionBtn>
      </div>

      {loading ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>LOADING...</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {systems.length === 0 ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 60 }}>NO SYSTEMS CREATED YET</div>
          ) : (
            systems.map(system => {
              const locs = grouped[system];
              return (
                <div key={system} style={{ border: `1px solid ${ACCENT}33`, background: `${ACCENT}08`, padding: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: ACCENT }}>{system}</h3>
                    <div style={{ display: "flex", gap: 8 }}>
                      <ActionBtn onClick={() => setShowAddLocation(system)} color="#4fc3f7" small>+ Location</ActionBtn>
                      <ActionBtn onClick={() => setDeleteConfirm({ type: "system", value: system })} color="#ef5350" small>Delete System</ActionBtn>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {locs.length === 0 ? (
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 1, padding: 16, textAlign: "center" }}>No locations added yet</div>
                    ) : (
                      locs.map(loc => (
                        <div key={loc.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", border: `1px solid rgba(255,255,255,0.1)`, background: "rgba(0,0,0,0.3)" }}>
                          {editingId === loc.id ? (
                            <div style={{ display: "flex", gap: 8, flex: 1 }}>
                              <input
                                type="text"
                                value={editingValue}
                                onChange={e => setEditingValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter") updateLocation(loc.id, editingValue);
                                  if (e.key === "Escape") setEditingId(null);
                                }}
                                style={{ ...inputStyle, flex: 1 }}
                                autoFocus
                              />
                              <ActionBtn onClick={() => updateLocation(loc.id, editingValue)} color="#81c784" small>Save</ActionBtn>
                              <ActionBtn onClick={() => setEditingId(null)} color="rgba(255,255,255,0.3)" small>Cancel</ActionBtn>
                            </div>
                          ) : (
                            <>
                              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "#e8eaf6", flex: 1, cursor: "pointer", padding: "4px 8px" }} onClick={() => { setEditingId(loc.id); setEditingValue(loc.location); }}>
                                {loc.location}
                              </span>
                              <ActionBtn onClick={() => setDeleteConfirm({ type: "location", value: loc.id })} color="#ef5350" small>Delete</ActionBtn>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {showAddLocation === system && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${ACCENT}22` }}>
                      <input
                        type="text"
                        placeholder="Enter location name..."
                        value={newLocation}
                        onChange={e => setNewLocation(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") addLocation(system); }}
                        style={{ ...inputStyle, flex: 1 }}
                        autoFocus
                      />
                      <ActionBtn onClick={() => addLocation(system)} color="#81c784" small>Add</ActionBtn>
                      <ActionBtn onClick={() => setShowAddLocation(null)} color="rgba(255,255,255,0.3)" small>Cancel</ActionBtn>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {showAddSystem && (
        <Modal title="Add System" onClose={() => setShowAddSystem(false)}>
          <Field label="System Name">
            <input
              type="text"
              placeholder="Enter system name..."
              value={newSystem}
              onChange={e => setNewSystem(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") addSystem(); }}
              style={inputStyle}
              autoFocus
            />
          </Field>
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn onClick={addSystem} color="#81c784">Add System</ActionBtn>
            <ActionBtn onClick={() => setShowAddSystem(false)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}

      {deleteConfirm && (
        <Modal title="Confirm Delete" onClose={() => setDeleteConfirm(null)}>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.7)", marginBottom: 8 }}>
            {deleteConfirm.type === "system"
              ? `Delete system "${deleteConfirm.value}" and all its locations?`
              : "Delete this location?"}
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <ActionBtn
              onClick={() => {
                if (deleteConfirm.type === "system") deleteSystem(deleteConfirm.value);
                else deleteLocation(deleteConfirm.value);
              }}
              color="#ef5350"
            >
              Delete
            </ActionBtn>
            <ActionBtn onClick={() => setDeleteConfirm(null)} color="rgba(255,255,255,0.3)">Cancel</ActionBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS DASHBOARD (super admin only)
// ─────────────────────────────────────────────────────────────────────────────
function AnalyticsDashboard({ profiles: allProfiles, events: allEvents, teams: allTeams }) {
  const [sessions, setSessions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => { loadAnalytics(); }, []);

  async function loadAnalytics() {
    setAnalyticsLoading(true);
    const [{ data: s }, { data: rev }, { data: rat }, { data: att }] = await Promise.all([
      supabase.from("user_sessions").select("user_id, started_at, last_active_at").order("started_at", { ascending: false }).limit(5000),
      supabase.from("event_reviews").select("*"),
      supabase.from("event_ratings").select("*"),
      supabase.from("event_attendance").select("*"),
    ]);
    setSessions(s || []);
    setReviews(rev || []);
    setRatings(rat || []);
    setAttendance(att || []);
    setAnalyticsLoading(false);
  }

  if (analyticsLoading) {
    return (
      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textAlign: "center", padding: 60 }}>
        LOADING ANALYTICS...
      </div>
    );
  }

  const profileMap = {};
  (allProfiles || []).forEach(p => { profileMap[p.id] = p; });

  // ── Deduplicate sessions: collapse multiple starts within 15 min per user into one ──
  const sessionsByUser = {};
  sessions.forEach(s => {
    if (!sessionsByUser[s.user_id]) sessionsByUser[s.user_id] = [];
    sessionsByUser[s.user_id].push(s);
  });
  const FIFTEEN_MIN = 15 * 60 * 1000;
  const dedupedSessions = [];
  Object.values(sessionsByUser).forEach(userSessions => {
    userSessions.sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
    let lastKept = null;
    userSessions.forEach(s => {
      const t = new Date(s.started_at).getTime();
      if (lastKept === null || t - lastKept >= FIFTEEN_MIN) {
        dedupedSessions.push(s);
        lastKept = t;
      }
    });
  });

  // ── Visit data: sessions per day (last 30 days) ──
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sessionsByDay = {};
  const uniqueVisitorsByDay = {};
  dedupedSessions.forEach(s => {
    const d = new Date(s.started_at);
    if (d < thirtyDaysAgo) return;
    const key = d.toISOString().slice(0, 10);
    sessionsByDay[key] = (sessionsByDay[key] || 0) + 1;
    if (!uniqueVisitorsByDay[key]) uniqueVisitorsByDay[key] = new Set();
    uniqueVisitorsByDay[key].add(s.user_id);
  });

  // Build 30 day array
  const dayLabels = [];
  const dayValues = [];
  const dayVisitors = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const key = d.toISOString().slice(0, 10);
    dayLabels.push(d.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
    dayValues.push(sessionsByDay[key] || 0);
    dayVisitors.push(uniqueVisitorsByDay[key]?.size || 0);
  }
  const maxSessions = Math.max(...dayValues, 1);

  // ── Event creation leaderboard ──
  const creatorCounts = {};
  (allEvents || []).forEach(ev => {
    if (ev.created_by) {
      creatorCounts[ev.created_by] = (creatorCounts[ev.created_by] || 0) + 1;
    }
  });
  const creatorLeaderboard = Object.entries(creatorCounts)
    .map(([uid, count]) => ({ profile: profileMap[uid], count }))
    .filter(e => e.profile)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // ── Most active by event attendance ──
  // Only count rows marked showed_up AND whose event still exists, so orphaned
  // attendance left behind by deleted events can't inflate the count.
  const validEventIds = new Set((allEvents || []).map(ev => ev.id));
  const attendanceCounts = {};
  attendance.forEach(a => {
    if (a.showed_up && validEventIds.has(a.event_id)) {
      attendanceCounts[a.user_id] = (attendanceCounts[a.user_id] || 0) + 1;
    }
  });
  const attendanceLeaderboard = Object.entries(attendanceCounts)
    .map(([uid, count]) => ({ profile: profileMap[uid], count }))
    .filter(e => e.profile)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  const maxAttendance = Math.max(...attendanceLeaderboard.map(e => e.count), 1);

  // ── Most active by sessions/visits ──
  const visitCounts = {};
  dedupedSessions.forEach(s => {
    visitCounts[s.user_id] = (visitCounts[s.user_id] || 0) + 1;
  });
  const visitLeaderboard = Object.entries(visitCounts)
    .map(([uid, count]) => ({ profile: profileMap[uid], count }))
    .filter(e => e.profile)
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  const maxVisits = Math.max(...visitLeaderboard.map(e => e.count), 1);

  // ── Event satisfaction: general + per team ──
  const eventRatingMap = {};
  ratings.forEach(r => {
    if (!eventRatingMap[r.event_id]) eventRatingMap[r.event_id] = [];
    eventRatingMap[r.event_id].push(r.rating);
  });
  const overallSatisfaction = ratings.length > 0
    ? (ratings.reduce((s, r) => s + r.rating, 0) / ratings.length).toFixed(1)
    : 0;

  // Per-team satisfaction
  const teamSatisfaction = {};
  (allEvents || []).forEach(ev => {
    const team = ev.team || (ev.is_org_wide ? "Org-Wide" : "Unassigned");
    const evRatings = eventRatingMap[ev.id];
    if (evRatings && evRatings.length > 0) {
      if (!teamSatisfaction[team]) teamSatisfaction[team] = { sum: 0, count: 0 };
      evRatings.forEach(r => { teamSatisfaction[team].sum += r; teamSatisfaction[team].count += 1; });
    }
  });
  const teamSatArr = Object.entries(teamSatisfaction)
    .map(([team, d]) => ({ team, avg: (d.sum / d.count).toFixed(1), count: d.count }))
    .sort((a, b) => parseFloat(b.avg) - parseFloat(a.avg));

  const BAR_COLORS = ["#fcd657", "#4fc3f7", "#34d399", "#a78bfa", "#ef5350", "#ce93d8", "#f87171", "#facc15", "#ff7043", "#81c784"];

  function LeaderboardBar({ entries, max, label, color = "#fcd657" }) {
    return (
      <div style={{ padding: "clamp(16px, 3vw, 24px)", border: `1px solid ${color}22`, background: `${color}04` }}>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color, textTransform: "uppercase", marginBottom: 16 }}>{label}</div>
        {entries.length === 0 ? (
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.15)", letterSpacing: 2, textAlign: "center", padding: 20 }}>NO DATA YET</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map((e, i) => (
              <div key={e.profile?.id || i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: `1px solid ${color}33`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {e.profile?.discord_avatar
                    ? <img src={e.profile.discord_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <span style={{ fontFamily: "'Orbitron'", fontSize: 9, color }}>{(e.profile?.discord_username || "?").charAt(0)}</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {e.profile?.discord_username || "Unknown"}
                    </span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", flexShrink: 0 }}>{e.count}</span>
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(e.count / max) * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length], borderRadius: 2, transition: "width 0.5s ease" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Session graphs — side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 24 }}>

        {/* Left: User Visits — bars = unique visitors, line = sessions */}
        <div style={{ padding: "clamp(16px, 3vw, 24px)", border: "1px solid rgba(79,195,247,0.2)", background: "rgba(79,195,247,0.03)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#4fc3f7", textTransform: "uppercase", marginBottom: 16 }}>
            User Visits — Last 30 Days
          </div>
          <svg
            width="100%"
            height="130"
            viewBox="0 0 300 100"
            preserveAspectRatio="none"
            style={{ display: "block", overflow: "visible" }}
          >
            {/* Bars: sessions per day (behind) */}
            {dayValues.map((v, i) => {
              const barH = Math.max((v / maxSessions) * 100, 2);
              return (
                <rect
                  key={i}
                  x={i * 10 + 1}
                  y={100 - barH}
                  width={8}
                  height={barH}
                  fill={v > 0 ? "#f472b6" : "rgba(255,255,255,0.05)"}
                  opacity={v > 0 ? 0.7 : 0.3}
                  rx={0.5}
                >
                  <title>{`${dayLabels[i]}: ${v} sessions`}</title>
                </rect>
              );
            })}
            {/* Line: unique visitors per day (on top) */}
            <polyline
              points={dayVisitors.map((v, i) => `${i * 10 + 5},${100 - Math.max((v / maxSessions) * 100, 0)}`).join(" ")}
              fill="none"
              stroke="#4fc3f7"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {dayVisitors.map((v, i) => (
              <circle
                key={i}
                cx={i * 10 + 5}
                cy={100 - Math.max((v / maxSessions) * 100, 0)}
                r={1.2}
                fill="#4fc3f7"
                vectorEffect="non-scaling-stroke"
              >
                <title>{`${dayLabels[i]}: ${v} unique visitors`}</title>
              </circle>
            ))}
          </svg>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.25)" }}>{dayLabels[0]}</span>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.25)" }}>{dayLabels[dayLabels.length - 1]}</span>
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ display: "inline-block", width: 10, height: 8, background: "#f472b6", borderRadius: 1, opacity: 0.7 }} />
              Total sessions: <span style={{ color: "#f472b6" }}>{dedupedSessions.length}</span>
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ display: "inline-block", width: 16, height: 2, background: "#4fc3f7", borderRadius: 1 }} />
              Unique visitors: <span style={{ color: "#4fc3f7" }}>{new Set(dedupedSessions.map(s => s.user_id)).size}</span>
            </div>
          </div>
        </div>

        {/* Right: Event Satisfaction */}
        <div style={{ padding: "clamp(16px, 3vw, 24px)", border: "1px solid rgba(252,214,87,0.2)", background: "rgba(252,214,87,0.03)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: "uppercase", marginBottom: 16 }}>
            Event Satisfaction
          </div>
          {overallSatisfaction > 0 ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 3 }}>
                  {[1, 2, 3, 4].map(s => (
                    <span key={s} style={{ fontSize: 28, color: s <= Math.round(overallSatisfaction) ? ACCENT : "rgba(255,255,255,0.1)" }}>★</span>
                  ))}
                </div>
                <div>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: ACCENT, lineHeight: 1 }}>{overallSatisfaction}</div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.35)" }}>
                    OVERALL ({ratings.length} rating{ratings.length !== 1 ? "s" : ""})
                  </div>
                </div>
              </div>

              {/* Per-team satisfaction bars */}
              {teamSatArr.length > 0 && (
                <div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 12 }}>
                    By Team / Scope
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {teamSatArr.map((t, i) => {
                      const teamData = (allTeams || []).find(tm => tm.name === t.team);
                      const teamColor = teamData?.color || BAR_COLORS[i % BAR_COLORS.length];
                      return (
                        <div key={t.team} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: teamColor, minWidth: 100, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {t.team}
                          </span>
                          <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${(parseFloat(t.avg) / 4) * 100}%`, background: teamColor, borderRadius: 4, transition: "width 0.5s" }} />
                          </div>
                          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                            {[1, 2, 3, 4].map(s => (
                              <span key={s} style={{ fontSize: 10, color: s <= Math.round(t.avg) ? teamColor : "rgba(255,255,255,0.08)" }}>★</span>
                            ))}
                          </div>
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", minWidth: 20 }}>{t.avg}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.15)", letterSpacing: 2, textAlign: "center", padding: 30 }}>
              NO RATINGS YET — Event ratings will appear here after members rate past events
            </div>
          )}
        </div>

      </div>

      {/* Leaderboards: Event Creators + Activity (two graphs) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 24 }}>
        <LeaderboardBar entries={creatorLeaderboard} max={Math.max(...creatorLeaderboard.map(e => e.count), 1)} label="Top Event Creators" color="#34d399" />
        <LeaderboardBar entries={attendanceLeaderboard} max={maxAttendance} label="Most Active — Event Attendance" color="#a78bfa" />
      </div>

      {/* Visit-based activity */}
      <LeaderboardBar entries={visitLeaderboard} max={maxVisits} label="Most Active — Site Visits" color="#4fc3f7" />

    </div>
  );
}

export default function AdminPage() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  // Initial section from URL hash so a hard refresh on /admin#merits keeps the
  // user on the Merits tab. Default "dashboard" when the hash is missing or unknown.
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === "undefined") return "dashboard";
    const h = (window.location.hash || "").replace(/^#/, "");
    return h || "dashboard";
  });
  // Write the active section into the URL hash so refresh / bookmarking / sharing
  // restores the same view. replaceState avoids polluting the back-button history.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const target = `#${activeSection}`;
    if (window.location.hash !== target) {
      window.history.replaceState(null, "", target);
    }
  }, [activeSection]);
  // React to manual hash edits / hash-jumps from other links so the UI stays in sync.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHash = () => {
      const h = (window.location.hash || "").replace(/^#/, "");
      if (h) setActiveSection(h);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const [stats, setStats] = useState({ members: 0, teams: 0, events: 0, merits: 0, ships: 0, pendingTx: 0 });
  const [tzData, setTzData] = useState([]);
  const [hbData, setHbData] = useState([]);
  // Drill-down state for the distribution donuts. Clicking a legend row opens
  // a "users at this zone/base" panel just like the team page does.
  const [expandedTz, setExpandedTz] = useState(null);   // offset key
  const [expandedHb, setExpandedHb] = useState(null);   // home_base label
  const [salesData, setSalesData] = useState({ merit: 0, marketplace: 0, swag: 0 });

  const { profiles: preloadedProfiles, events: preloadedEvents, teams: preloadedTeams, loaded: preloaded } = useFleetData();
  const { isSuperAdmin, isDivisionLead, isTeamLead } = getPerms(profile);

  useEffect(() => { if (!loading && (!user || !profile?.is_admin)) navigate("/"); }, [user, profile, loading, navigate]);
  useEffect(() => { if (profile?.is_admin) loadStats(); }, [profile?.id]);

  async function loadStats() {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    let pendingTx = null, recentTx = null;
    const [{ data: members }, { data: teams }, { data: events }, { data: merits }, { data: profileShips }, { data: profiles }] = await Promise.all([
      // Count only active members — exclude departed (soft-deleted) and the
      // guest/pending intake statuses so this matches Member Management's tab.
      supabase.from("profiles").select("id, member_status").limit(2000),
      // Count only active Security-division teams for now. Active = not archived;
      // Security = division="Security" OR Security in the divisions array.
      supabase.from("teams").select("id, archived, division, divisions"),
      supabase.from("events").select("id"),
      supabase.from("merits").select("id").limit(1000),
      supabase.from("profiles").select("ships").not("ships", "is", null).limit(2000),
      // Carry identity fields too — the distribution donuts can be clicked to
      // expand into a member chip list, which needs id/username/avatar.
      supabase.from("profiles").select("id, discord_username, discord_avatar, timezone, home_base").limit(2000),
    ]);
    const activeMembers = (members || []).filter(m => !m.member_status || m.member_status === "member");
    const activeSecurityTeams = (teams || []).filter(t =>
      !t.archived && (t.division === "Security" || (Array.isArray(t.divisions) && t.divisions.includes("Security")))
    );
    try {
      const [res1, res2] = await Promise.all([
        supabase.from("store_transactions").select("id").eq("status", "pending"),
        supabase.from("store_transactions").select("store_type, status").gte("created_at", thirtyDaysAgo).in("status", ["confirmed", "pending"]),
      ]);
      pendingTx = res1.data;
      recentTx = res2.data;
    } catch (err) { console.warn('Store tables not available yet:', err.message); }
    // Count unique ships owned by at least one member
    const ownedShipIds = new Set();
    (profileShips || []).forEach(p => {
      const userShips = Array.isArray(p.ships) ? p.ships : [];
      userShips.forEach(entry => {
        if (typeof entry === 'object' && entry !== null && entry.ship_id) ownedShipIds.add(entry.ship_id);
      });
    });
    setStats({
      members: activeMembers.length,
      teams: activeSecurityTeams.length,
      events: events?.length || 0,
      merits: merits?.length || 0,
      ships: ownedShipIds.size,
      pendingTx: pendingTx?.length || 0,
    });
    // Build sales data (last 30 days)
    const salesCount = { merit: 0, marketplace: 0, swag: 0 };
    (recentTx || []).forEach(t => { if (salesCount[t.store_type] !== undefined) salesCount[t.store_type]++; });
    setSalesData(salesCount);
    // Build timezone distribution — group by current UTC offset (so e.g. Toronto
    // and New York merge into one "Eastern" slice) but track contributing cities
    // so the regional breakdown is preserved as a sublabel.
    function getTzOffsetHours(tz) {
      try {
        const date = new Date();
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: false }).formatToParts(date);
        const o = {};
        parts.forEach(part => { o[part.type] = parseInt(part.value, 10); });
        const tzMs = Date.UTC(o.year, o.month - 1, o.day, o.hour === 24 ? 0 : o.hour, o.minute, o.second);
        return Math.round((tzMs - date.getTime()) / 3600000 * 4) / 4;
      } catch { return 0; }
    }
    function offsetLabel(off) {
      // Friendly name where it's unambiguous in the current DST state — falls back to UTC±N.
      const friendly = {
        "-10": "Hawaii", "-9": "Alaska", "-8": "Pacific", "-7": "Mountain", "-6": "Central",
        "-5": "Eastern", "-4": "Atlantic", "-3.5": "Newfoundland", "-3": "Brasília",
        "0": "UTC", "1": "Central European", "2": "Eastern European", "3": "Moscow",
        "5.5": "India", "8": "China", "9": "Japan", "9.5": "Aus. Central", "10": "Aus. Eastern",
      }[String(off)];
      const sign = off >= 0 ? "+" : "−";
      const abs = Math.abs(off);
      const intP = Math.floor(abs);
      const fracP = abs - intP;
      const fracStr = fracP === 0 ? "" : fracP === 0.5 ? ":30" : fracP === 0.75 ? ":45" : fracP === 0.25 ? ":15" : "";
      const utc = `UTC${sign}${intP}${fracStr}`;
      return friendly ? `${friendly} (${utc})` : utc;
    }
    const tzGroups = {}; // offset -> { offset, count, cities, members }
    (profiles || []).forEach(p => {
      if (!p.timezone) return;
      const off = getTzOffsetHours(p.timezone);
      const city = p.timezone.replace(/_/g, " ").replace(/^.*\//, "");
      if (!tzGroups[off]) tzGroups[off] = { offset: off, count: 0, cities: {}, members: [] };
      tzGroups[off].count++;
      tzGroups[off].cities[city] = (tzGroups[off].cities[city] || 0) + 1;
      tzGroups[off].members.push(p);
    });
    const tzArr = Object.values(tzGroups)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
      .map(g => {
        const cities = Object.entries(g.cities).sort((a, b) => b[1] - a[1]);
        const sublabel = cities.length > 1
          ? cities.map(([c, n]) => `${c} · ${n}`).join(", ")
          : (cities.length === 1 ? cities[0][0] : null);
        return { label: offsetLabel(g.offset), count: g.count, sublabel, offset: g.offset, members: g.members };
      });
    setTzData(tzArr);
    // Build home base distribution — same shape upgrade: carry the contributing members.
    const hbMap = {}; // home_base -> { count, members }
    (profiles || []).forEach(p => {
      if (!p.home_base) return;
      if (!hbMap[p.home_base]) hbMap[p.home_base] = { count: 0, members: [] };
      hbMap[p.home_base].count++;
      hbMap[p.home_base].members.push(p);
    });
    const hbArr = Object.entries(hbMap)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 8)
      .map(([label, g]) => ({ label, count: g.count, members: g.members }));
    setHbData(hbArr);
  }

  if (loading) return <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(252,214,87,0.6)" }}>LOADING...</div>;
  if (!user || !profile?.is_admin) return null;

  const SECTIONS = [
    { id: "dashboard", label: "Dashboard", icon: "◈", group: 0 },
    { id: "members", label: "Members", icon: "◉", group: 1 },
    { id: "orgroles", label: "Org Roles", icon: "⬡", group: 1 },
    { id: "divisions", label: "Divisions", icon: "◇", group: 1 },
    { id: "teams", label: "Teams", icon: "◎", group: 1 },
    { id: "homebases", label: "Home Bases", icon: "⬢", group: 2 },
    { id: "fleet", label: "Fleet", icon: "✦", group: 2 },
    { id: "blueprints", label: "Blueprints", icon: "▤", group: 2 },
    { id: "resources", label: "Resources", icon: "◈", group: 2 },
    { id: "events", label: "Events", icon: "◆", group: 3 },
    { id: "merits", label: "Merits", icon: "★", group: 3 },
    { id: "orgmarket", label: "Org Market", icon: "⬡", group: 3 },
    { id: "voiceactivity", label: "Voice Activity", icon: "🎙", group: 3 },
  ];

  const visibleSections = isSuperAdmin ? SECTIONS
    : isDivisionLead ? SECTIONS.filter(s => ["dashboard","members","teams","fleet","divisions","events","homebases","orgmarket","voiceactivity"].includes(s.id))
    : SECTIONS.filter(s => ["dashboard","members"].includes(s.id));

  const permLabels = [
    isSuperAdmin && "SUPER ADMIN",
    isDivisionLead && "DIV LEAD",
    isTeamLead && "TEAM LEAD",
  ].filter(Boolean);

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::selection { background: #fcd657; color: #05070f; } a { text-decoration: none; color: inherit; } .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; } .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #fcd657; transition: width 0.3s ease; } .nav-link:hover { color: #fcd657; } .nav-link:hover::after { width: 100%; } .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); } input, select, textarea { color-scheme: dark; } input:focus, select:focus, textarea:focus { border-color: #fcd657 !important; outline: none; } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); } ::-webkit-scrollbar-thumb { background: rgba(252,214,87,0.3); }`}</style>
      <div className="scanline" />
      <Nav accentColor="#fcd657" mobileSlot={
        <div style={{ position: "relative", display: "inline-block" }}>
          <select
            value={activeSection}
            onChange={e => setActiveSection(e.target.value)}
            style={{
              appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
              fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: 1.5, textTransform: "uppercase",
              background: "rgba(252,214,87,0.12)", color: "#fcd657",
              border: "1px solid #fcd657",
              padding: "8px 28px 8px 14px", cursor: "pointer", maxWidth: "55vw",
            }}
          >
            {visibleSections.map(s => (
              <option key={s.id} value={s.id} style={{ background: "#05070f", color: "#e8eaf6" }}>
                {s.icon}  {s.label}
              </option>
            ))}
          </select>
          <span aria-hidden style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#fcd657", fontSize: 9 }}>▼</span>
        </div>
      } />
      <style>{`
        @media (max-width: 768px) {
          .admin-sidebar { display: none !important; }
          .admin-content { padding: 24px 16px !important; }
          /* Hide the "STARKEEPER" wordmark in the global Nav so the section dropdown
             passed via Nav's mobileSlot can sit in its place. Only applies while
             AdminPage is mounted. */
          .nav-logo-text { display: none !important; }
        }
      `}</style>
      <div style={{ display: "flex", flex: 1, paddingTop: "calc(64px + env(safe-area-inset-top, 0px))" }}>
        <div className="admin-sidebar" style={{ width: 220, flexShrink: 0, borderRight: "1px solid rgba(252,214,87,0.15)", background: "rgba(0,0,0,0.3)", padding: "32px 0", position: "sticky", top: "calc(64px + env(safe-area-inset-top, 0px))", height: "calc(100vh - 64px - env(safe-area-inset-top, 0px))", overflowY: "auto" }}>
          <div style={{ padding: "0 20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 16 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 3, color: "rgba(252,214,87,0.6)", textTransform: "uppercase", marginBottom: 6 }}>Admin Center</div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{profile?.discord_username}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {permLabels.map(l => <span key={l} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "#fcd657", border: "1px solid rgba(252,214,87,0.4)", padding: "1px 5px", background: "rgba(252,214,87,0.08)" }}>{l}</span>)}
            </div>
          </div>
          {visibleSections.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && s.group !== visibleSections[i - 1].group && (
                <div style={{ margin: "8px 20px", borderTop: "1px solid rgba(255,255,255,0.06)" }} />
              )}
              <button onClick={() => setActiveSection(s.id)} style={{ width: "100%", padding: "12px 20px", display: "flex", alignItems: "center", gap: 12, background: activeSection === s.id ? "rgba(252,214,87,0.08)" : "transparent", borderLeft: activeSection === s.id ? "3px solid #fcd657" : "3px solid transparent", border: "none", cursor: "pointer", transition: "all 0.2s", textAlign: "left" }}>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 14, color: activeSection === s.id ? "#fcd657" : "rgba(232,234,246,0.3)" }}>{s.icon}</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, color: activeSection === s.id ? "#fcd657" : "rgba(232,234,246,0.5)", textTransform: "uppercase" }}>{s.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
        <div className="admin-content" style={{ flex: 1, padding: "48px 48px", overflowY: "auto", minWidth: 0 }}>
          {activeSection === "dashboard" && (
            <div>
              <div style={{ marginBottom: 40 }}>
                <SectionLabel>Welcome Back</SectionLabel>
                <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 4vw, 48px)", color: "#e8eaf6", marginBottom: 8 }}>Admin <span style={{ color: ACCENT }}>Center</span></h1>
                <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 17, color: "rgba(232,234,246,0.45)" }}>Manage Starkeeper Industries from one place.</p>
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 48 }}>
                <StatCard label="Members" value={stats.members} color="#fcd657" />
                <StatCard label="Teams" value={stats.teams} color="#4fc3f7" />
                <StatCard label="Events" value={stats.events} color="#f87171" />
                <StatCard label="Ships" value={stats.ships} color="#ce93d8" />
                <StatCard label="Merits Awarded" value={stats.merits} color="#34d399" />
                <StatCard label="Pending Orders" value={stats.pendingTx} color={stats.pendingTx > 0 ? "#ef5350" : "#a78bfa"} />
              </div>

              {/* Live aUEC inflation tracker from UEX Corp's API */}
              <UexCurrencyWidget />

              {/* Sales Charts (last 30 days) */}
              {(salesData.merit > 0 || salesData.marketplace > 0 || salesData.swag > 0) && (() => {
                const SALE_COLORS = { merit: "#fcd657", marketplace: "#4fc3f7", swag: "#34d399" };
                const SALE_LABELS = { merit: "Merit Store", marketplace: "User-to-User", swag: "Merch Store" };
                const entries = Object.entries(salesData).filter(([, v]) => v > 0);
                const total = entries.reduce((s, [, v]) => s + v, 0);
                let cumAngle = 0;
                const slices = entries.map(([key, count]) => {
                  const angle = (count / total) * 360;
                  const startAngle = cumAngle; cumAngle += angle;
                  return { label: SALE_LABELS[key], count, color: SALE_COLORS[key], startAngle, angle };
                });
                function polarToCart(cx, cy, r, deg) { const rad = (deg - 90) * Math.PI / 180; return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }; }
                return (
                  <div style={{ padding: 24, border: "1px solid rgba(167,139,250,0.15)", background: "rgba(167,139,250,0.03)", marginBottom: 48 }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#a78bfa", textTransform: "uppercase", marginBottom: 16 }}>Sales — Last 30 Days</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                      <svg width="140" height="140" viewBox="0 0 140 140">
                        {slices.map((s, i) => {
                          if (s.angle >= 359.9) return <circle key={i} cx="70" cy="70" r="60" fill={s.color} />;
                          const s1 = polarToCart(70, 70, 60, s.startAngle);
                          const s2 = polarToCart(70, 70, 60, s.startAngle + s.angle);
                          const large = s.angle > 180 ? 1 : 0;
                          return <path key={i} d={`M70,70 L${s1.x},${s1.y} A60,60 0 ${large},1 ${s2.x},${s2.y} Z`} fill={s.color} opacity={0.85} />;
                        })}
                        <circle cx="70" cy="70" r="30" fill="#05070f" />
                        <text x="70" y="70" textAnchor="middle" dominantBaseline="middle" fill="#e8eaf6" fontFamily="Orbitron" fontSize="14" fontWeight="900">{total}</text>
                      </svg>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {slices.map((s, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#e8eaf6" }}>{s.label}</span>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)" }}>({s.count})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Timezone & Home Base Charts */}
              {(tzData.length > 0 || hbData.length > 0) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 24, marginBottom: 48 }}>
                  {tzData.length > 0 && (() => {
                    const PIE_COLORS = ["#fcd657","#4fc3f7","#ef5350","#34d399","#a78bfa","#ce93d8","#f87171","#facc15"];
                    const total = tzData.reduce((s, d) => s + d.count, 0);
                    let cumAngle = 0;
                    const slices = tzData.map((d, i) => {
                      const angle = (d.count / total) * 360;
                      const startAngle = cumAngle;
                      cumAngle += angle;
                      // CRITICAL: forward offset + members so the click handler
                      // and DistributionMembersPanel have what they need. Missing
                      // `offset` makes every slice's offset === undefined, which
                      // causes `s.offset === expandedTz` to match every row at once.
                      return { label: d.label, sublabel: d.sublabel, count: d.count, offset: d.offset, members: d.members, color: PIE_COLORS[i % PIE_COLORS.length], startAngle, angle };
                    });
                    function polarToCart(cx, cy, r, deg) {
                      const rad = (deg - 90) * Math.PI / 180;
                      return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
                    }
                    const activeSlice = slices.find(s => s.offset === expandedTz) || null;
                    return (
                      <div style={{ padding: 24, border: "1px solid rgba(252,214,87,0.15)", background: "rgba(252,214,87,0.03)" }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: "uppercase", marginBottom: 16 }}>Timezone Distribution</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                          <svg width="140" height="140" viewBox="0 0 140 140">
                            {slices.map((s, i) => {
                              if (s.angle >= 359.9) return <circle key={i} cx="70" cy="70" r="60" fill={s.color} />;
                              const s1 = polarToCart(70, 70, 60, s.startAngle);
                              const s2 = polarToCart(70, 70, 60, s.startAngle + s.angle);
                              const large = s.angle > 180 ? 1 : 0;
                              return <path key={i} d={`M70,70 L${s1.x},${s1.y} A60,60 0 ${large},1 ${s2.x},${s2.y} Z`} fill={s.color} opacity={s.offset === expandedTz ? 1 : 0.85} stroke={s.offset === expandedTz ? "#e8eaf6" : "none"} strokeWidth={s.offset === expandedTz ? 1 : 0} style={{ cursor: "pointer" }} onClick={() => setExpandedTz(s.offset === expandedTz ? null : s.offset)} />;
                            })}
                            <circle cx="70" cy="70" r="30" fill="#05070f" />
                            <text x="70" y="70" textAnchor="middle" dominantBaseline="middle" fill="#e8eaf6" fontFamily="Orbitron" fontSize="14" fontWeight="900">{total}</text>
                          </svg>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {slices.map((s, i) => {
                              const isActive = s.offset === expandedTz;
                              return (
                                <button key={i} type="button" onClick={() => setExpandedTz(isActive ? null : s.offset)}
                                  style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 8px", background: isActive ? `${s.color}15` : "transparent", border: `1px solid ${isActive ? s.color : "transparent"}`, cursor: "pointer", textAlign: "left" }}>
                                  <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0, marginTop: 4 }} />
                                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#e8eaf6" }}>{s.label}</span>
                                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)" }}>({s.count})</span>
                                    </div>
                                    {s.sublabel && (
                                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", letterSpacing: 0.5, marginTop: 1 }}>{s.sublabel}</div>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {activeSlice && (
                          <DistributionMembersPanel
                            label={activeSlice.label}
                            members={activeSlice.members || []}
                            color={activeSlice.color}
                            onClose={() => setExpandedTz(null)}
                          />
                        )}
                      </div>
                    );
                  })()}
                  {hbData.length > 0 && (() => {
                    const PIE_COLORS = ["#4fc3f7","#34d399","#a78bfa","#fcd657","#ef5350","#ce93d8","#f87171","#facc15"];
                    const total = hbData.reduce((s, d) => s + d.count, 0);
                    let cumAngle = 0;
                    const slices = hbData.map((d, i) => {
                      const angle = (d.count / total) * 360;
                      const startAngle = cumAngle;
                      cumAngle += angle;
                      return { label: d.label, count: d.count, members: d.members, color: PIE_COLORS[i % PIE_COLORS.length], startAngle, angle };
                    });
                    function polarToCart(cx, cy, r, deg) {
                      const rad = (deg - 90) * Math.PI / 180;
                      return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
                    }
                    const activeSlice = slices.find(s => s.label === expandedHb) || null;
                    return (
                      <div style={{ padding: 24, border: "1px solid rgba(79,195,247,0.15)", background: "rgba(79,195,247,0.03)" }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#4fc3f7", textTransform: "uppercase", marginBottom: 16 }}>Home Base Distribution</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                          <svg width="140" height="140" viewBox="0 0 140 140">
                            {slices.map((s, i) => {
                              if (s.angle >= 359.9) return <circle key={i} cx="70" cy="70" r="60" fill={s.color} />;
                              const s1 = polarToCart(70, 70, 60, s.startAngle);
                              const s2 = polarToCart(70, 70, 60, s.startAngle + s.angle);
                              const large = s.angle > 180 ? 1 : 0;
                              return <path key={i} d={`M70,70 L${s1.x},${s1.y} A60,60 0 ${large},1 ${s2.x},${s2.y} Z`} fill={s.color} opacity={s.label === expandedHb ? 1 : 0.85} stroke={s.label === expandedHb ? "#e8eaf6" : "none"} strokeWidth={s.label === expandedHb ? 1 : 0} style={{ cursor: "pointer" }} onClick={() => setExpandedHb(s.label === expandedHb ? null : s.label)} />;
                            })}
                            <circle cx="70" cy="70" r="30" fill="#05070f" />
                            <text x="70" y="70" textAnchor="middle" dominantBaseline="middle" fill="#e8eaf6" fontFamily="Orbitron" fontSize="14" fontWeight="900">{total}</text>
                          </svg>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {slices.map((s, i) => {
                              const isActive = s.label === expandedHb;
                              return (
                                <button key={i} type="button" onClick={() => setExpandedHb(isActive ? null : s.label)}
                                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", background: isActive ? `${s.color}15` : "transparent", border: `1px solid ${isActive ? s.color : "transparent"}`, cursor: "pointer", textAlign: "left" }}>
                                  <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#e8eaf6" }}>{s.label}</span>
                                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)" }}>({s.count})</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {activeSlice && (
                          <DistributionMembersPanel
                            label={activeSlice.label}
                            members={activeSlice.members || []}
                            color={activeSlice.color}
                            onClose={() => setExpandedHb(null)}
                          />
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* Analytics Section — Super Admin Only */}
              {isSuperAdmin && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ width: 40, height: 2, background: "#4fc3f7" }} />
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "#4fc3f7", textTransform: "uppercase" }}>Analytics</span>
                    <div style={{ flex: 1, height: 1, background: "rgba(79,195,247,0.15)" }} />
                  </div>
                  <AnalyticsDashboard
                    profiles={preloaded ? preloadedProfiles : []}
                    events={preloaded ? preloadedEvents : []}
                    teams={preloaded ? preloadedTeams : []}
                  />
                </div>
              )}

              {/* Voice Activity Snapshot — Super Admin Only */}
              {isSuperAdmin && (
                <div style={{ marginTop: 36 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ width: 40, height: 2, background: "#fcd657" }} />
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: "#fcd657", textTransform: "uppercase" }}>Voice Activity</span>
                    <div style={{ flex: 1, height: 1, background: "rgba(252,214,87,0.15)" }} />
                    <button onClick={() => setActiveSection("voiceactivity")} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "#fcd657", background: "transparent", border: "1px solid rgba(252,214,87,0.4)", padding: "5px 10px", cursor: "pointer", textTransform: "uppercase" }}>View All →</button>
                  </div>
                  <VoiceActivitySnapshot />
                </div>
              )}
            </div>
          )}
          {activeSection === "members" && <MemberManagement profile={profile} />}
          {activeSection === "teams" && <TeamBuilder adminProfile={profile} />}
          {activeSection === "fleet" && <FleetManager />}
          {activeSection === "blueprints" && <BlueprintManager />}
          {activeSection === "divisions" && <DivisionView />}
          {activeSection === "orgroles" && <OrgRoleManager />}
          {activeSection === "events" && <EventManager />}
          {activeSection === "merits" && <MeritSystem />}
          {activeSection === "homebases" && <HomeBaseManager />}
          {activeSection === "resources" && <ResourceCategoryManager />}
          {activeSection === "orgmarket" && <OrgMarketManager profile={profile} />}
          {activeSection === "voiceactivity" && <VoiceActivityManager profile={profile} />}
        </div>
      </div>
    </div>
  );
}

// ─── Org Market Manager (admin) ───────────────────────────────────────────────
function OrgMarketManager({ profile }) {
  const [subTab, setSubTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [sections, setSections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [storeAdmins, setStoreAdmins] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Market settings (sales channel, share channels, etc.)
  const [marketAllChannels, setMarketAllChannels] = useState([]);
  const [salesChannelId, setSalesChannelId] = useState("");
  const [salesChannelSaving, setSalesChannelSaving] = useState(false);
  const [salesChannelSaved, setSalesChannelSaved] = useState(false);
  const [marketChannelSearch, setMarketChannelSearch] = useState("");
  // Three parallel share-channel settings (Marketplace / Bounty / Org Store)
  const [shareGroups, setShareGroups] = useState({
    marketplace: { ids: [], saving: false, saved: false, search: "" },
    bounty:      { ids: [], saving: false, saved: false, search: "" },
    orgstore:    { ids: [], saving: false, saved: false, search: "" },
  });
  function patchShareGroup(key, patch) {
    setShareGroups(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  // Form states
  const [showListingForm, setShowListingForm] = useState(false);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [listingForm, setListingForm] = useState({ store_type: "merit", section_id: "", title: "", description: "", image_url: "", accepts_merits: true, accepts_auec: false, accepts_cash: false, merit_price: "", auec_price: "", cash_price: "", is_permanent: false, quantity: "1", sc_item_id: null, sc_item_source_url: "" });
  const [listingPickerOpen, setListingPickerOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ store_type: "merit", name: "", description: "", sort_order: 0 });
  const [adminForm, setAdminForm] = useState({ user_id: "", store_type: "merit" });
  const [saving, setSaving] = useState(false);
  const [filterStoreType, setFilterStoreType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [txFilterStatus, setTxFilterStatus] = useState("");

  useEffect(() => { loadAll(); loadMarketSettings(); }, []);

  async function loadAll() {
    setLoading(true);
    const [{ data: l }, { data: s }, { data: t }, { data: a }, { data: m }] = await Promise.all([
      supabase.from("store_listings").select("*, profiles!store_listings_listed_by_fkey(discord_username)").order("created_at", { ascending: false }),
      supabase.from("store_sections").select("*").order("sort_order"),
      supabase.from("store_transactions").select("*, buyer:profiles!store_transactions_buyer_id_fkey(discord_username), seller:profiles!store_transactions_seller_id_fkey(discord_username)").order("created_at", { ascending: false }),
      supabase.from("store_admins").select("*, profiles!store_admins_user_id_fkey(discord_username, discord_avatar)").order("created_at"),
      supabase.from("profiles").select("id, discord_username, discord_avatar, org_role_sort").order("discord_username"),
    ]);
    setListings(l || []); setSections(s || []); setTransactions(t || []); setStoreAdmins(a || []); setMembers(m || []);
    setLoading(false);
  }

  async function loadMarketSettings() {
    try {
      const chRes = await fetch("/api/discord-channels");
      if (chRes.ok) { const d = await chRes.json(); setMarketAllChannels(d.channels || []); }
    } catch (err) { console.warn("Failed to load channels:", err.message); }
    try {
      const { data } = await supabase.from("app_settings").select("key, value").eq("key", "marketplace_sales_channel").single();
      if (data?.value) {
        // Tolerate both plain string and JSON-quoted string
        let v = data.value;
        try { const parsed = JSON.parse(v); if (typeof parsed === "string") v = parsed; } catch { /* not JSON, use as-is */ }
        setSalesChannelId(v);
      }
    } catch (err) { /* ignore missing row */ }
    // Load all three share-channel lists in parallel
    const SHARE_KEYS = { marketplace: "marketplace_share_channels", bounty: "bounty_share_channels", orgstore: "orgstore_share_channels" };
    for (const [groupKey, settingKey] of Object.entries(SHARE_KEYS)) {
      try {
        const { data } = await supabase.from("app_settings").select("key, value").eq("key", settingKey).single();
        if (data?.value) {
          try {
            const parsed = JSON.parse(data.value);
            if (Array.isArray(parsed)) patchShareGroup(groupKey, { ids: parsed.filter(Boolean) });
          } catch { /* ignore parse error */ }
        }
      } catch (err) { /* ignore missing row */ }
    }
  }

  async function saveSalesChannel() {
    setSalesChannelSaving(true);
    try {
      await supabase.from("app_settings").upsert({ key: "marketplace_sales_channel", value: salesChannelId || "" }, { onConflict: "key" });
      setSalesChannelSaved(true);
      setTimeout(() => setSalesChannelSaved(false), 2000);
    } catch (err) { console.error("Failed to save sales channel:", err); }
    setSalesChannelSaving(false);
  }

  const SHARE_GROUP_SETTING_KEYS = {
    marketplace: "marketplace_share_channels",
    bounty: "bounty_share_channels",
    orgstore: "orgstore_share_channels",
  };

  async function saveShareChannels(groupKey) {
    const group = shareGroups[groupKey];
    if (!group) return;
    patchShareGroup(groupKey, { saving: true });
    try {
      await supabase.from("app_settings").upsert(
        { key: SHARE_GROUP_SETTING_KEYS[groupKey], value: JSON.stringify(group.ids) },
        { onConflict: "key" },
      );
      patchShareGroup(groupKey, { saving: false, saved: true });
      setTimeout(() => patchShareGroup(groupKey, { saved: false }), 2000);
    } catch (err) {
      console.error(`Failed to save ${groupKey} share channels:`, err);
      patchShareGroup(groupKey, { saving: false });
    }
  }

  function toggleShareChannel(groupKey, id) {
    const group = shareGroups[groupKey];
    const ids = group.ids.includes(id) ? group.ids.filter(c => c !== id) : [...group.ids, id];
    patchShareGroup(groupKey, { ids });
  }

  // ─── Listing CRUD ────────────────────────────────────────────────────
  async function saveListing() {
    if (!listingForm.title.trim()) return;
    setSaving(true);
    const payload = {
      store_type: listingForm.store_type, section_id: listingForm.section_id || null,
      title: listingForm.title.trim(), description: listingForm.description.trim(), image_url: listingForm.image_url,
      sc_item_id: listingForm.sc_item_id || null,
      accepts_merits: listingForm.accepts_merits, accepts_auec: listingForm.accepts_auec, accepts_cash: listingForm.accepts_cash,
      merit_price: listingForm.accepts_merits ? parseInt(listingForm.merit_price) || null : null,
      auec_price: listingForm.accepts_auec ? parseInt(listingForm.auec_price) || null : null,
      cash_price: listingForm.accepts_cash ? parseFloat(listingForm.cash_price) || null : null,
      is_permanent: listingForm.is_permanent, quantity: listingForm.is_permanent ? null : parseInt(listingForm.quantity) || 1,
      listed_by: profile.id, updated_at: new Date().toISOString(),
    };
    if (editingListing) {
      await supabase.from("store_listings").update(payload).eq("id", editingListing);
    } else {
      await supabase.from("store_listings").insert([payload]);
    }
    setSaving(false); setShowListingForm(false); setEditingListing(null);
    setListingForm({ store_type: "merit", section_id: "", title: "", description: "", image_url: "", accepts_merits: true, accepts_auec: false, accepts_cash: false, merit_price: "", auec_price: "", cash_price: "", is_permanent: false, quantity: "1", sc_item_id: null, sc_item_source_url: "" });
    loadAll();
  }

  async function removeListing(id) {
    if (!window.confirm("Remove this listing?")) return;
    await supabase.from("store_listings").update({ status: "removed" }).eq("id", id);
    loadAll();
  }

  function startEditListing(l) {
    setEditingListing(l.id);
    setListingForm({
      store_type: l.store_type, section_id: l.section_id || "", title: l.title, description: l.description || "",
      image_url: l.image_url || "", accepts_merits: l.accepts_merits, accepts_auec: l.accepts_auec, accepts_cash: l.accepts_cash,
      merit_price: l.merit_price || "", auec_price: l.auec_price || "", cash_price: l.cash_price || "",
      is_permanent: l.is_permanent, quantity: l.quantity || "1",
      sc_item_id: l.sc_item_id || null, sc_item_source_url: "",
    });
    setShowListingForm(true);
    // If this listing is already linked to a catalog item, fetch the wiki link
    // so the chip can render it.
    if (l.sc_item_id) {
      supabase.from("sc_items").select("source_url").eq("id", l.sc_item_id).maybeSingle()
        .then(({ data }) => { if (data?.source_url) setListingForm(f => ({ ...f, sc_item_source_url: data.source_url })); });
    }
  }

  // Convenience auto-fill from SC Items catalog. Everything stays editable.
  function applyListingCatalogPick(item) {
    if (!item) return;
    setListingForm(f => ({
      ...f,
      title: item.name || f.title,
      description: item.description || f.description,
      image_url: item.image_url || f.image_url,
      sc_item_id: item.id,
      sc_item_source_url: item.source_url || "",
    }));
    setListingPickerOpen(false);
  }

  // ─── Section CRUD ────────────────────────────────────────────────────
  async function saveSection() {
    if (!sectionForm.name.trim()) return;
    setSaving(true);
    if (editingSection) {
      await supabase.from("store_sections").update({ name: sectionForm.name.trim(), description: sectionForm.description.trim(), sort_order: parseFloat(sectionForm.sort_order) || 0, updated_at: new Date().toISOString() }).eq("id", editingSection);
    } else {
      await supabase.from("store_sections").insert([{ store_type: sectionForm.store_type, name: sectionForm.name.trim(), description: sectionForm.description.trim(), sort_order: parseFloat(sectionForm.sort_order) || 0, created_by: profile.id }]);
    }
    setSaving(false); setShowSectionForm(false); setEditingSection(null);
    setSectionForm({ store_type: "merit", name: "", description: "", sort_order: 0 });
    loadAll();
  }

  async function deleteSection(id) {
    if (!window.confirm("Delete this section?")) return;
    await supabase.from("store_sections").delete().eq("id", id);
    loadAll();
  }

  // ─── Store Admin CRUD ────────────────────────────────────────────────
  async function addStoreAdmin() {
    if (!adminForm.user_id) return;
    setSaving(true);
    await supabase.from("store_admins").insert([{ user_id: adminForm.user_id, store_type: adminForm.store_type }]);
    setSaving(false); setShowAdminForm(false); setAdminForm({ user_id: "", store_type: "merit" });
    loadAll();
  }

  async function removeStoreAdmin(id) {
    await supabase.from("store_admins").delete().eq("id", id);
    loadAll();
  }

  // ─── Transaction management ──────────────────────────────────────────
  async function updateTransaction(txId, newStatus) {
    const updates = { status: newStatus, updated_at: new Date().toISOString() };
    if (newStatus === "confirmed" || newStatus === "cancelled") updates.completed_at = new Date().toISOString();
    await supabase.from("store_transactions").update(updates).eq("id", txId);

    // If cancelled and payment was merits, the merit_balances view will auto-recalc
    // If confirmed, merits are consumed (view handles this)
    loadAll();
  }

  // ─── Filtered data ───────────────────────────────────────────────────
  const filteredListings = listings.filter(l => {
    if (filterStoreType && l.store_type !== filterStoreType) return false;
    if (filterStatus && l.status !== filterStatus) return false;
    return true;
  });

  const filteredTransactions = transactions.filter(t => {
    if (txFilterStatus && t.status !== txFilterStatus) return false;
    return true;
  });

  const pendingCount = transactions.filter(t => t.status === "pending").length;

  // ─── Image upload helper ─────────────────────────────────────────────
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const listingFileRef = React.useRef(null);
  const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/avif"];

  async function uploadListingFile(file) {
    if (!file || !ACCEPTED_TYPES.includes(file.type)) return;
    setUploading(true);
    const ext = file.name.split(".").pop().toLowerCase();
    const path = `admin/${Date.now()}.${ext}`;
    const optFile = await optimizeImageFile(file, { maxDim: 1200, quality: 0.85 });
    const { error } = await supabase.storage.from("store-media").upload(path, optFile, { contentType: optFile.type || file.type });
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from("store-media").getPublicUrl(path);
      setListingForm(f => ({ ...f, image_url: publicUrl }));
    }
    setUploading(false);
  }

  function handleImageUpload(e) { uploadListingFile(e.target.files?.[0]); }
  function handleListingDrop(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); const file = e.dataTransfer.files?.[0]; if (file && ACCEPTED_TYPES.includes(file.type)) uploadListingFile(file); }
  function handleListingDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragOver(true); }
  function handleListingDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragOver(false); }

  const SUB_TABS = [
    { id: "listings", label: "Listings", count: listings.filter(l => l.status !== "removed").length },
    { id: "transactions", label: "Transactions", count: pendingCount > 0 ? pendingCount : null, highlight: pendingCount > 0 },
    { id: "sections", label: "Sections", count: sections.length },
    { id: "admins", label: "Store Admins", count: storeAdmins.length },
    { id: "settings", label: "Settings", count: null },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 3, color: `${ACCENT}55` }}>LOADING...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <SectionLabel>Admin</SectionLabel>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 20, letterSpacing: 3, color: "#e8eaf6", marginBottom: 4 }}>Org Market</h2>
        </div>
        {pendingCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", background: "rgba(239,83,80,0.08)", border: "1px solid rgba(239,83,80,0.4)", cursor: "pointer" }} onClick={() => { setSubTab("transactions"); setTxFilterStatus("pending"); }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 22, color: "#ef5350" }}>{pendingCount}</div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#ef5350", textTransform: "uppercase" }}>Pending<br/>Transactions</div>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
        {SUB_TABS.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={{
            padding: "12px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
            background: subTab === st.id ? `${ACCENT}08` : "transparent",
            borderBottom: subTab === st.id ? `2px solid ${ACCENT}` : "2px solid transparent",
            border: "none", borderBottomStyle: "solid", borderBottomWidth: 2, borderBottomColor: subTab === st.id ? ACCENT : "transparent",
            color: subTab === st.id ? ACCENT : "rgba(232,234,246,0.4)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}>
            {st.label}
            {st.count != null && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, padding: "1px 6px", background: st.highlight ? "rgba(239,83,80,0.2)" : `${ACCENT}15`, color: st.highlight ? "#ef5350" : ACCENT, border: `1px solid ${st.highlight ? "rgba(239,83,80,0.4)" : `${ACCENT}33`}` }}>{st.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ─── LISTINGS TAB ─── */}
      {subTab === "listings" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <select value={filterStoreType} onChange={e => setFilterStoreType(e.target.value)} style={selectStyle}>
              <option value="">All Store Types</option>
              <option value="merit">Merit Items</option>
              <option value="marketplace">Marketplace</option>
              <option value="bounty">Bounties</option>
              <option value="swag">Swag Store</option>
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="sold_out">Sold Out</option>
              <option value="removed">Removed</option>
            </select>
            <button onClick={() => { setShowListingForm(true); setEditingListing(null); setListingForm({ store_type: "merit", section_id: "", title: "", description: "", image_url: "", accepts_merits: true, accepts_auec: false, accepts_cash: false, merit_price: "", auec_price: "", cash_price: "", is_permanent: false, quantity: "1", sc_item_id: null, sc_item_source_url: "" }); }} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}15`, border: `1px solid ${ACCENT}55`, color: ACCENT, cursor: "pointer", marginLeft: "auto" }}>+ New Listing</button>
          </div>

          {showListingForm && (
            <div style={{ padding: 20, border: `1px solid ${ACCENT}33`, background: `${ACCENT}06`, marginBottom: 24 }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: "uppercase", marginBottom: 16 }}>{editingListing ? "Edit Listing" : "New Listing"}</div>

              {/* SC Items catalog convenience picker — auto-fills title/description/image, fully editable after */}
              <div style={{ marginBottom: 12 }}>
                {listingForm.sc_item_id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${ACCENT}44`, background: `${ACCENT}10` }}>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, color: ACCENT, textTransform: "uppercase" }}>SC Item</span>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{listingForm.title || "(no title)"}</span>
                    {listingForm.sc_item_source_url && (
                      <a href={listingForm.sc_item_source_url} target="_blank" rel="noreferrer" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: ACCENT, textTransform: "uppercase", textDecoration: "none" }}>Wiki ↗</a>
                    )}
                    <button type="button" onClick={() => setListingForm(f => ({ ...f, sc_item_id: null, sc_item_source_url: "" }))} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, padding: "4px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.5)", textTransform: "uppercase", cursor: "pointer" }}>Unlink</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setListingPickerOpen(true)} style={{ width: "100%", padding: "10px 12px", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.8, textAlign: "left", background: "transparent", border: `1px dashed ${ACCENT}55`, color: `${ACCENT}cc`, textTransform: "uppercase", cursor: "pointer" }}>
                    + Pick from SC Items Catalog (optional)
                  </button>
                )}
                <ItemPicker open={listingPickerOpen} onClose={() => setListingPickerOpen(false)} onSelect={applyListingCatalogPick} accentColor={ACCENT} title="Link to Catalog Item" />
              </div>

              {/* UEX suggested pricing — appears once a catalog item is linked.
                  Tier clicks fill auec_price (and ensure accepts_auec is on). */}
              {listingForm.sc_item_id && listingForm.title && (
                <div style={{ marginBottom: 12 }}>
                  <SuggestedPricing
                    itemName={listingForm.title}
                    onApply={(auec) => setListingForm(f => ({ ...f, accepts_auec: true, auec_price: String(auec) }))}
                  />
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ gridColumn: "1 / -1" }}><input value={listingForm.title} onChange={e => setListingForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" style={inputStyle} /></div>
                <div style={{ gridColumn: "1 / -1" }}><textarea value={listingForm.description} onChange={e => setListingForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" rows={2} style={{ ...inputStyle, resize: "vertical" }} /></div>
                <select value={listingForm.store_type} onChange={e => setListingForm(f => ({ ...f, store_type: e.target.value }))} style={selectStyle}>
                  <option value="merit">Merit Items</option>
                  <option value="swag">Swag Store</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="bounty">Bounty</option>
                </select>
                <select value={listingForm.section_id} onChange={e => setListingForm(f => ({ ...f, section_id: e.target.value }))} style={selectStyle}>
                  <option value="">No Section</option>
                  {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: `${ACCENT}66`, marginBottom: 6 }}>IMAGE</div>
                  <input ref={listingFileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/avif" onChange={handleImageUpload} style={{ display: "none" }} />
                  {listingForm.image_url ? (
                    <div style={{ display: "inline-flex", position: "relative", width: 80, height: 80, border: `1px solid ${ACCENT}33`, background: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={() => listingFileRef.current?.click()}>
                      <img src={listingForm.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "#fff", letterSpacing: 1 }}>CHANGE</span>
                      </div>
                      <button onClick={e => { e.stopPropagation(); setListingForm(f => ({ ...f, image_url: "" })); }} style={{ position: "absolute", top: -6, right: -6, width: 16, height: 16, borderRadius: "50%", background: "#ef5350", border: "none", color: "#fff", fontSize: 9, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>×</button>
                    </div>
                  ) : (
                    <div
                      onDragOver={handleListingDragOver} onDragLeave={handleListingDragLeave} onDrop={handleListingDrop}
                      onClick={() => listingFileRef.current?.click()}
                      style={{ border: `2px dashed ${dragOver ? ACCENT : ACCENT + "44"}`, background: dragOver ? `${ACCENT}10` : "rgba(255,255,255,0.02)", padding: "20px 16px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
                    >
                      <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, color: `${ACCENT}${dragOver ? "" : "44"}`, marginBottom: 6 }}>⬡</div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: dragOver ? ACCENT : "rgba(232,234,246,0.4)", marginBottom: 2 }}>
                        {uploading ? "Uploading..." : dragOver ? "Drop here" : "Drag & drop or click to upload"}
                      </div>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", letterSpacing: 1 }}>PNG, JPG, GIF, WebP</div>
                    </div>
                  )}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: `${ACCENT}66`, marginBottom: 6 }}>PAYMENT OPTIONS</div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}><input type="checkbox" checked={listingForm.accepts_merits} onChange={e => setListingForm(f => ({ ...f, accepts_merits: e.target.checked }))} /><span style={{ fontFamily: "'Rajdhani'", fontSize: 14, color: "#fcd657" }}>Merits</span></label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}><input type="checkbox" checked={listingForm.accepts_auec} onChange={e => setListingForm(f => ({ ...f, accepts_auec: e.target.checked }))} /><span style={{ fontFamily: "'Rajdhani'", fontSize: 14, color: "#4fc3f7" }}>aUEC</span></label>
                    {(listingForm.store_type === "swag") && <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}><input type="checkbox" checked={listingForm.accepts_cash} onChange={e => setListingForm(f => ({ ...f, accepts_cash: e.target.checked }))} /><span style={{ fontFamily: "'Rajdhani'", fontSize: 14, color: "#34d399" }}>Cash</span></label>}
                  </div>
                </div>
                {listingForm.accepts_merits && <input type="number" value={listingForm.merit_price} onChange={e => setListingForm(f => ({ ...f, merit_price: e.target.value }))} placeholder="Merit Price" style={inputStyle} />}
                {listingForm.accepts_auec && <input type="number" value={listingForm.auec_price} onChange={e => setListingForm(f => ({ ...f, auec_price: e.target.value }))} placeholder="aUEC Price" style={inputStyle} />}
                {listingForm.accepts_cash && <input type="number" step="0.01" value={listingForm.cash_price} onChange={e => setListingForm(f => ({ ...f, cash_price: e.target.value }))} placeholder="Cash Price ($)" style={inputStyle} />}
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}><input type="radio" checked={!listingForm.is_permanent} onChange={() => setListingForm(f => ({ ...f, is_permanent: false }))} /><span style={{ fontFamily: "'Rajdhani'", fontSize: 14, color: "#e8eaf6" }}>Limited Qty</span></label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}><input type="radio" checked={listingForm.is_permanent} onChange={() => setListingForm(f => ({ ...f, is_permanent: true }))} /><span style={{ fontFamily: "'Rajdhani'", fontSize: 14, color: "#e8eaf6" }}>Permanent</span></label>
                    {!listingForm.is_permanent && <input type="number" value={listingForm.quantity} onChange={e => setListingForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Qty" style={{ ...inputStyle, maxWidth: 80 }} min="1" />}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={saveListing} disabled={saving || !listingForm.title.trim()} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", opacity: !listingForm.title.trim() ? 0.4 : 1 }}>{editingListing ? "Save Changes" : "Create Listing"}</button>
                <button onClick={() => { setShowListingForm(false); setEditingListing(null); }} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "10px 16px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredListings.map(l => (
              <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", border: `1px solid ${l.status === "sold_out" ? "rgba(239,83,80,0.15)" : l.status === "removed" ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.08)"}`, background: l.status === "removed" ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.02)", opacity: l.status === "removed" ? 0.5 : 1 }}>
                <div style={{ width: 48, height: 48, flexShrink: 0, overflow: "hidden", background: "rgba(0,0,0,0.3)" }}>
                  {l.image_url ? <img src={l.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 14, color: `${ACCENT}20` }}>◇</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: "#e8eaf6", letterSpacing: 0.5 }}>{l.title}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "1px 6px", border: `1px solid ${ACCENT}33`, color: ACCENT }}>{l.store_type.toUpperCase()}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: l.status === "active" ? "#34d399" : l.status === "sold_out" ? "#ef5350" : "rgba(255,255,255,0.3)" }}>{l.status.toUpperCase()}</span>
                    {l.profiles?.discord_username && <span style={{ fontFamily: "'Rajdhani'", fontSize: 12, color: "rgba(232,234,246,0.35)" }}>by {l.profiles.discord_username}</span>}
                    {!l.is_permanent && l.quantity != null && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>{l.quantity_sold}/{l.quantity} sold</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => startEditListing(l)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", padding: "6px 12px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer" }}>Edit</button>
                  {l.status !== "removed" && <button onClick={() => removeListing(l.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", padding: "6px 12px", background: "none", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer" }}>Remove</button>}
                </div>
              </div>
            ))}
            {filteredListings.length === 0 && <div style={{ textAlign: "center", padding: 40, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO LISTINGS FOUND</div>}
          </div>
        </div>
      )}

      {/* ─── TRANSACTIONS TAB ─── */}
      {subTab === "transactions" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <select value={txFilterStatus} onChange={e => setTxFilterStatus(e.target.value)} style={selectStyle}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredTransactions.map(t => (
              <div key={t.id} style={{ padding: "16px 20px", border: `1px solid ${t.status === "pending" ? "rgba(252,214,87,0.25)" : t.status === "confirmed" ? "rgba(52,211,153,0.15)" : "rgba(239,83,80,0.15)"}`, background: t.status === "pending" ? "rgba(252,214,87,0.04)" : "rgba(255,255,255,0.02)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: "#e8eaf6", marginBottom: 4 }}>{t.item_title}</div>
                    <div style={{ fontFamily: "'Rajdhani'", fontSize: 12, color: "rgba(232,234,246,0.4)", marginBottom: 4 }}>
                      Buyer: <span style={{ color: "#e8eaf6" }}>{t.buyer?.discord_username || "Unknown"}</span>
                      {t.seller?.discord_username && <> · Seller: <span style={{ color: "#e8eaf6" }}>{t.seller.discord_username}</span></>}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "1px 6px", border: `1px solid ${ACCENT}33`, color: ACCENT }}>{t.store_type.toUpperCase()}</span>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: t.payment_type === "merits" ? "#fcd657" : t.payment_type === "auec" ? "#4fc3f7" : "#34d399" }}>
                        {t.payment_type === "merits" && `${t.merit_amount} Merits`}
                        {t.payment_type === "auec" && `${Number(t.auec_amount).toLocaleString()} aUEC`}
                        {t.payment_type === "cash" && `$${Number(t.cash_amount).toFixed(2)}`}
                      </span>
                      {t.quantity > 1 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>×{t.quantity}</span>}
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.25)" }}>{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: t.status === "pending" ? "#fcd657" : t.status === "confirmed" ? "#34d399" : "#ef5350" }}>{t.status}</span>
                    {t.status === "pending" && (
                      <>
                        <button onClick={() => updateTransaction(t.id, "confirmed")} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, padding: "6px 12px", background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.5)", color: "#34d399", cursor: "pointer" }}>Confirm</button>
                        <button onClick={() => updateTransaction(t.id, "cancelled")} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, padding: "6px 12px", background: "none", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer" }}>Cancel</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {filteredTransactions.length === 0 && <div style={{ textAlign: "center", padding: 40, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO TRANSACTIONS</div>}
          </div>
        </div>
      )}

      {/* ─── SECTIONS TAB ─── */}
      {subTab === "sections" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => { setShowSectionForm(true); setEditingSection(null); setSectionForm({ store_type: "merit", name: "", description: "", sort_order: sections.length + 1 }); }} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}15`, border: `1px solid ${ACCENT}55`, color: ACCENT, cursor: "pointer" }}>+ New Section</button>
          </div>
          {showSectionForm && (
            <div style={{ padding: 20, border: `1px solid ${ACCENT}33`, background: `${ACCENT}06`, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <select value={sectionForm.store_type} onChange={e => setSectionForm(f => ({ ...f, store_type: e.target.value }))} style={{ ...selectStyle, flex: "1 1 120px" }}>
                  <option value="merit">Merit Items</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="bounty">Bounty</option>
                  <option value="swag">Swag Store</option>
                </select>
                <input value={sectionForm.name} onChange={e => setSectionForm(f => ({ ...f, name: e.target.value }))} placeholder="Section name" style={{ ...inputStyle, flex: "2 1 180px" }} />
                <input type="number" value={sectionForm.sort_order} onChange={e => setSectionForm(f => ({ ...f, sort_order: e.target.value }))} placeholder="Order" step="0.1" style={{ ...inputStyle, flex: "0 1 80px", textAlign: "center" }} />
              </div>
              <textarea value={sectionForm.description} onChange={e => setSectionForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" rows={2} style={{ ...inputStyle, marginBottom: 12, resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveSection} disabled={!sectionForm.name.trim()} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, padding: "10px 20px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", opacity: !sectionForm.name.trim() ? 0.4 : 1 }}>{editingSection ? "Save" : "Create"}</button>
                <button onClick={() => { setShowSectionForm(false); setEditingSection(null); }} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "10px 16px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {sections.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${ACCENT}33`, fontFamily: "'Space Mono', monospace", fontSize: 11, color: ACCENT, flexShrink: 0 }}>{s.sort_order}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: "#e8eaf6" }}>{s.name}</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "1px 6px", border: `1px solid ${ACCENT}33`, color: ACCENT }}>{s.store_type.toUpperCase()}</span>
                    {s.description && <span style={{ fontFamily: "'Rajdhani'", fontSize: 12, color: "rgba(232,234,246,0.3)" }}>{s.description}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => { setEditingSection(s.id); setSectionForm({ store_type: s.store_type, name: s.name, description: s.description || "", sort_order: s.sort_order }); setShowSectionForm(true); }} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", padding: "6px 12px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer" }}>Edit</button>
                  <button onClick={() => deleteSection(s.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", padding: "6px 12px", background: "none", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer" }}>Delete</button>
                </div>
              </div>
            ))}
            {sections.length === 0 && <div style={{ textAlign: "center", padding: 40, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO SECTIONS CREATED</div>}
          </div>
        </div>
      )}

      {/* ─── STORE ADMINS TAB ─── */}
      {subTab === "admins" && (
        <div>
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.4)", marginBottom: 16 }}>Store admins receive Discord DMs when purchases are made and can confirm/cancel merit and swag transactions.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setShowAdminForm(true)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}15`, border: `1px solid ${ACCENT}55`, color: ACCENT, cursor: "pointer" }}>+ Add Store Admin</button>
          </div>
          {showAdminForm && (
            <div style={{ padding: 20, border: `1px solid ${ACCENT}33`, background: `${ACCENT}06`, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <select value={adminForm.user_id} onChange={e => setAdminForm(f => ({ ...f, user_id: e.target.value }))} style={{ ...selectStyle, flex: "2 1 180px" }}>
                  <option value="">Select member...</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.discord_username}</option>)}
                </select>
                <select value={adminForm.store_type} onChange={e => setAdminForm(f => ({ ...f, store_type: e.target.value }))} style={{ ...selectStyle, flex: "1 1 120px" }}>
                  <option value="merit">Merit Store</option>
                  <option value="swag">Swag Store</option>
                </select>
                <button onClick={addStoreAdmin} disabled={!adminForm.user_id} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, padding: "10px 20px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", opacity: !adminForm.user_id ? 0.4 : 1 }}>Add</button>
                <button onClick={() => setShowAdminForm(false)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "10px 16px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {storeAdmins.map(a => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", border: `2px solid ${ACCENT}44`, flexShrink: 0 }}>
                  {a.profiles?.discord_avatar ? <img src={a.profiles.discord_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 12, color: ACCENT }}>{a.profiles?.discord_username?.charAt(0)}</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{a.profiles?.discord_username}</div>
                </div>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "2px 8px", border: `1px solid ${ACCENT}33`, color: ACCENT }}>{a.store_type.toUpperCase()}</span>
                <button onClick={() => removeStoreAdmin(a.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, padding: "6px 12px", background: "none", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer" }}>Remove</button>
              </div>
            ))}
            {storeAdmins.length === 0 && <div style={{ textAlign: "center", padding: 40, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO STORE ADMINS ASSIGNED</div>}
          </div>
        </div>
      )}

      {/* ─── SETTINGS TAB ─── */}
      {subTab === "settings" && (
        <div>
          <div style={{ marginBottom: 24, padding: 20, border: "1px solid rgba(206,147,216,0.25)", background: "rgba(206,147,216,0.05)" }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#ce93d8", textTransform: "uppercase", marginBottom: 8 }}>Sales Channel</div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", marginBottom: 16, lineHeight: 1.5 }}>
              The Discord channel where the bot posts a new thread every time a member clicks <strong>Take Contract</strong> on a bounty. Both the buyer and the seller are pinged in the new thread so they can coordinate the deal.
            </div>
            <input
              value={marketChannelSearch}
              onChange={e => setMarketChannelSearch(e.target.value)}
              placeholder="Search channels..."
              style={{ width: "100%", maxWidth: 340, padding: "8px 12px", marginBottom: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(206,147,216,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none" }}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 200, overflowY: "auto", marginBottom: 14 }}>
              {marketAllChannels
                .filter(ch => !marketChannelSearch || ch.name.toLowerCase().includes(marketChannelSearch.toLowerCase()) || (ch.category || "").toLowerCase().includes(marketChannelSearch.toLowerCase()))
                .map(ch => {
                  const sel = salesChannelId === ch.id;
                  return (
                    <button key={ch.id} onClick={() => setSalesChannelId(sel ? "" : ch.id)} style={{
                      padding: "5px 12px", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
                      background: sel ? "rgba(206,147,216,0.18)" : "rgba(255,255,255,0.03)",
                      border: sel ? "1px solid rgba(206,147,216,0.6)" : "1px solid rgba(255,255,255,0.1)",
                      color: sel ? "#ce93d8" : "rgba(232,234,246,0.5)", cursor: "pointer", transition: "all 0.2s",
                    }}>
                      {sel ? "✓ " : ""}#{ch.name}{ch.category ? ` (${ch.category})` : ""}
                    </button>
                  );
                })}
              {marketAllChannels.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO CHANNELS FOUND</div>}
            </div>
            {salesChannelId && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(206,147,216,0.65)", marginBottom: 10 }}>
                Selected: #{(marketAllChannels.find(c => c.id === salesChannelId) || {}).name || salesChannelId}
              </div>
            )}
            <button onClick={saveSalesChannel} disabled={salesChannelSaving} style={{
              padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
              background: salesChannelSaved ? "rgba(206,147,216,0.18)" : "rgba(206,147,216,0.1)",
              border: `1px solid ${salesChannelSaved ? "#ce93d8" : "rgba(206,147,216,0.5)"}`,
              color: "#ce93d8", cursor: "pointer", transition: "all 0.2s",
            }}>{salesChannelSaving ? "Saving..." : salesChannelSaved ? "✓ Saved" : "Save Sales Channel"}</button>
          </div>

          {/* Per-type Share Channels */}
          {[
            { key: "marketplace", label: "Marketplace Share Channels",  color: "#4fc3f7", rgb: "79,195,247",  blurb: "Channels where Marketplace listings get posted when a member clicks Share." },
            { key: "bounty",      label: "Bounty Share Channels",       color: "#ce93d8", rgb: "206,147,216", blurb: "Channels where Bounty listings (buy requests) get posted." },
            { key: "orgstore",    label: "Org Store Share Channels",    color: "#fcd657", rgb: "245,166,35",  blurb: "Channels where Org Store items (merit & swag) get posted." },
          ].map(group => {
            const g = shareGroups[group.key];
            return (
              <div key={group.key} style={{ marginBottom: 20, padding: 20, border: `1px solid rgba(${group.rgb},0.25)`, background: `rgba(${group.rgb},0.05)` }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: group.color, textTransform: "uppercase", marginBottom: 8 }}>{group.label}</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", marginBottom: 14, lineHeight: 1.5 }}>
                  {group.blurb} A listing can only be shared to the same channel once.
                </div>
                <input
                  value={g.search}
                  onChange={e => patchShareGroup(group.key, { search: e.target.value })}
                  placeholder="Search channels..."
                  style={{ width: "100%", maxWidth: 340, padding: "8px 12px", marginBottom: 12, background: "rgba(255,255,255,0.04)", border: `1px solid rgba(${group.rgb},0.25)`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none" }}
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto", marginBottom: 12 }}>
                  {marketAllChannels
                    .filter(ch => !g.search || ch.name.toLowerCase().includes(g.search.toLowerCase()) || (ch.category || "").toLowerCase().includes(g.search.toLowerCase()))
                    .map(ch => {
                      const sel = g.ids.includes(ch.id);
                      return (
                        <button key={ch.id} onClick={() => toggleShareChannel(group.key, ch.id)} style={{
                          padding: "5px 12px", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
                          background: sel ? `rgba(${group.rgb},0.18)` : "rgba(255,255,255,0.03)",
                          border: sel ? `1px solid rgba(${group.rgb},0.6)` : "1px solid rgba(255,255,255,0.1)",
                          color: sel ? group.color : "rgba(232,234,246,0.5)", cursor: "pointer", transition: "all 0.2s",
                        }}>
                          {sel ? "✓ " : ""}#{ch.name}{ch.category ? ` (${ch.category})` : ""}
                        </button>
                      );
                    })}
                  {marketAllChannels.length === 0 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>NO CHANNELS FOUND</div>}
                </div>
                {g.ids.length > 0 && (
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: `rgba(${group.rgb},0.65)`, marginBottom: 10 }}>
                    {g.ids.length} channel{g.ids.length !== 1 ? "s" : ""} selected
                  </div>
                )}
                <button onClick={() => saveShareChannels(group.key)} disabled={g.saving} style={{
                  padding: "8px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
                  background: g.saved ? `rgba(${group.rgb},0.18)` : `rgba(${group.rgb},0.1)`,
                  border: `1px solid ${g.saved ? group.color : `rgba(${group.rgb},0.5)`}`,
                  color: group.color, cursor: "pointer", transition: "all 0.2s",
                }}>{g.saving ? "Saving..." : g.saved ? "✓ Saved" : "Save"}</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Resource Category Manager (super admin) ─────────────────────────────────
function ResourceCategoryManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", sort_order: 0 });
  const { profile } = useAuth();

  useEffect(() => { loadCategories(); }, []);

  async function loadCategories() {
    setLoading(true);
    const { data } = await supabase.from("resource_categories").select("*").order("sort_order", { ascending: true });
    setCategories(data || []);
    setLoading(false);
  }

  async function saveCategory() {
    if (!form.name.trim()) return;
    if (editingId) {
      await supabase.from("resource_categories").update({
        name: form.name.trim(), description: form.description.trim(),
        sort_order: parseFloat(form.sort_order) || 0, updated_at: new Date().toISOString(),
      }).eq("id", editingId);
    } else {
      await supabase.from("resource_categories").insert([{
        name: form.name.trim(), description: form.description.trim(),
        sort_order: parseFloat(form.sort_order) || 0, created_by: profile.id,
      }]);
    }
    setForm({ name: "", description: "", sort_order: 0 });
    setAdding(false);
    setEditingId(null);
    loadCategories();
  }

  async function deleteCategory(id) {
    if (!window.confirm("Delete this category and ALL its resources?")) return;
    await supabase.from("resource_categories").delete().eq("id", id);
    loadCategories();
  }

  function startEdit(cat) {
    setEditingId(cat.id);
    setForm({ name: cat.name, description: cat.description || "", sort_order: cat.sort_order });
    setAdding(true);
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 3, color: `${ACCENT}55` }}>LOADING...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 20, letterSpacing: 3, color: "#e8eaf6", marginBottom: 4 }}>Resource Categories</h2>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>
            Manage categories visible on the Resources page
          </p>
        </div>
        {!adding && (
          <button onClick={() => { setAdding(true); setEditingId(null); setForm({ name: "", description: "", sort_order: categories.length + 1 }); }}
            style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}15`, border: `1px solid ${ACCENT}55`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
            + New Category
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {adding && (
        <div style={{ padding: 20, border: `1px solid ${ACCENT}33`, background: `${ACCENT}06`, marginBottom: 24 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: "uppercase", marginBottom: 16 }}>
            {editingId ? "Edit Category" : "New Category"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Category name..."
                style={{ flex: "3 1 200px", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none" }} />
              <input value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                placeholder="Sort order" type="number" step="0.1"
                style={{ flex: "1 1 80px", maxWidth: 100, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Space Mono', monospace", fontSize: 13, borderRadius: 2, outline: "none", textAlign: "center" }} />
            </div>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Description (optional)..." rows={2}
              style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", resize: "vertical" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveCategory} disabled={!form.name.trim()}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", opacity: form.name.trim() ? 1 : 0.4 }}>
                {editingId ? "Save Changes" : "Create Category"}
              </button>
              <button onClick={() => { setAdding(false); setEditingId(null); setForm({ name: "", description: "", sort_order: 0 }); }}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "10px 16px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Category list */}
      {categories.length === 0 && !adding && (
        <div style={{ textAlign: "center", padding: 60, border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, color: "rgba(255,255,255,0.06)", marginBottom: 12 }}>◇</div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.25)" }}>No categories yet. Create one to get started.</div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {categories.map(cat => (
          <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", transition: "all 0.2s" }}>
            {/* Sort order badge */}
            <div style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${ACCENT}33`, fontFamily: "'Space Mono', monospace", fontSize: 11, color: ACCENT, flexShrink: 0 }}>
              {cat.sort_order}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: "#e8eaf6", letterSpacing: 1 }}>{cat.name}</div>
              {cat.description && (
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.35)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cat.description}</div>
              )}
            </div>

            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button onClick={() => startEdit(cat)}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", padding: "6px 12px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>Edit</button>
              <button onClick={() => deleteCategory(cat.id)}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", padding: "6px 12px", background: "none", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer", transition: "all 0.2s" }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Voice Activity Manager ───────────────────────────────────────────────────
// Reads from `voice_activity_events` (populated by /api/discord-vc-* endpoints).
// All aggregations happen client-side over rows in the selected time window so
// time-of-day buckets can be converted to the viewer's local timezone.
// "Nap Time" voice channel — excluded from member/leaderboard hour totals by
// default since it's AFK/idle time, not real activity. Matched loosely so
// casing/spacing variants ("naptime", "Nap  Time") all count.
function isNaptimeChannel(name) {
  return (name || "").replace(/\s+/g, "").toLowerCase() === "naptime";
}

function VoiceActivityManager({ profile }) {
  const ACCENT_VC = "#fcd657";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30); // 7 / 30 / 365 / 0 (all-time)
  const [includeNaptime, setIncludeNaptime] = useState(false); // exclude Nap Time by default
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [view, setView] = useState("members"); // members | channels | times

  async function loadData() {
    setLoading(true);
    let q = supabase
      .from("voice_activity_events")
      .select("discord_user_id, profile_id, username, action, channel_name, session_seconds, event_at")
      .eq("action", "left")
      .not("session_seconds", "is", null)
      .order("event_at", { ascending: false })
      .limit(50000);
    if (windowDays > 0) {
      const since = new Date();
      since.setDate(since.getDate() - windowDays);
      q = q.gte("event_at", since.toISOString());
    }
    const { data, error } = await q;
    if (error) console.warn("[vc] load error", error);
    setEvents(data || []);
    setLoading(false);
  }
  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [windowDays]);

  const [diagSamples, setDiagSamples] = useState(null);

  async function runSync() {
    setSyncing(true); setSyncResult(null); setDiagSamples(null);
    try {
      const r = await fetch("/api/discord-vc-sync", { method: "POST" });
      const j = await r.json();
      setSyncResult(r.ok ? `✓ Synced — ${j.eventsInserted} new event${j.eventsInserted === 1 ? "" : "s"} from ${j.messagesScanned} message${j.messagesScanned === 1 ? "" : "s"}` : `✕ ${j.error || "Sync failed"}`);
      if (r.ok && Array.isArray(j.unmatchedSamples) && j.unmatchedSamples.length) setDiagSamples(j.unmatchedSamples);
      if (r.ok) await loadData();
    } catch (e) { setSyncResult(`✕ ${e.message}`); }
    setSyncing(false);
  }
  async function runBackfill() {
    if (!window.confirm("Run a full backfill of the audit channel? This walks the entire channel history and may take a few minutes on first run. Idempotent — safe to repeat.")) return;
    setBackfilling(true); setSyncResult(null); setDiagSamples(null);
    try {
      const r = await fetch("/api/discord-vc-backfill", { method: "POST" });
      const j = await r.json();
      setSyncResult(r.ok ? `✓ Backfilled — ${j.eventsInserted} event${j.eventsInserted === 1 ? "" : "s"} from ${j.messagesScanned} message${j.messagesScanned === 1 ? "" : "s"} across ${j.batches} batch${j.batches === 1 ? "" : "es"}` : `✕ ${j.error || "Backfill failed"}`);
      if (r.ok && Array.isArray(j.unmatchedSamples) && j.unmatchedSamples.length) setDiagSamples(j.unmatchedSamples);
      if (r.ok) await loadData();
    } catch (e) { setSyncResult(`✕ ${e.message}`); }
    setBackfilling(false);
  }

  // ── Aggregations ──
  // Per member we track work (non-nap) and nap seconds separately. By default
  // only work time counts; the "Include Nap Time" toggle adds nap back and the
  // row shows how much of the total is nap.
  const members = (() => {
    const m = {};
    events.forEach(ev => {
      const k = ev.discord_user_id;
      if (!m[k]) m[k] = { discord_user_id: k, profile_id: ev.profile_id, username: ev.username || k, is_member: !!ev.profile_id, work_seconds: 0, nap_seconds: 0, work_sessions: 0, nap_sessions: 0 };
      const secs = ev.session_seconds || 0;
      if (isNaptimeChannel(ev.channel_name)) { m[k].nap_seconds += secs; m[k].nap_sessions++; }
      else { m[k].work_seconds += secs; m[k].work_sessions++; }
      // Prefer the most recent username we saw
      if (ev.username) m[k].username = ev.username;
      if (ev.profile_id) { m[k].profile_id = ev.profile_id; m[k].is_member = true; }
    });
    return Object.values(m)
      .map(x => ({
        ...x,
        total_seconds: includeNaptime ? x.work_seconds + x.nap_seconds : x.work_seconds,
        sessions: includeNaptime ? x.work_sessions + x.nap_sessions : x.work_sessions,
      }))
      .filter(x => x.sessions > 0) // hide members whose only time was nap (when excluded)
      .sort((a, b) => b.total_seconds - a.total_seconds);
  })();
  const channels = (() => {
    // Group case-insensitively so "Public VC" and "PUBLIC VC" merge. Display label
    // is the casing variant we saw most often (or first if tied).
    const c = {};
    events.forEach(ev => {
      const raw = ev.channel_name || "(unknown)";
      const k = raw.toLowerCase();
      if (!c[k]) c[k] = { channel_name: raw, total_seconds: 0, sessions: 0, users: new Set(), variants: {} };
      c[k].total_seconds += ev.session_seconds || 0;
      c[k].sessions++;
      c[k].users.add(ev.discord_user_id);
      c[k].variants[raw] = (c[k].variants[raw] || 0) + 1;
    });
    return Object.values(c).map(x => {
      const best = Object.entries(x.variants).sort((a, b) => b[1] - a[1])[0]?.[0] || x.channel_name;
      return { channel_name: best, total_seconds: x.total_seconds, sessions: x.sessions, unique_users: x.users.size };
    }).sort((a, b) => b.total_seconds - a.total_seconds);
  })();
  const heatmap = (() => {
    // 7×24 grid in viewer's local timezone. Each cell = sum of session_seconds whose
    // "left" timestamp lands in that hour bucket. Nap Time is always excluded here.
    const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
    events.forEach(ev => {
      if (isNaptimeChannel(ev.channel_name)) return;
      const d = new Date(ev.event_at);
      grid[d.getDay()][d.getHours()] += ev.session_seconds || 0;
    });
    let max = 0;
    grid.forEach(row => row.forEach(v => { if (v > max) max = v; }));
    return { grid, max };
  })();

  function fmtH(s) { return (s / 3600).toFixed(2); }
  function fmtHr(h) {
    if (h === 0) return "12a";
    if (h === 12) return "12p";
    return h < 12 ? `${h}a` : `${h - 12}p`;
  }
  // Header totals respect the Nap Time toggle (sessions + hours).
  const napSessionCount = events.filter(ev => isNaptimeChannel(ev.channel_name)).length;
  const napSeconds = events.reduce((sum, ev) => sum + (isNaptimeChannel(ev.channel_name) ? (ev.session_seconds || 0) : 0), 0);
  const workSeconds = events.reduce((sum, ev) => sum + (isNaptimeChannel(ev.channel_name) ? 0 : (ev.session_seconds || 0)), 0);
  const totalSeconds = includeNaptime ? workSeconds + napSeconds : workSeconds;
  const displaySessions = includeNaptime ? events.length : events.length - napSessionCount;

  return (
    <div>
      {/* Header / controls */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700, color: "#e8eaf6", letterSpacing: 1.5 }}>Voice Activity</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", marginTop: 4, textTransform: "uppercase" }}>
            From #audit-logging · {displaySessions.toLocaleString()} sessions · {fmtH(totalSeconds)} total hours
            {!includeNaptime && napSeconds > 0 && ` · ${fmtH(napSeconds)}h nap excluded`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={runSync} disabled={syncing || backfilling} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: `${ACCENT_VC}18`, border: `1px solid ${ACCENT_VC}`, color: ACCENT_VC, cursor: syncing ? "wait" : "pointer", opacity: (syncing || backfilling) ? 0.6 : 1 }}>
            {syncing ? "Syncing…" : "Sync now"}
          </button>
          <button onClick={runBackfill} disabled={syncing || backfilling} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "rgba(232,234,246,0.7)", cursor: backfilling ? "wait" : "pointer", opacity: (syncing || backfilling) ? 0.6 : 1 }}>
            {backfilling ? "Backfilling…" : "Run backfill"}
          </button>
        </div>
      </div>
      {syncResult && (
        <div style={{ marginBottom: 16, padding: "10px 14px", border: `1px solid ${syncResult.startsWith("✓") ? "rgba(52,211,153,0.4)" : "rgba(239,83,80,0.4)"}`, color: syncResult.startsWith("✓") ? "#34d399" : "#ef5350", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1 }}>
          {syncResult}
        </div>
      )}
      {diagSamples && diagSamples.length > 0 && (
        <div style={{ marginBottom: 16, padding: "12px 14px", border: "1px solid rgba(252,214,87,0.4)", background: "rgba(252,214,87,0.04)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "#fcd657", textTransform: "uppercase", marginBottom: 8 }}>
            Parser found 0 events — here's what the audit channel actually looks like:
          </div>
          {diagSamples.map(s => (
            <div key={s.id} style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#e8eaf6", padding: "6px 0", borderTop: "1px solid rgba(252,214,87,0.18)" }}>
              <div style={{ fontSize: 9, color: "rgba(232,234,246,0.4)", marginBottom: 3 }}>{s.author || "(unknown)"} · msg {s.id}</div>
              {s.content && <pre style={{ whiteSpace: "pre-wrap", margin: "0 0 4px 0", fontFamily: "inherit" }}>{s.content}</pre>}
              {Array.isArray(s.embeds) && s.embeds.map((emb, i) => (
                <pre key={i} style={{ whiteSpace: "pre-wrap", margin: "0 0 4px 0", fontFamily: "inherit", color: "#a78bfa" }}>{"[embed] "}{emb}</pre>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Window + view tabs */}
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 4 }}>
          {[{ id: "members", label: "Members" }, { id: "channels", label: "Channels" }, { id: "times", label: "Times" }].map(v => {
            const active = view === v.id;
            return (
              <button key={v.id} onClick={() => setView(v.id)} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", padding: "8px 16px", background: active ? `${ACCENT_VC}14` : "transparent", border: `1px solid ${active ? ACCENT_VC : "rgba(255,255,255,0.1)"}`, color: active ? ACCENT_VC : "rgba(232,234,246,0.55)", cursor: "pointer" }}>{v.label}</button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => setIncludeNaptime(v => !v)}
            title="Nap Time is idle/AFK voice time, excluded from totals by default"
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "8px 12px", background: includeNaptime ? `${ACCENT_VC}14` : "transparent", border: `1px solid ${includeNaptime ? ACCENT_VC : "rgba(255,255,255,0.08)"}`, color: includeNaptime ? ACCENT_VC : "rgba(232,234,246,0.45)", cursor: "pointer", marginRight: 8 }}>
            {includeNaptime ? "☑" : "☐"} Include Nap Time
          </button>
          {[{ id: 7, label: "7 days" }, { id: 30, label: "30 days" }, { id: 365, label: "1 year" }, { id: 0, label: "All time" }].map(w => {
            const active = windowDays === w.id;
            return (
              <button key={w.id} onClick={() => setWindowDays(w.id)} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", padding: "8px 12px", background: active ? "rgba(255,255,255,0.06)" : "transparent", border: `1px solid ${active ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)"}`, color: active ? "#e8eaf6" : "rgba(232,234,246,0.45)", cursor: "pointer" }}>{w.label}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>Loading…</div>
      ) : events.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>
          No voice activity recorded yet — click "Run backfill" to import the channel history.
        </div>
      ) : view === "members" ? (
        // ── Members leaderboard ──
        <div style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 80px", gap: 0, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
            <div>#</div><div>Member</div><div style={{ textAlign: "right" }}>Hours</div><div style={{ textAlign: "right" }}>Sessions</div><div style={{ textAlign: "right" }}>Status</div>
          </div>
          {members.slice(0, 200).map((m, i) => (
            <div key={m.discord_user_id} style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 80px", gap: 0, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", background: m.is_member ? "transparent" : "rgba(239,83,80,0.04)", alignItems: "center" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.35)" }}>{i + 1}</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {m.profile_id ? <Link to={`/profile/${m.profile_id}`} style={{ color: "#e8eaf6" }}>{m.username}</Link> : m.username}
              </div>
              <div style={{ textAlign: "right", fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: ACCENT_VC }}>
                {fmtH(m.total_seconds)}
                {includeNaptime && m.nap_seconds > 0 && (
                  <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 400, fontSize: 8, letterSpacing: 0.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
                    incl {fmtH(m.nap_seconds)}h nap
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.5)" }}>{m.sessions}</div>
              <div style={{ textAlign: "right", fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, textTransform: "uppercase", color: m.is_member ? "#34d399" : "#ef5350" }}>
                {m.is_member ? "Member" : "Non-member"}
              </div>
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 80px", padding: "12px 14px", background: "rgba(255,255,255,0.04)", fontFamily: "'Orbitron', sans-serif", fontSize: 12, fontWeight: 700 }}>
            <div></div><div style={{ color: "#e8eaf6", letterSpacing: 1 }}>TOTAL</div>
            <div style={{ textAlign: "right", color: ACCENT_VC }}>{fmtH(totalSeconds)}</div>
            <div style={{ textAlign: "right", color: "rgba(232,234,246,0.7)" }}>{displaySessions}</div>
            <div></div>
          </div>
        </div>
      ) : view === "channels" ? (
        // ── Channels ──
        <div style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 100px", padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
            <div>#</div><div>Channel</div><div style={{ textAlign: "right" }}>Hours</div><div style={{ textAlign: "right" }}>Sessions</div><div style={{ textAlign: "right" }}>Users</div>
          </div>
          {channels.map((c, i) => (
            <div key={c.channel_name} style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 100px 100px", padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", alignItems: "center" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.35)" }}>{i + 1}</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "rgba(232,234,246,0.4)", fontSize: 11 }}>🎙</span>
                <span>{c.channel_name}</span>
                {/* Inline bar chart — bar length proportional to top channel */}
                <div style={{ flex: 1, height: 4, background: "rgba(255,255,255,0.04)", marginLeft: 12, maxWidth: 200 }}>
                  <div style={{ height: "100%", width: `${(c.total_seconds / channels[0].total_seconds) * 100}%`, background: ACCENT_VC, opacity: 0.8 }} />
                </div>
              </div>
              <div style={{ textAlign: "right", fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, color: ACCENT_VC }}>{fmtH(c.total_seconds)}</div>
              <div style={{ textAlign: "right", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.5)" }}>{c.sessions}</div>
              <div style={{ textAlign: "right", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.5)" }}>{c.unique_users}</div>
            </div>
          ))}
        </div>
      ) : (
        // ── Times-of-day heatmap ──
        <div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 12 }}>
            Hour of day × day of week — your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "40px repeat(24, minmax(22px, 1fr))", gap: 2, minWidth: 700 }}>
              <div></div>
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", textAlign: "center" }}>{h % 3 === 0 ? fmtHr(h) : ""}</div>
              ))}
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((label, di) => (
                <React.Fragment key={label}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.5)", display: "flex", alignItems: "center", paddingRight: 8, textTransform: "uppercase" }}>{label}</div>
                  {Array.from({ length: 24 }, (_, h) => {
                    const v = heatmap.grid[di][h];
                    const intensity = heatmap.max > 0 ? v / heatmap.max : 0;
                    return (
                      <div key={h} title={`${label} ${fmtHr(h)} — ${fmtH(v)}h`} style={{ aspectRatio: "1", background: intensity > 0 ? `rgba(252,214,87,${0.08 + intensity * 0.85})` : "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }} />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
            <span>Less</span>
            {[0, 0.2, 0.4, 0.6, 0.8, 1].map(i => (
              <div key={i} style={{ width: 18, height: 12, background: i === 0 ? "rgba(255,255,255,0.04)" : `rgba(252,214,87,${0.08 + i * 0.85})`, border: "1px solid rgba(255,255,255,0.04)" }} />
            ))}
            <span>More</span>
            <span style={{ marginLeft: 14, color: "rgba(232,234,246,0.55)" }}>Peak: {fmtH(heatmap.max)}h in a single hour</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Voice Activity Snapshot (dashboard widget) ───────────────────────────────
// Compact 30-day at-a-glance view: top members, top channels, mini heatmap.
// Shares aggregation logic with VoiceActivityManager but does its own narrow
// query so the dashboard load stays fast.
function VoiceActivitySnapshot() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(true);
  const [windowDays, setWindowDays] = useState(30); // 7 / 30 / 365 / 0 (all time)
  const VC_GOLD = "#fcd657";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      let q = supabase
        .from("voice_activity_events")
        .select("discord_user_id, profile_id, username, channel_name, session_seconds, event_at")
        .eq("action", "left")
        .not("session_seconds", "is", null)
        .order("event_at", { ascending: false })
        .limit(20000);
      if (windowDays > 0) {
        const since = new Date();
        since.setDate(since.getDate() - windowDays);
        q = q.gte("event_at", since.toISOString());
      }
      const { data, error } = await q;
      if (cancelled) return;
      if (error) { setHasData(false); setLoading(false); return; }
      setEvents(data || []);
      setHasData(Array.isArray(data));
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [windowDays]);

  const fmtH = s => (s / 3600).toFixed(1);
  const fmtHr = h => h === 0 ? "12a" : h === 12 ? "12p" : h < 12 ? `${h}a` : `${h - 12}p`;

  // Aggregations — Nap Time (idle/AFK) is excluded from member totals.
  const topMembers = (() => {
    const m = {};
    events.forEach(ev => {
      if (isNaptimeChannel(ev.channel_name)) return;
      const k = ev.discord_user_id || (ev.username || "").toLowerCase();
      if (!m[k]) m[k] = { profile_id: ev.profile_id, username: ev.username || k, total_seconds: 0, is_member: !!ev.profile_id };
      m[k].total_seconds += ev.session_seconds || 0;
      if (ev.username) m[k].username = ev.username;
      if (ev.profile_id) { m[k].profile_id = ev.profile_id; m[k].is_member = true; }
    });
    return Object.values(m).filter(x => x.total_seconds > 0).sort((a, b) => b.total_seconds - a.total_seconds).slice(0, 5);
  })();
  const topChannels = (() => {
    // Case-insensitive grouping; display label is the most-common variant.
    const c = {};
    events.forEach(ev => {
      const raw = ev.channel_name || "(unknown)";
      const k = raw.toLowerCase();
      if (!c[k]) c[k] = { channel_name: raw, total_seconds: 0, variants: {} };
      c[k].total_seconds += ev.session_seconds || 0;
      c[k].variants[raw] = (c[k].variants[raw] || 0) + 1;
    });
    return Object.values(c).map(x => {
      const best = Object.entries(x.variants).sort((a, b) => b[1] - a[1])[0]?.[0] || x.channel_name;
      return { channel_name: best, total_seconds: x.total_seconds };
    }).sort((a, b) => b.total_seconds - a.total_seconds).slice(0, 5);
  })();
  const heatmap = (() => {
    const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
    events.forEach(ev => {
      if (isNaptimeChannel(ev.channel_name)) return;
      const d = new Date(ev.event_at);
      grid[d.getDay()][d.getHours()] += ev.session_seconds || 0;
    });
    let max = 0;
    grid.forEach(row => row.forEach(v => { if (v > max) max = v; }));
    return { grid, max };
  })();

  if (loading) {
    return (
      <div style={{ padding: 36, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", border: "1px solid rgba(255,255,255,0.06)" }}>
        Loading…
      </div>
    );
  }
  if (!hasData || events.length === 0) {
    return (
      <div style={{ padding: 36, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", border: "1px dashed rgba(255,255,255,0.08)" }}>
        No voice activity in the last 30 days. Run a backfill from the Voice Activity tab.
      </div>
    );
  }

  // Headline totals exclude Nap Time (idle/AFK), matching member totals.
  const nonNapEvents = events.filter(e => !isNaptimeChannel(e.channel_name));
  const totalSeconds = nonNapEvents.reduce((s, e) => s + (e.session_seconds || 0), 0);
  const totalSessions = nonNapEvents.length;
  const topMemberMax = topMembers[0]?.total_seconds || 1;
  const topChannelMax = topChannels[0]?.total_seconds || 1;

  return (
    <div>
      {/* Time window filter — same options as the full Voice Activity tab */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
        {[{ id: 7, label: "7 days" }, { id: 30, label: "30 days" }, { id: 365, label: "1 year" }, { id: 0, label: "All time" }].map(w => {
          const active = windowDays === w.id;
          return (
            <button key={w.id} onClick={() => setWindowDays(w.id)} style={{
              fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase",
              padding: "8px 12px",
              background: active ? "rgba(255,255,255,0.06)" : "transparent",
              border: `1px solid ${active ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)"}`,
              color: active ? "#e8eaf6" : "rgba(232,234,246,0.45)",
              cursor: "pointer",
            }}>{w.label}</button>
          );
        })}
      </div>

      {/* Headline stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={{ padding: "12px 16px", border: `1px solid ${VC_GOLD}33`, background: `${VC_GOLD}06` }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: `${VC_GOLD}aa`, textTransform: "uppercase", marginBottom: 4 }}>Total VC Hours</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, fontWeight: 800, color: VC_GOLD }}>{fmtH(totalSeconds)}</div>
        </div>
        <div style={{ padding: "12px 16px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.45)", textTransform: "uppercase", marginBottom: 4 }}>Sessions</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, fontWeight: 800, color: "#e8eaf6" }}>{totalSessions.toLocaleString()}</div>
        </div>
        <div style={{ padding: "12px 16px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.45)", textTransform: "uppercase", marginBottom: 4 }}>Active Members</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, fontWeight: 800, color: "#e8eaf6" }}>{new Set(events.map(e => e.discord_user_id || e.username)).size}</div>
        </div>
        <div style={{ padding: "12px 16px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.45)", textTransform: "uppercase", marginBottom: 4 }}>Active Channels</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, fontWeight: 800, color: "#e8eaf6" }}>{new Set(events.map(e => e.channel_name)).size}</div>
        </div>
      </div>

      {/* Three-panel snapshot */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {/* Top members */}
        <div style={{ border: "1px solid rgba(255,255,255,0.06)", padding: 14 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", marginBottom: 12 }}>Top Members</div>
          {topMembers.map((m, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600, color: m.is_member ? "#e8eaf6" : "rgba(239,83,80,0.8)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                  {m.profile_id ? <Link to={`/profile/${m.profile_id}`} style={{ color: "inherit" }}>{m.username}</Link> : m.username}
                </span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: VC_GOLD }}>{fmtH(m.total_seconds)}h</span>
              </div>
              <div style={{ height: 4, background: "rgba(255,255,255,0.04)" }}>
                <div style={{ height: "100%", width: `${(m.total_seconds / topMemberMax) * 100}%`, background: VC_GOLD, opacity: 0.85 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Top channels */}
        <div style={{ border: "1px solid rgba(255,255,255,0.06)", padding: 14 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", marginBottom: 12 }}>Top Channels</div>
          {topChannels.map((c, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                  <span style={{ color: "rgba(232,234,246,0.4)", fontSize: 11, marginRight: 4 }}>🎙</span>{c.channel_name}
                </span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: VC_GOLD }}>{fmtH(c.total_seconds)}h</span>
              </div>
              <div style={{ height: 4, background: "rgba(255,255,255,0.04)" }}>
                <div style={{ height: "100%", width: `${(c.total_seconds / topChannelMax) * 100}%`, background: VC_GOLD, opacity: 0.85 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Heatmap — spans both other panel columns so cells get usable width
            instead of wrapping to a narrow 1/3-column. Inner grid is capped so
            cells don't blow up huge on ultrawide viewports. */}
        <div style={{ border: "1px solid rgba(255,255,255,0.06)", padding: 14, gridColumn: "1 / -1" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.55)", textTransform: "uppercase", marginBottom: 4 }}>Activity by Hour & Day</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", marginBottom: 10 }}>
            Your timezone · {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "32px repeat(24, minmax(0, 1fr))", gap: 2, maxWidth: 820 }}>
            <div></div>
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", textAlign: "center" }}>{h % 3 === 0 ? fmtHr(h) : ""}</div>
            ))}
            {["S","M","T","W","T","F","S"].map((label, di) => (
              <React.Fragment key={di}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.5)", display: "flex", alignItems: "center", paddingRight: 6 }}>{label}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = heatmap.grid[di][h];
                  const intensity = heatmap.max > 0 ? v / heatmap.max : 0;
                  return (
                    <div key={h} title={`${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][di]} ${fmtHr(h)} — ${fmtH(v)}h`}
                      style={{ aspectRatio: "1", background: intensity > 0 ? `rgba(252,214,87,${0.08 + intensity * 0.85})` : "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }} />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
